import { Children, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { enumLabel } from '../lib/enumLabel';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  SearchX,
  Loader2,
  PackageX,
  Copy,
  ShoppingCart,
  Plus,
  Minus,
  MapPin,
  Car,
  X,
  ChevronDown,
  Briefcase,
  User,
  Check,
  AlertTriangle,
  Coins,
  CreditCard,
  FileText,
  ArrowLeftRight,
  Ticket,
  Keyboard,
  ZoomIn,
  Truck,
  Printer,
  ChevronRight,
  ArrowDownAZ,
  ArrowDown01,
  ArrowRight,
  RotateCcw,
  History,
  RefreshCw,
  ScanLine,
} from 'lucide-react';
import apiClient from '../api/client';
import { CounterBrowse } from '../components/order/CounterBrowse';
import { useAuthStore } from '../stores/authStore';
import { Role, B2B_ROLES } from '../auth/roles';
import { useToast } from '../components/ui/Toast';
import { usePrompt } from '../components/ui/PromptDialog';
import { useScanInput } from '../hooks/useScanInput';
import { useAudioFeedback } from '../hooks/useAudioFeedback';
import { usePermission, useAnyPermission, useAction } from '../auth/usePermission';
import { Permission } from '../auth/permissions';
import { brandStyle, PartThumb } from '../customer/shared';
import { ArticleImage } from '../components/ui/ArticleImage';
import { ArticleInfoDialog, type ArticleInfoTab } from '../components/ArticleInfoDialog';
import { cn } from '../utils/cn';
import {
  deliveryEta,
  supplierEta,
  promiseEta,
  cutoffCountdown,
  todayCutoff,
  useMinuteTick,
  type DeliveryCalendar,
  type DeliverySlot,
  type PromiseRule,
  type Eta,
} from '../utils/deliveryPromise';
import CustomerCreate from './CustomerCreate';
import { PrintDocumentDialog } from '../components/PrintDocumentDialog';
import { formatEUR, specLabel, specValue } from '../lib/format';

// ---------- types ----------
/**
 * Selected spec facets live in ONE flat Set across every criterion group, so each entry has to
 * carry its label and value together. They are joined on NUL because both halves contain spaces
 * ("Filter type", "272.0 mm") and any printable separator would split one of them down the middle.
 */
const SPEC_SEP = '\u0000';
const specKey = (label: string, value: string): string => `${label}${SPEC_SEP}${value}`;

/**
 * Bucket id for rows the category walk could not file — an unfiled product, or a SUPPLIER row with
 * no product record behind it. A sentinel rather than `null` so it can be a selectable facet like
 * any other; the leading NUL keeps it from ever colliding with a real UUID.
 */
const UNFILED_CATEGORY = `${SPEC_SEP}unfiled`;

type SortKey = 'price' | 'lead' | 'margin';
const SORT_OPTIONS: { key: SortKey; marginOnly?: boolean }[] = [
  { key: 'price' },
  { key: 'lead' },
  // Ordering by margin exposes the SHAPE of our cost data, so it is offered only to the roles
  // already cleared to read margin outright — otherwise the list itself becomes the leak.
  { key: 'margin', marginOnly: true },
];

/** One display-ready technical criterion, as TecDoc resolved it: "Length" / "272.0" / "mm". */
interface Spec {
  label: string;
  value: string;
  unit?: string | null;
}
/** One step of a category chain, as the server sends it. */
interface CategoryCrumb {
  id: string;
  name: string;
}
interface CatalogHit {
  id: string;
  sku: string;
  name: string;
  brand: string | null;
  ean: string | null;
  manufacturerRef?: string | null;
  stock: number;
  /** The part's own photo (when TecDoc has one) and the brand logo, tried in that order. */
  imageUrl?: string | null;
  brandImageUrl?: string | null;
  /**
   * Technical criteria snapshotted onto the product — the dimensions strip under the row. Empty on
   * SUPPLIER rows (nothing was ever snapshotted for a part we don't carry), so the strip is simply
   * absent there rather than showing a half-built line.
   */
  specifications?: Spec[];
  /**
   * Root-first category chain, deepest last — `[Braking system, Brake disc / pad, Brake disc]`.
   * The filter rail's first two levels. Absent on SUPPLIER rows and empty on an unfiled product,
   * which the rail treats identically: both land in "Uncategorised".
   */
  categoryPath?: CategoryCrumb[];
  /** SUPPLIER = we don't carry it; a supplier quoted it live for this search. */
  source?: 'CATALOG' | 'SUPPLIER';
  /** Which rung of the relevance ladder the server put this row on. See RelevanceTier below. */
  relevanceTier?: RelevanceTier;
  supplier?: {
    supplierCode?: string;
    supplierName?: string;
    supplierArticleNo: string;
    availableQty: number | null;
    leadTimeDays: number | null;
    /** The supplier's own dates, per warehouse — shown as information in the tooltip. */
    deliverySlots?: DeliverySlot[];
    /** OUR promise rule for this supplier — what the delivery column is dated with (2.1). */
    promiseRule?: PromiseRule;
    unitPriceHT?: number;
    indicativeSellPriceHT: number | null;
    /** Margin on the indicative price — margin roles only, like the cost it derives from. */
    marginPct?: number | null;
    /** The supplier lists the article but gave no price/stock — not a zero, "no offer found". */
    noOffer?: true;
  };
}
interface Availability {
  productId: string;
  internalStock: number;
  price: {
    unitPriceHT: number;
    finalUnitPriceHT: number;
    unitPriceTTC: number;
    discountPct: number;
    depositHT: number;
    /** The rate the server priced with (per cent). Older rows may not carry it. */
    vatRate?: number;
  } | null;
  /**
   * What a unit SOURCED FROM THE SUPPLIER sells for — the same cascade run against the supplier's
   * live cost rather than ours. A different acquisition, so a different price; the two must never
   * be averaged into one line. Null when nobody can supply or no cost was quoted.
   */
  externalPrice: Availability['price'];
  // Soonest lead across suppliers that can actually deliver (0 = on our shelf, null = none can).
  // Safe for every role; the detailed per-supplier rows are staff-only and unused here.
  supplierAvailable: boolean;
  supplierLeadDays: number | null;
  /** The dates the supplier gave for the offer we would buy (empty = date to be confirmed). */
  supplierSlots?: DeliverySlot[];
  /** OUR promise rule for the supplier this line would be bought from (2.1). */
  supplierPromise?: PromiseRule;
  /** What the SUPPLIER holds for this product — shown next to our own stock on the same line. */
  supplierQty: number | null;
  supplierName?: string;
  /** The holder's code (INTERCARS, AUTOPARTNER…) — what the warehouse line is named after. */
  supplierCode?: string;
  // Only present for margin-authority callers (DISCOUNT_APPLY_DIRECT); never sent to B2B/counter.
  cost?: number;
  marginPct?: number | null;
}
type Price = NonNullable<Availability['price']>;
type AvailState = 'ok' | 'warn' | 'out';
interface Row extends CatalogHit {
  /** Our own shelf price. */
  price?: Price;
  /** The supplier-sourced price for the same reference — see Availability.externalPrice. */
  externalPrice?: Price | null;
  supplierQty?: number | null;
  supplierName?: string;
  supplierCode?: string;
  internalStock?: number;
  /** Ranking only — the date shown comes from promiseRule (our rule), slots are the tooltip. */
  leadDays?: number | null;
  deliverySlots?: DeliverySlot[];
  promiseRule?: PromiseRule;
  avail: AvailState;
  cost?: number;
  marginPct?: number | null;
}
interface CustomerHit {
  id: string;
  name: string;
  siret: string | null;
  customer_type: 'B2B' | 'B2C' | 'WALK_IN';
  /** COMPTANT for a pro paying at each pickup; NET_x for an account (2026-10-08). */
  payment_terms?: string | null;
  /**
   * Migration 079 — null means EITHER "explicitly unlimited" (from the search endpoint) or
   * "not loaded" (the locally-built stubs below). Both render as "no ceiling to show"; 0 is
   * the distinct, meaningful state "no credit line", which the gauge now says out loud.
   */
  credit_limit: string | null;
  current_balance: string;
  reserved_credit: string;
}
interface CartLine {
  /**
   * Our product, when the part is one we hold. For a supplier-sourced part that has no record
   * yet this is the SEARCH ROW's id (`sup:<code>:<number>`) and serves only as the basket key —
   * `offer` below is what actually travels to the server. Nothing in `products` is written while
   * a basket is being composed: see services/catalog/offerLines.ts.
   */
  productId: string;
  /** Set when this line is a supplier's offer we have not (yet) minted a record for. */
  offer?: {
    supplierCode: string;
    matchedNumber: string;
    supplierArticleNo: string | null;
    name: string;
    brand: string | null;
  };
  sku: string;
  name: string;
  brand: string | null;
  qty: number;
  price: Price;
  source: 'INTERNAL' | 'EXTERNAL';
  /**
   * 2026-10-30 — what was on OUR shelf when this INTERNAL line was composed. A shelf line never
   * grows past it (owner: "an internal stock line never gets a supplier; above the stock, the
   * order is blocked with 'only 8 in stock'"). The server still recounts under lock at
   * validation (409 STOCK_CHANGED); this only keeps the screen from asking for what it can see
   * is not there. Undefined on a supplier line, and on lines reopened from a quote.
   */
  stockQty?: number;
  /**
   * WHOSE shelf an EXTERNAL line is served from (the supplier's code) and the dates that supplier
   * gave. Carried so the basket names the warehouse the way the result row did — a bare
   * "Supplier · tomorrow" line cannot be told from any other — and so the promise is re-checked
   * at confirmation against the SAME dates it was quoted with.
   */
  supplierCode?: string;
  leadDays?: number;
  deliverySlots?: DeliverySlot[];
  promiseRule?: PromiseRule;
  /** Carried from the search row so the basket shows the same picture as the result list. */
  imageUrl?: string | null;
  brandImageUrl?: string | null;
  cost?: number; // purchase cost (margin-authority only) — drives the margin editor
  /**
   * The delivery day quoted when this line was added, as an instant.
   *
   * Kept so confirmation can ask "is that still true?". A seller who reads Tomorrow 13:00 at 14:18
   * and presses Confirm at 14:22 has crossed the cut-off and promised a date that no longer
   * exists — and nothing on screen would have changed to tell them.
   */
  promisedAt?: number;
  /** The words the seller read at that moment — what the drift dialog says was promised. */
  promisedLabel?: string;
  // Seller's overridden final selling price HT (margin adjustment). Undefined = use grid price.
  customPriceHT?: number;
  /** INV-47 — this supplier line replaces stock that had gone; the customer agreed on screen. */
  switchAgreed?: boolean;
}

/** Effective per-unit selling price (HT) for a cart line — override if set, else grid price. */
const lineUnit = (l: CartLine): number => l.customPriceHT ?? l.price.finalUnitPriceHT;
/**
 * A line's HT and VAT exactly as the server will write them (OrderService.createOrder →
 * PricingService.lineTotal): the line HT is rounded ONCE on unit × (1 − discount) × qty, the VAT
 * is rounded per line on that figure, and the rate is the one the server priced with. The till
 * used to sum unrounded unit × qty and back-derive the rate from a rounded TTC, which drifted a
 * cent from the order's total often enough for `/pay` to refuse the figure it collected. A
 * seller's override replaces the grid price outright (discount folded in → 0), as on the server.
 */
const lineFigures = (l: CartLine): { ht: number; vat: number; vatRate: number } => {
  // Every price the basket takes comes from the server whole (availability, offers/quote), so
  // the rate is there; the fallback snaps a rate back-derived from a rounded TTC to the whole per
  // cent (20 % / 0 % are the rates in use) rather than carrying 20.02 % into the total.
  const vatRate =
    l.price.vatRate ??
    (l.price.finalUnitPriceHT > 0
      ? Math.round((l.price.unitPriceTTC / l.price.finalUnitPriceHT - 1) * 100)
      : 0);
  const ht =
    l.customPriceHT != null
      ? Math.round(l.customPriceHT * l.qty * 100) / 100
      : Math.round(l.price.unitPriceHT * (1 - l.price.discountPct / 100) * l.qty * 100) / 100;
  const vat = Math.round(ht * (vatRate / 100) * 100) / 100;
  return { ht, vat, vatRate };
};
/** A parked quote: its own customer + basket. Counter staff hold several at once. */
interface Quote {
  id: string;
  customer: CustomerHit | null;
  cart: CartLine[];
}

const eur = (n: number): string => formatEUR(n);
const round2 = (n: number) => Math.round(n * 100) / 100;
let quoteSeq = 1;
const newQuote = (customer: CustomerHit | null = null): Quote => ({
  id: `q${quoteSeq++}`,
  customer,
  cart: [],
});

/**
 * Counter baskets survive leaving the screen. The parked quotes used to live only in component
 * state, so anything that unmounted this page — most painfully the "no open cash register"
 * detour to /pos/close — threw away every scanned line and the cashier had to start over.
 *
 * Scoped per user id so a second cashier on the same terminal never inherits someone else's
 * basket. Prices/costs are persisted as a convenience for redisplay only; the server re-prices
 * authoritatively at checkout (see the note on this component), so a stale snapshot cannot
 * change what is actually charged.
 */
const BASKET_KEY = 'mecadepot.counterBaskets';
/** Whether the keyboard-shortcut strip is shown — a per-terminal preference, not per user. */
const HOTKEYBAR_KEY = 'mecadepot.hotkeyBar';
const basketKeyFor = (userId?: string): string => `${BASKET_KEY}.${userId ?? 'anon'}`;

interface PersistedBaskets {
  quotes: Quote[];
  activeIdx: number;
  seq: number;
}

const loadBaskets = (key: string): PersistedBaskets | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedBaskets>;
    if (!Array.isArray(parsed.quotes) || parsed.quotes.length === 0) return null;
    return {
      quotes: parsed.quotes,
      activeIdx: typeof parsed.activeIdx === 'number' ? parsed.activeIdx : 0,
      seq: typeof parsed.seq === 'number' ? parsed.seq : parsed.quotes.length + 1,
    };
  } catch {
    return null; // corrupt/unparseable → start clean rather than break the till
  }
};

/**
 * The references this seller has actually looked up, newest first.
 *
 * A counter re-searches the same handful of references all day — the filter that four cars need
 * this morning, the reference a customer is deciding on and comes back to twice. Retyping a
 * fifteen-character OE number from memory is where the mistakes are, so the box remembers.
 *
 * Per user id, like the parked baskets: two sellers share a terminal and neither should be reading
 * the other's afternoon. Local to the browser on purpose — this is a typing aid, not a business
 * record, and a server round-trip to open a dropdown is a cost with nothing behind it.
 */
const HISTORY_KEY = 'mecadepot.counterSearchHistory';
/** Follow-up searches while a supplier is still looking up equivalents, and their spacing. */
const MAX_OFFER_REFRESH = 6;
const OFFER_REFRESH_MS = 4_000;
const historyKeyFor = (userId?: string): string => `${HISTORY_KEY}.${userId ?? 'anon'}`;
const HISTORY_MAX = 12;

const loadHistory = (key: string): string[] => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string').slice(0, HISTORY_MAX);
  } catch {
    return []; // corrupt/unparseable → start clean rather than break the till
  }
};

/**
 * Add a term, newest first — DROPPING any entry the new one merely extends.
 *
 * That collapse is what makes this list readable at all. The search runs on a 300 ms debounce, so
 * typing one reference fires several real searches; recording each would fill the dropdown with
 * "T", "TR", "TR-P" and bury the thing the seller was actually looking for. Each successful search
 * therefore supersedes the prefix it grew out of, and a paused half-word survives only when the
 * seller really did stop and look at those results.
 */
const pushHistory = (list: string[], term: string): string[] => {
  const next = term.trim();
  if (!next) return list;
  const lower = next.toLowerCase();
  const kept = list.filter((h) => !lower.startsWith(h.toLowerCase()));
  return [next, ...kept].slice(0, HISTORY_MAX);
};

const availOf = (internalStock?: number, leadDays?: number | null): AvailState =>
  internalStock && internalStock > 0 ? 'ok' : leadDays != null ? 'warn' : 'out';

/**
 * Which rung of the relevance ladder the SERVER put a row on — see `rankHits` in CatalogService,
 * where the ladder is defined. The screen pins `TYPED_REF` to line 1 and otherwise uses the ladder
 * only to break exact ties of the chosen sort (see `sortedRows`). The label is carried rather than
 * re-derived because this file cannot see the inputs (live supplier quantities) it was computed
 * from. A row with no label ranks second-from-bottom.
 */
type RelevanceTier = 'TYPED_REF' | 'IN_STOCK' | 'CATALOG' | 'SUPPLIER_AVAILABLE' | 'SUPPLIER_OUT';

/**
 * The server's ladder, mirrored — the tie-break of the chosen sort. Keep in step with
 * RELEVANCE_TIERS in backend CatalogService.
 */
const TIER_ORDER: RelevanceTier[] = [
  'TYPED_REF',
  'IN_STOCK',
  'SUPPLIER_AVAILABLE',
  'CATALOG',
  'SUPPLIER_OUT',
];
const tierRank = (t: RelevanceTier | undefined): number => {
  const i = t === undefined ? -1 : TIER_ORDER.indexOf(t);
  return i === -1 ? TIER_ORDER.length - 1 : i;
};

/**
 * When a supplier-sourced quantity arrives: THE DATE WE PROMISE (feedback 2.1) — the supplier's
 * cut-off and lead as the owner set them, "before HH:MM" when the supplier has that hour. The
 * supplier's own slot date is kept in the tooltip. See promiseEta.
 */
const externalEta = (
  r: { deliverySlots?: DeliverySlot[] | null; promiseRule?: PromiseRule; leadDays?: number | null },
  qty: number,
  nowMs: number,
  cal: DeliveryCalendar
): Eta =>
  promiseEta(r.promiseRule, cal, nowMs, { slots: r.deliverySlots, qty, leadDays: r.leadDays });

/**
 * A part we don't carry, quoted live by a supplier for this search. Enriched from the quote that
 * came back with the search — there is no product record to look availability up against.
 */
const supplierRow = (h: CatalogHit): Row => ({
  ...h,
  internalStock: 0,
  supplierQty: h.supplier?.availableQty ?? null,
  supplierName: h.supplier?.supplierName,
  supplierCode: h.supplier?.supplierCode,
  leadDays: h.supplier?.leadTimeDays ?? null,
  deliverySlots: h.supplier?.deliverySlots ?? [],
  promiseRule: h.supplier?.promiseRule,
  avail: (h.supplier?.availableQty ?? 0) > 0 ? 'warn' : 'out',
  cost: h.supplier?.unitPriceHT,
  // The supplier quote carries its own margin (indicative, like the price it is taken on). This
  // used to be a flat null, which left every supplier row tied at "unknown margin" and made the
  // margin sort a no-op on any search that is mostly supplier rows.
  marginPct: h.supplier?.marginPct ?? null,
});

/**
 * Screen 20 — Counter order creation, MDS "Counter" composition.
 * Multi-window: several parked quotes, each bound to its own customer, switchable
 * via the top tabs. The active quote drives the catalogue, cart, credit and checkout.
 * B2B portal users get a single quote bound to their JWT customer.
 * All money is authoritative server-side; React renders a preview.
 */
export default function OrderCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showApiError, push } = useToast();
  // The till's "type a code" lane, for a barcode that will not read.
  const [promptForCode, codePromptDialog, codePromptOpen] = usePrompt();
  const beep = useAudioFeedback();
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? Role.COUNTER_AGENT;
  const isB2b = B2B_ROLES.includes(role);
  // 'sale' (/pos/sale) = pay-now ticket · 'order' (/orders/new) = deferred order.
  const routeSale = !isB2b && location.pathname.startsWith('/pos/sale');
  // The route IS the mode: there is no in-screen "pay later / pay now" switch any more (the
  // counter only rings up; a deferred order is its own entry point). Declared up here, next to
  // the route flag it derives from, because two render-time memos read it: `totals` (does
  // delivery apply) and `sellableRows` (may this screen offer a part nobody can deliver).
  const mode: 'devis' | 'caisse' = routeSale ? 'caisse' : 'devis';
  const canQuote = usePermission(Permission.QUOTE_CREATE);
  // Seeing cost/margin is decoupled from the authority to change a price, so this column falls
  // back to "Disc." for a role without MARGIN_VIEW — the server strips the figures from the
  // payload anyway, and what an operator may DO is still enforced server-side by MarginPolicy.
  // The counter holds MARGIN_VIEW again since 2026-09-09 (WP-9 had removed it), so the margin
  // desk renders at the till; the B2B roles and the warehouse still get the fallback.
  const canSeeMargin = usePermission(Permission.MARGIN_VIEW);
  // May this operator sell a part straight off a supplier's shelf? Counter holds it; the B2B
  // roles do not — for them a supplier-only row says "on supplier order, ask the counter".
  const canSourceFromSupplier = usePermission(Permission.PRODUCT_CREATE_FROM_OFFER);
  /* Which supplier row is mid-resolution. Keyed by row id rather than a boolean, because a search
     shows many supplier rows and a shared flag would spin every one of them — and the round trip
     is a live supplier call, so it is slow enough to be visible (0,4–4 s). */
  const [creatingKey, setCreatingKey] = useState<string | null>(null);
  // May this operator ring a sale under purchase cost? Counter/polyvalent cannot, at any
  // discount, and no manager override lifts that — so the editor blocks it locally too.
  const canSellBelowCost = usePermission(Permission.MARGIN_SELL_BELOW_COST);
  /* MAY THIS OPERATOR MOVE A PRICE AT ALL? (Invariant #14.)
     A final price typed on a line IS a discount, so it answers to the same authority as the
     apply-discount endpoint. The counter no longer holds it: the server resolves such a role to
     MarginPolicy authority NONE and answers 403 MARGIN_AUTHORITY_REQUIRED. The margin desk is
     therefore read-only for them rather than hidden — the cost and the margin are still theirs
     to see (MARGIN_VIEW), and knowing the margin is exactly how they decide what discount to
     ask for. Hiding the figures would take away the reason to ask. */
  const canMovePrice = useAnyPermission(
    Permission.DISCOUNT_APPLY_DIRECT,
    Permission.DISCOUNT_APPLY_CAPPED
  );
  // …and if they cannot set it themselves, can they ask? That is what the read-only desk offers
  // instead of a dead end: raise a request on the order, an approver grants it (#14/#15).
  const canRequestDiscount = usePermission(Permission.DISCOUNT_REQUEST);

  // -------- multi-quote --------
  // Restored from localStorage for counter staff (see loadBaskets) so an interrupted sale —
  // e.g. a trip to /pos/close to open the register — comes back intact. The B2B portal keeps a
  // single JWT-bound quote and is not persisted here.
  const persistKey = basketKeyFor(user?.sub);
  // Read storage exactly once per mount (not on every render).
  const restoredRef = useRef<PersistedBaskets | null | undefined>(undefined);
  if (restoredRef.current === undefined) {
    restoredRef.current = isB2b ? null : loadBaskets(persistKey);
    if (restoredRef.current) quoteSeq = Math.max(quoteSeq, restoredRef.current.seq);
  }
  const restored = restoredRef.current;
  const [quotes, setQuotes] = useState<Quote[]>(() => restored?.quotes ?? [newQuote()]);
  const [activeIdx, setActiveIdx] = useState(() =>
    restored ? Math.min(restored.activeIdx, (restored.quotes.length || 1) - 1) : 0
  );

  /* The basket that just became a document (ticket, order, invoice, BL or quote). It stays on
     screen while its done panel / print prompt is open — the panel reads its totals — but it is
     never restored: the next "New sale" starts empty (client, 2026-10-16: the cart kept the last
     sale). Cleared in memory when the seller closes the prompt or starts a new sale. */
  const [completedId, setCompletedId] = useState<string | null>(null);

  /**
   * A confirmed, paid counter sale waiting for the warehouse to pick its in-stock parts (2.3).
   * Snapshots what the done panel will need, because the basket is gone by the time it opens.
   */
  const [prep, setPrep] = useState<{
    orderId: string;
    orderNumber: string;
    pickupCode: string | null;
    ttc: number;
    received: number;
    change: number;
    onAccount: boolean;
    awaitingEta: string | null;
    awaitingTTC: number;
  } | null>(null);
  // Read by the scanner callback, which is not rebuilt on every render.
  const prepRef = useRef(prep);
  prepRef.current = prep;
  const tillScanRef = useRef<(code: string) => Promise<void>>(async () => {});
  /** Bumped by a till scan so the panel re-reads the order at once instead of on its timer. */
  const [prepTick, setPrepTick] = useState(0);

  // Mirror the baskets to localStorage on every change; clear the entry once nothing is parked
  // so a finished till doesn't restore an empty shell (or leak a customer) on the next visit.
  useEffect(() => {
    if (isB2b) return;
    const toSave = quotes.map((q) =>
      q.id === completedId ? { ...q, cart: [], customer: null } : q
    );
    const hasContent = toSave.some((q) => q.cart.length > 0 || q.customer != null);
    if (!hasContent) {
      localStorage.removeItem(persistKey);
      return;
    }
    try {
      localStorage.setItem(
        persistKey,
        JSON.stringify({ quotes: toSave, activeIdx, seq: quoteSeq })
      );
    } catch {
      /* quota/private-mode — persistence is best-effort, never block the sale */
    }
  }, [quotes, activeIdx, isB2b, persistKey, completedId]);

  /** Empty the completed basket on screen too (new sale, or the print prompt was closed). */
  const clearCompleted = () => {
    if (!completedId) return;
    setQuotes((qs) =>
      qs.map((q) => (q.id === completedId ? { ...q, cart: [], customer: null } : q))
    );
    setCompletedId(null);
  };
  const active = quotes[activeIdx] ?? quotes[0];
  const customer = active.customer;
  const cart = active.cart;
  const customerId = customer?.id ?? null;

  /**
   * Reopening a saved quote (`/orders/new?quoteId=…`).
   *
   * A quote is now a piece of paper — nothing is held for it — so editing one is simply loading
   * its lines back into this screen, where parts can be added or removed like any other basket,
   * and saving replaces the quote. Availability is re-read LIVE rather than trusted from the
   * quote, because between raising it and reopening it the shelf has moved and nothing was
   * reserved to stop that. The quoted unit prices ARE preserved (as per-line overrides): the
   * customer was given those prices, and Invariant #20 keeps them.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const editingQuoteId = searchParams.get('quoteId');
  const [quoteLoadError, setQuoteLoadError] = useState<string | null>(null);
  const hydratedQuoteRef = useRef<string | null>(null);

  useEffect(() => {
    if (!editingQuoteId || hydratedQuoteRef.current === editingQuoteId) return;
    hydratedQuoteRef.current = editingQuoteId;
    let alive = true;
    (async () => {
      try {
        const { data: q } = await apiClient.get<{
          id: string;
          customer_id: string | null;
          licensePlate?: string | null;
          lines?: Array<{
            productId: string;
            quantity: number;
            unitPriceHT: string | number;
            productSku?: string | null;
            productName?: string | null;
            productBrand?: string | null;
          }>;
        }>(`/quotes/${editingQuoteId}`);
        const lines = q.lines ?? [];
        if (!alive || lines.length === 0) return;

        if (q.customer_id) {
          const { data: c } = await apiClient.get<CustomerHit>(`/customers/${q.customer_id}`);
          if (alive) setCustomer(c);
        }
        const ids = lines.map((l) => l.productId).join(',');
        const { data: avs } = await apiClient.get<Availability[]>(
          `/catalog/availability?ids=${encodeURIComponent(ids)}` +
            (q.customer_id ? `&customerId=${q.customer_id}` : '')
        );
        if (!alive) return;
        const byId = new Map(avs.map((a) => [a.productId, a]));
        setCart(() =>
          lines.flatMap((l) => {
            const av = byId.get(l.productId);
            if (!av?.price) return []; // product no longer priceable — drop rather than guess
            return [
              {
                productId: l.productId,
                sku: l.productSku ?? '',
                name: l.productName ?? l.productSku ?? l.productId,
                brand: l.productBrand ?? null,
                qty: l.quantity,
                price: av.price,
                source: (av.internalStock ?? 0) >= l.quantity ? 'INTERNAL' : 'EXTERNAL',
                cost: av.cost,
                /* Carried as an override ONLY when it actually departs from today's grid price.
                   It used to be set unconditionally, which stamped every reopened line as
                   "custom" even when the quote had simply been raised at catalogue price — a
                   pencil mark on nothing, and, since #14, a line an operator without price
                   authority would be refused (403) for merely reopening their own quote. */
                customPriceHT:
                  Number(l.unitPriceHT) !== av.price.finalUnitPriceHT
                    ? Number(l.unitPriceHT)
                    : undefined,
              } as CartLine,
            ];
          })
        );
        if (q.licensePlate) {
          plateTouched.current = true;
          setLicensePlate(q.licensePlate);
        }
      } catch {
        if (alive) setQuoteLoadError(t('counter.quoteLoadFailed'));
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingQuoteId]);

  // The vehicle this sale is for. Optional, and normalised to uppercase alphanumerics as it is
  // typed so the counter sees exactly what will be stored and searched.
  const [licensePlate, setLicensePlate] = useState('');
  // Pre-fill from the customer's last document. A DEFAULT only — the value that counts is the
  // one on THIS document, so it is never re-applied over something the operator has typed.
  const plateTouched = useRef(false);
  useEffect(() => {
    plateTouched.current = false;
    setLicensePlate('');
    if (!customerId) return;
    let alive = true;
    apiClient
      .get<{ data: { licensePlate: string | null } }>(`/customers/${customerId}/last-plate`)
      .then((r) => {
        if (alive && !plateTouched.current && r.data.data.licensePlate) {
          setLicensePlate(r.data.data.licensePlate);
        }
      })
      .catch(() => {}); // a missing default is not an error — the field just stays empty
    return () => {
      alive = false;
    };
  }, [customerId]);

  const patchActive = (patch: Partial<Quote>) =>
    setQuotes((qs) => qs.map((q, i) => (i === activeIdx ? { ...q, ...patch } : q)));
  const setCart = (updater: (c: CartLine[]) => CartLine[]) =>
    setQuotes((qs) => qs.map((q, i) => (i === activeIdx ? { ...q, cart: updater(q.cart) } : q)));
  const setCustomer = (c: CustomerHit | null) => patchActive({ customer: c });

  const addQuoteTab = () => {
    setQuotes((qs) => [...qs, newQuote()]);
    setActiveIdx(quotes.length);
  };
  const closeQuoteTab = (idx: number) => {
    if (quotes.length <= 1) return;
    setQuotes((qs) => qs.filter((_, i) => i !== idx));
    setActiveIdx((cur) => {
      const next = idx < cur ? cur - 1 : cur;
      return Math.min(next, quotes.length - 2);
    });
  };

  useEffect(() => {
    if (isB2b && !active.customer) {
      setCustomer({
        id: user!.customerId!,
        name: user!.email ?? t('counter.myCompany'),
        siret: null,
        customer_type: 'B2B',
        // Placeholder until the real relation loads — null = "unknown here", not "no credit".
        credit_limit: null,
        current_balance: '0',
        reserved_credit: '0',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isB2b, user]);

  // -------- customer picker --------
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerHits, setCustomerHits] = useState<CustomerHit[]>([]);
  const lastQ = useRef('');
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isB2b || customerQuery.trim().length < 2) {
      setCustomerHits([]);
      return;
    }
    lastQ.current = customerQuery;
    const t = setTimeout(async () => {
      if (lastQ.current !== customerQuery) return;
      try {
        const { data } = await apiClient.get<CustomerHit[]>(
          `/customers/search?q=${encodeURIComponent(customerQuery)}`
        );
        setCustomerHits(data);
      } catch (err) {
        showApiError(err);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [customerQuery, isB2b, showApiError]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickerOpen]);

  const pickWalkIn = async () => {
    try {
      const { data } = await apiClient.get<{ id: string }>('/customers/walk-in');
      setCustomer({
        id: data.id,
        name: t('counter.walkIn'),
        siret: null,
        customer_type: 'WALK_IN',
        // A walk-in never uses credit; the gauge is skipped on customer_type anyway.
        credit_limit: null,
        current_balance: '0',
        reserved_credit: '0',
      });
      setPickerOpen(false);
    } catch (err) {
      showApiError(err);
    }
  };

  // -------- create a customer without leaving the sale --------
  // CUSTOMER_CREATE is the action the server's route guard checks for POST /customers/{b2b,b2c}
  // (routeActionMap), so gating on it means the button is only offered to someone whose POST
  // would actually be accepted — including an account individually granted it by override.
  const canCreateCustomer = useAction('CUSTOMER_CREATE');
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);

  const openCreateCustomer = () => {
    // Close the dropdown first: the panel renders outside pickerRef, so leaving the picker
    // open would let its outside-click handler fire on every click inside the form.
    setPickerOpen(false);
    setCustomerQuery('');
    setShowCreateCustomer(true);
  };

  // The create endpoints return only { id, customerType }, but the picker needs the same
  // shape /customers/search yields — including the credit fields the B2B meter reads. Fetch
  // the full record rather than synthesising a half-populated hit.
  const onCustomerCreated = async (created: { id: string }) => {
    try {
      const { data } = await apiClient.get<CustomerHit>(`/customers/${created.id}`);
      setCustomer(data);
    } catch (err) {
      // The relation exists either way — don't strand the user on a failed re-read.
      showApiError(err);
      push({ tone: 'warning', message: t('counter.customerCreated') });
    }
  };

  // Step 1 is mandatory: a customer drives the whole session's pricing grid. Pre-select the
  // walk-in (standard tier) on first load so the seller can search & add immediately; they can
  // swap it from the tab's customer picker or create a new one. Counter only — B2B is JWT-bound.
  const didPreselect = useRef(false);
  useEffect(() => {
    if (isB2b || didPreselect.current || active.customer) return;
    didPreselect.current = true;
    void pickWalkIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isB2b]);

  // -------- product search --------
  type SearchMode = 'ALL' | 'EAN';
  /**
   * The search text is seeded from `?q=` so leaving and coming back returns to the same search —
   * see the URL-sync effect below for why it lives there rather than in state alone.
   */
  const [pq, setPq] = useState(() => searchParams.get('q') ?? '');
  const [rows, setRows] = useState<Row[]>([]);
  const [results, setResults] = useState<'ok' | 'loading' | 'empty'>('empty');
  /** Which row's identity panel is open, and on which tab. Null = closed. */
  const [infoPanel, setInfoPanel] = useState<{ reference: string; tab: ArticleInfoTab } | null>(
    null
  );
  const [partialWarn, setPartialWarn] = useState(false);
  /** Equivalents a supplier is still being asked about for the current search (see searchProduct). */
  const [supplierPending, setSupplierPending] = useState(0);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    },
    []
  );
  const [selectedBrands, setSelectedBrands] = useState<Set<string>>(new Set());
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  /**
   * Selected categories, as category IDS from anywhere in the tree — a top-level group and one of
   * its sub-categories live in the same set. A row matches when ANY id on its own chain is in here,
   * so picking a parent covers its children by construction rather than by expanding them first.
   */
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  /** Which top-level categories are unfolded to show their sub-categories. */
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  /** Selected criteria, keyed by specKey() so one flat set covers every spec group. */
  const [selectedSpecs, setSelectedSpecs] = useState<Set<string>>(new Set());
  /** Free text over the FACET LABELS — "which filter am I looking for", not "which part". */
  const [facetQuery, setFacetQuery] = useState('');
  const [brandSort, setBrandSort] = useState<'alpha' | 'count'>('alpha');
  /** Groups the operator has folded away, and the ones they asked to see in full. */
  const [foldedGroups, setFoldedGroups] = useState<Set<string>>(new Set());
  const [shownInFull, setShownInFull] = useState<Set<string>>(new Set());
  const [filtersHidden, setFiltersHidden] = useState(false);
  const [availTab, setAvailTab] = useState<'all' | 'ok' | 'warn' | 'out'>('all');
  // Devis → Caisse with "Unavailable" ticked would strand the seller on a filter whose control
  // has just been removed from the screen, looking at an empty list with nothing to un-tick.
  useEffect(() => {
    if (availTab === 'out') setAvailTab('all');
  }, [availTab]);
  /**
   * Price ascending is the DEFAULT and there is no "relevance" option.
   *
   * Relevance is not one order among four — it is the pin under all three, applied in `sortedRows`
   * and never offered as a choice. Made selectable it would have to be dismissable, and "the
   * reference I typed" is not a preference a seller can sensibly switch off.
   *
   * The three offered are the three questions actually being asked of a list already on screen:
   * what is cheapest, what is soonest, what earns most.
   */
  const [sortKey, setSortKey] = useState<SortKey>('price');
  const [sortOpen, setSortOpen] = useState(false);
  /**
   * The term the rows on screen actually came from — NOT what is currently in the box.
   *
   * The two diverged the moment typing stopped searching. A seller half-way through typing a new
   * reference is still looking at the results of the last one, and anything that re-runs the
   * search on their behalf (a customer change, below) has to re-run THAT, or the screen would
   * silently answer a question nobody has asked yet.
   */
  const searchedTerm = useRef<string | null>(null);

  /* Recent searches, and whether the dropdown is showing them. `histIdx` is the keyboard highlight:
     -1 means "none", i.e. Enter still runs whatever is typed rather than a suggestion. */
  const historyKey = historyKeyFor(user?.sub);
  const [history, setHistory] = useState<string[]>(() => loadHistory(historyKey));
  const [historyOpen, setHistoryOpen] = useState(false);
  const [histIdx, setHistIdx] = useState(-1);
  // A second cashier logging in on the same terminal gets their own list, not the previous one's.
  useEffect(() => {
    setHistory(loadHistory(historyKey));
  }, [historyKey]);
  const rememberSearch = (term: string) => {
    setHistory((cur) => {
      const next = pushHistory(cur, term);
      try {
        localStorage.setItem(historyKey, JSON.stringify(next));
      } catch {
        /* private mode / quota → the dropdown still works for this session */
      }
      return next;
    });
  };
  const forgetSearch = (term?: string) => {
    setHistory((cur) => {
      const next = term == null ? [] : cur.filter((h) => h !== term);
      try {
        localStorage.setItem(historyKey, JSON.stringify(next));
      } catch {
        /* as above */
      }
      return next;
    });
    setHistIdx(-1);
  };
  /* What the dropdown actually offers: everything on an empty box, and as the seller types, the
     recents that contain it. The exact term already in the box is dropped — offering someone the
     string they are looking at is a row that can only waste a keystroke. */
  const historyShown = useMemo(() => {
    const needle = pq.trim().toLowerCase();
    return history.filter((h) => {
      const hl = h.toLowerCase();
      return hl !== needle && (!needle || hl.includes(needle));
    });
  }, [history, pq]);

  /**
   * `round` > 0 is a silent follow-up of the same search: a supplier's live budget per search is
   * smaller than a big equivalents family, the rest is looked up in the background, and the server
   * says how many are still pending (`supplierPending`). The page asks again a few times so the
   * whole offer appears without the seller having to search twice — keeping the rows on screen
   * meanwhile rather than flashing back to a spinner.
   */
  const searchProduct = async (
    text: string,
    m: SearchMode = 'ALL',
    round = 0,
    /** Browse a universal group (oils, batteries…) or one category code instead of a reference. */
    browseBy?: { group: string } | { category: string }
  ) => {
    searchedTerm.current = text;
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
    if (!text.trim()) {
      setRows([]);
      setResults('empty');
      setSupplierPending(0);
      return;
    }
    if (round === 0) {
      setResults('loading');
      setSelectedBrands(new Set());
      setPartialWarn(false);
      setSupplierPending(0);
    }
    try {
      const { data: body } = await apiClient.get<{ hits: CatalogHit[]; supplierPending: number }>(
        // supplier=1: the order flow is the priority consumer of the supplier API's rate limit —
        // this is where external stock is sold to a customer for D+1/D+2.
        // offer=full: the whole equivalents family, the way the supplier's own catalogue answers a
        // reference — the seller should see the Mann and Filtron alternatives, not just the Bosch
        // number that was typed.
        browseBy
          ? 'group' in browseBy
            ? `/catalog/browse?group=${encodeURIComponent(browseBy.group)}&meta=1`
            : `/catalog/browse?category=${encodeURIComponent(browseBy.category)}&meta=1`
          : `/catalog/search?q=${encodeURIComponent(text)}&mode=${m}&supplier=1&offer=full&meta=1`
      );
      // The seller has typed something else meanwhile: this answer is for an old question.
      if (searchedTerm.current !== text) return;
      const hits = body.hits;
      const pending = body.supplierPending ?? 0;
      setSupplierPending(round < MAX_OFFER_REFRESH ? pending : 0);
      if (pending > 0 && round < MAX_OFFER_REFRESH) {
        refreshTimer.current = setTimeout(() => {
          if (searchedTerm.current === text) void searchProduct(text, m, round + 1, browseBy);
        }, OFFER_REFRESH_MS);
      }
      let enriched: Row[] = hits.map((h) => ({ ...h, avail: 'out' as AvailState }));
      // Supplier rows carry no product record: their id is a synthetic key, so they must be kept
      // out of the availability lookup (which resolves product UUIDs) and enriched from the quote
      // that came back with the search itself.
      const catalogHits = hits.filter((h) => h.source !== 'SUPPLIER');
      if (catalogHits.length > 0) {
        try {
          const ids = catalogHits.map((h) => h.id).join(',');
          const { data: avs } = await apiClient.get<Availability[]>(
            `/catalog/availability?ids=${encodeURIComponent(ids)}${customerId ? `&customerId=${customerId}` : ''}`
          );
          const byId = new Map(avs.map((a) => [a.productId, a]));
          enriched = hits.map((h) => {
            if (h.source === 'SUPPLIER') return supplierRow(h);
            const av = byId.get(h.id);
            /* TWO SOURCES FOR ONE FACT, and the weaker one must not erase the stronger.
               `/catalog/availability` quotes through `supplier_article_mapping`, so it is blind to
               any product that has never been bought through the supplier's API — it answers null,
               not "none". The SEARCH meanwhile quotes the reference live and attaches the
               supplier's own answer to our row when they match (see the merge in
               CatalogService.searchWithSuppliers, which exists for exactly this reason). Taking
               `av` unconditionally threw that away and showed a part the supplier is offering as
               having no external stock at all. */
            const leadDays = av?.supplierLeadDays ?? h.supplier?.leadTimeDays ?? null;
            return {
              ...h,
              price: av?.price ?? undefined,
              externalPrice: av?.externalPrice ?? null,
              internalStock: av?.internalStock,
              supplierQty: av?.supplierQty ?? h.supplier?.availableQty ?? null,
              supplierName: av?.supplierName ?? h.supplier?.supplierName,
              supplierCode: av?.supplierCode ?? h.supplier?.supplierCode,
              leadDays,
              // Same rule as the quantity above: the availability call's slots when it found the
              // supplier, the search's own when it was blind to it.
              deliverySlots: av?.supplierSlots?.length
                ? av.supplierSlots
                : (h.supplier?.deliverySlots ?? av?.supplierSlots ?? []),
              promiseRule: av?.supplierPromise ?? h.supplier?.promiseRule,
              avail: availOf(av?.internalStock, leadDays),
              cost: av?.cost,
              marginPct: av?.marginPct ?? null,
            };
          });
        } catch {
          setPartialWarn(true);
        }
      } else {
        enriched = hits.map((h) => (h.source === 'SUPPLIER' ? supplierRow(h) : (h as Row)));
      }
      setRows(enriched);
      /* Nothing YET is not nothing: while suppliers are still answering, the first round can come
         back empty (TCK87 on prod, 2026-10-22: 0 rows for 7 s, then 38) — keep saying the search is
         running rather than "no result". */
      const stillSearching = pending > 0 && round < MAX_OFFER_REFRESH;
      setResults(enriched.length ? 'ok' : stillSearching ? 'loading' : 'empty');
      /* Only a search that FOUND something is worth remembering. History here is a shortcut back
         to somewhere the seller has already been, not a log of everything they typed — recording
         the misses would make the dropdown a list of the day's typos. */
      if (round > 0) return; // a follow-up only widens the list: no history, no scan feedback
      if (enriched.length > 0 && !browseBy) rememberSearch(text);
      if (m === 'EAN' && enriched.length === 1 && customer) {
        beep.success();
        await addToCart(enriched[0]);
      } else if (m === 'EAN' && enriched.length === 0) {
        beep.wrongProduct();
        push({
          tone: 'error',
          title: t('counter.productNotFound'),
          message: t('counter.noBarcodeMatch', { code: text }),
        });
      }
    } catch (err) {
      if (round > 0) return; // a failed follow-up keeps the list already shown
      showApiError(err);
      setResults('empty');
    }
  };

  /** One category from the resting-state tabs (Settings → Counter tabs, migration 148). Universal
   *  groups (oils, batteries, accessories) are categories too (UNI-OILS…). */
  const browseCategory = (c: { code: string; name: string }) => {
    setPq(c.name);
    setHistoryOpen(false);
    void searchProduct(c.name, 'ALL', 0, { category: c.code });
  };

  /** Run a remembered term: fill the box and search it, the click being the "go" in itself. */
  const runSearchTerm = (term: string) => {
    setPq(term);
    setHistoryOpen(false);
    setHistIdx(-1);
    void searchProduct(term);
    searchRef.current?.focus();
  };

  /**
   * SEARCHING IS AN EXPLICIT ACT. Typing is not.
   *
   * This used to fire the whole search 300 ms after every keystroke, which meant one fifteen-
   * character OE number ran a dozen searches — each one hitting the catalogue, the equivalence
   * expansion and the supplier lane — and the seller watched the list rearrange itself under
   * their hands while they were still typing. Enter and the Search button now run it, and nothing
   * else does. `pq` is deliberately NOT a dependency here.
   *
   * Three things still search without being asked, and all three are explicit acts of their own:
   * a barcode SCAN (the scanner's job is to mean "this one"), a click on a RECENT search, and the
   * two cases below.
   */
  useEffect(() => {
    // Mount with a restored `?q=` (Back, refresh, a pasted link) → show its results, or the box
    // comes back filled in above an empty list. After that: the customer changed, and price and
    // discount are customer-specific, so the rows on screen are quoting the wrong person until
    // they are re-fetched. Re-runs the EXECUTED term, never the half-typed one.
    const term = searchedTerm.current ?? pq;
    if (!term.trim()) return;
    void searchProduct(term);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  /**
   * The search text lives in the URL, not only in component state.
   *
   * Opening a row's catalogue sheet LEAVES this screen, and coming back through the browser's back
   * button remounts it — which used to mean an empty search box and a seller retyping the reference
   * they had been looking at a second earlier. `?q=` makes Back restore the search, and a refresh
   * or a pasted link land on it too.
   *
   * Two deliberate choices:
   *   • `replace`, on its own 300 ms debounce — pushing a history entry per keystroke would turn
   *     one Back press into thirteen, each landing on a prefix of the word. This debounce is the
   *     last one left: it writes the URL only, and never runs a search.
   *   • the RESULTS are not cached, only the query. They are re-fetched from the restored text,
   *     because a counter must never quote a price or a stock figure that was merely true when the
   *     seller wandered off. Restoring the search is free; restoring stale numbers is not.
   */
  useEffect(() => {
    if ((searchParams.get('q') ?? '') === pq) return;
    const t = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (pq) next.set('q', pq);
          else next.delete('q');
          return next;
        },
        { replace: true }
      );
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pq]);

  // -------- keyboard shortcuts (the hint strip under the search row) --------
  // `/` focus search · Esc leave search / close what's open · Alt+1…9 switch parked quote.
  // Enter is handled by the input itself. Alt digits are read off e.code, not e.key: on an
  // AZERTY till Alt+1 does not produce "1".
  const searchRef = useRef<HTMLInputElement>(null);
  // The strip is a beginner aid: once a cashier knows the keys it is just noise, so it can be
  // dismissed, and the choice sticks across sessions (per browser, like the parked baskets).
  // The keys keep working either way — hiding the guide never unbinds anything.
  const [hotkeysShown, setHotkeysShown] = useState(() => {
    try {
      return localStorage.getItem(HOTKEYBAR_KEY) !== 'hidden';
    } catch {
      return true; // private mode / storage blocked → show it
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(HOTKEYBAR_KEY, hotkeysShown ? 'shown' : 'hidden');
    } catch {
      /* best-effort, never block the till */
    }
  }, [hotkeysShown]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing =
        t?.tagName === 'INPUT' ||
        t?.tagName === 'TEXTAREA' ||
        t?.tagName === 'SELECT' ||
        !!t?.isContentEditable;

      if (e.altKey && !e.ctrlKey && !e.metaKey && /^Digit[1-9]$/.test(e.code)) {
        if (isB2b) return; // B2B portal has a single JWT-bound quote — nothing to switch to
        const idx = Number(e.code.slice(5)) - 1;
        if (idx >= quotes.length) return;
        e.preventDefault();
        setActiveIdx(idx);
        setPickerOpen(false);
        return;
      }
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
        return;
      }
      if (e.key === 'Escape') {
        // Overlays own Escape first, so it always closes the innermost thing.
        if (showCreateCustomer) setShowCreateCustomer(false);
        else if (pickerOpen) setPickerOpen(false);
        // The history dropdown is inside the search box, so it closes BEFORE the box gives up
        // focus — otherwise one Escape would dismiss both and cost the seller their cursor.
        else if (historyOpen) setHistoryOpen(false);
        else if (document.activeElement === searchRef.current) searchRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isB2b, quotes.length, pickerOpen, showCreateCustomer, historyOpen]);

  useScanInput({
    // The operator is typing a code by hand in the dialog: the same keystrokes must not also
    // be read as a scan, or one EAN counts twice at the till.
    paused: codePromptOpen,
    onScan: (text) => {
      // While a sale is being prepared, the scanner takes its units off the shelf — no search.
      if (prepRef.current) {
        void tillScanRef.current(text);
        return;
      }
      if (!customer) {
        beep.error();
        push({ tone: 'warning', message: t('counter.pickCustomerScan') });
        return;
      }
      setPq(text);
      void searchProduct(text, 'EAN');
    },
  });

  // facets + tabs
  /**
   * What a PAY-NOW ticket is allowed to offer — the result set every facet, count and tab below
   * is computed from.
   *
   * At the till the customer leaves with the part, so a row nobody can deliver is not a slow sale,
   * it is not a sale: `out` means no stock on our shelf AND no supplier lead time, i.e. the part
   * exists in the catalogue and that is all. Offering it at the counter costs the seller a click
   * and a conversation to find that out.
   *
   * `warn` rows STAY. Those are the D+1/D+2 supplier offers, and selling a supplier's shelf is the
   * point of the lane — the seller quotes the lead time and the customer decides. Only the dead
   * rows go.
   *
   * A DEFERRED order (/orders/new) and a QUOTE keep everything: both are promises about a future
   * date, so a part that has to be sourced is a perfectly ordinary line on them. That is why this
   * follows `mode` rather than the route — flipping the toggle to Devis is the seller saying "this
   * one isn't walking out today", and the rows come straight back.
   *
   * Filtering HERE rather than in `visibleRows` is deliberate: everything downstream — brand,
   * category, type and criteria facets, the availability tabs, the reference count — is derived
   * from this one list, so the rail can never advertise "Bosch (7)" and then show four rows.
   */
  const sellableRows = useMemo(
    /* Only what can actually be supplied, on every screen (feedback 20 Oct 2026, 1.5: "do not
       display offers with zero stock"). That includes a supplier row with NO answer — listed by
       the supplier but not quoted — which used to stay as "no offer found": on GDB1330 that was
       ten rows of noise for every useful one. They are counted in the "N masquées" note instead. */
    () => rows.filter((r) => r.avail !== 'out'),
    [rows]
  );
  /** How many rows the till is holding back, for the note that says so. */
  const hiddenUnsellable = rows.length - sellableRows.length;

  const brandFacets = useMemo(() => {
    const counts = new Map<string, number>();
    const logos = new Map<string, string | null>();
    for (const r of sellableRows) {
      if (!r.brand) continue;
      counts.set(r.brand, (counts.get(r.brand) ?? 0) + 1);
      if (!logos.get(r.brand) && r.brandImageUrl) logos.set(r.brand, r.brandImageUrl);
    }
    const list = [...counts.entries()].map(([brand, n]) => ({
      brand,
      n,
      logo: logos.get(brand) ?? null,
    }));
    return brandSort === 'count'
      ? list.sort((a, b) => b.n - a.n || a.brand.localeCompare(b.brand))
      : list.sort((a, b) => a.brand.localeCompare(b.brand));
  }, [sellableRows, brandSort]);

  /**
   * The category tree, two levels deep, counted at both — "Braking system (24) › Brake disc (20)".
   *
   * TWO LEVELS, not the whole chain: the server sends the full walk (assembly group down to the
   * generic article), but a rail that renders every rung turns one search into a five-deep
   * accordion. The top of the chain is the cut a seller makes first and the next rung narrows it;
   * anything deeper is the part designation, which has its own group below.
   *
   * Rows the walk could not file — an unfiled product, or a SUPPLIER row that has no product record
   * to file — are NOT dropped. They collect under one explicit bucket, because a filter that
   * silently omits rows from its own counts is worse than one that admits it doesn't know.
   */
  const categoryFacets = useMemo(() => {
    const tops = new Map<
      string,
      {
        id: string;
        name: string;
        n: number;
        subs: Map<string, { id: string; name: string; n: number }>;
      }
    >();
    for (const r of sellableRows) {
      const path = r.categoryPath ?? [];
      const top = path[0] ?? { id: UNFILED_CATEGORY, name: t('counter.uncategorised') };
      const entry = tops.get(top.id) ?? { id: top.id, name: top.name, n: 0, subs: new Map() };
      entry.n += 1;
      const sub = path[1];
      if (sub) {
        const s = entry.subs.get(sub.id) ?? { id: sub.id, name: sub.name, n: 0 };
        s.n += 1;
        entry.subs.set(sub.id, s);
      }
      tops.set(top.id, entry);
    }
    return (
      [...tops.values()]
        .map((c) => ({
          ...c,
          subs: [...c.subs.values()].sort((a, b) => b.n - a.n || a.name.localeCompare(b.name)),
        }))
        // The unfiled bucket sorts last whatever it holds: it is a residue, not a category, and
        // letting a big one head the list would make the rail look like it groups by nothing.
        .sort((a, b) =>
          a.id === UNFILED_CATEGORY
            ? 1
            : b.id === UNFILED_CATEGORY
              ? -1
              : b.n - a.n || a.name.localeCompare(b.name)
        )
    );
  }, [sellableRows, t]);

  /**
   * The part TYPE, counted — "Air filter (20)", "Filter Set (1)".
   *
   * A full-offer search returns one family, but not one product: asking for an air filter also
   * turns up the filter SET it belongs to and the housing gasket. Splitting on the designation is
   * the first cut a seller makes, which is why it sits above the rest of the panel.
   */
  const typeFacets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of sellableRows) if (r.name) counts.set(r.name, (counts.get(r.name) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [sellableRows]);

  /**
   * Technical criteria as facets, one group per criterion — the same TecDoc specifications the
   * result rows print in their dimensions strip.
   *
   * Only criteria that can actually DIVIDE the result set are kept: a label with one distinct
   * value across every row filters nothing, and a label with a value per row (a length in mm) is a
   * measurement rather than a facet and would render as a wall of one-hit checkboxes.
   */
  const specFacets = useMemo(() => {
    /* Keyed on the RAW label, titled with the tidied one. The key is what `visibleRows` matches a
       row's own `s.label` against, so it has to stay exactly as TecDoc sends it; only the heading
       drops the redundant unit bracket. Conflating the two would silently stop every criterion
       filter from matching anything. */
    const byLabel = new Map<string, { title: string; vals: Map<string, number> }>();
    for (const r of sellableRows) {
      for (const s of r.specifications ?? []) {
        const g = byLabel.get(s.label) ?? { title: specLabel(s), vals: new Map<string, number>() };
        g.vals.set(specValue(s), (g.vals.get(specValue(s)) ?? 0) + 1);
        byLabel.set(s.label, g);
      }
    }
    return [...byLabel.entries()]
      .filter(([, g]) => g.vals.size >= 2 && g.vals.size <= 12)
      .map(([label, { title, vals }]) => ({
        label,
        title,
        values: [...vals.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])),
      }))
      .sort((a, b) => a.values.length - b.values.length || a.label.localeCompare(b.label))
      .slice(0, 6);
  }, [sellableRows]);
  const availCounts = useMemo(
    () => ({
      all: sellableRows.length,
      ok: sellableRows.filter((r) => r.avail === 'ok').length,
      warn: sellableRows.filter((r) => r.avail === 'warn').length,
      // Always 0 in caisse mode — the tab and the rail checkbox are hidden there, see below.
      out: sellableRows.filter((r) => r.avail === 'out').length,
    }),
    [sellableRows]
  );
  const visibleRows = useMemo(() => {
    /* Criteria combine the way a parts catalogue's do: OR inside one criterion (two acceptable
       filter types), AND across criteria (that type AND that height). Selecting a second value in
       the same group must WIDEN the result set, never empty it. */
    const wanted = new Map<string, Set<string>>();
    for (const key of selectedSpecs) {
      const [label, value] = key.split(SPEC_SEP);
      const set = wanted.get(label) ?? new Set<string>();
      set.add(value);
      wanted.set(label, set);
    }
    return (
      sellableRows
        .filter((r) => selectedBrands.size === 0 || (r.brand && selectedBrands.has(r.brand)))
        /* ANY id on the chain, so a top-level pick covers every sub-category under it without the
         rail having to expand the branch and tick the children itself. */
        .filter((r) => {
          if (selectedCategories.size === 0) return true;
          const path = r.categoryPath ?? [];
          if (path.length === 0) return selectedCategories.has(UNFILED_CATEGORY);
          return path.some((c) => selectedCategories.has(c.id));
        })
        .filter((r) => selectedTypes.size === 0 || selectedTypes.has(r.name))
        .filter((r) => {
          if (wanted.size === 0) return true;
          const specs = r.specifications ?? [];
          return [...wanted.entries()].every(([label, values]) =>
            specs.some(
              (s) => s.label === label && values.has(s.unit ? `${s.value} ${s.unit}` : s.value)
            )
          );
        })
        .filter((r) => availTab === 'all' || r.avail === availTab)
    );
  }, [sellableRows, selectedBrands, selectedCategories, selectedTypes, selectedSpecs, availTab]);

  /**
   * The chosen order, applied to the PRODUCT LIST only.
   *
   * It stops at the row boundary: the origins inside a row (our depot, then whatever a supplier
   * holds) keep their fixed order whatever this is set to — see the Branches cell in PartCard.
   *
   * THE TYPED REFERENCE IS PINNED, AND NOTHING ELSE IS. The server labels the row matching what
   * was typed `TYPED_REF` (rankHits); it stays on line 1 under every order, because it is the
   * answer to the question actually asked. Every other row is ordered by the chosen key.
   *
   * WHY ONLY THAT (feedback 20 Oct 2026, 1.2: "sorting by price, delivery time and margin has no
   * effect"). The list used to be ordered by the server's whole relevance ladder first — in
   * stock, supplier, catalogue — with the chosen key only breaking ties inside a rung. The server
   * already price-sorts inside each rung, so "price" changed nothing; most rows are supplier rows
   * on the same D+1, so "delivery" changed nothing; and every supplier row is priced by the same
   * family coefficient, so the margin PERCENTAGE was identical and "margin" changed nothing. Rows
   * nobody can supply are no longer on the screen at all (sellableRows), which was the ladder's
   * remaining job. The ladder now only breaks exact ties.
   *
   * Each key has a second key so rows it cannot separate still read in a sensible order: price
   * then delivery, delivery then price, margin then price.
   */
  const sortOptions = useMemo(
    () => SORT_OPTIONS.filter((o) => !o.marginOnly || canSeeMargin),
    [canSeeMargin]
  );
  const sortedRows = useMemo(() => {
    // Belt and braces: a margin sort can only ever run for a role that may read margin, whatever
    // state says.
    const key: SortKey = sortKey === 'margin' && !canSeeMargin ? 'price' : sortKey;
    const priceOf = (r: Row): number =>
      r.price?.finalUnitPriceHT ?? r.supplier?.indicativeSellPriceHT ?? Number.POSITIVE_INFINITY;
    // On our shelf IS the shortest possible lead, whatever a supplier quotes against the same part.
    // A supplier row ranks by the supplier's own arrival moment (to the hour, cut-offs included),
    // not by whole days: two "tomorrow" rows at 08:00 and 19:30 are not a tie. A row the supplier
    // dated "to be confirmed" sinks; one quoted before dates existed keeps its day count.
    const now = Date.now();
    const leadOf = (r: Row): number =>
      r.avail === 'ok'
        ? 0
        : r.promiseRule
          ? r.promiseRule.leadDays * 86_400_000
          : r.deliverySlots?.length
            ? supplierEta(r.deliverySlots, 1, now).at - now
            : r.leadDays != null
              ? r.leadDays * 86_400_000
              : Number.POSITIVE_INFINITY;
    /* Margin in EUROS per unit, descending — the percentage is the same on every row priced by
       the same coefficient, so it cannot order anything. An unknown margin sinks: "no data" must
       never masquerade as "best". */
    const marginOf = (r: Row): number => {
      const price = priceOf(r);
      if (!Number.isFinite(price)) return Number.NEGATIVE_INFINITY;
      if (r.cost != null) return price - r.cost;
      if (r.marginPct != null) return (price * r.marginPct) / 100;
      return Number.NEGATIVE_INFINITY;
    };
    const diff = (x: number, y: number): number => (x === y ? 0 : x < y ? -1 : 1);
    const cmp =
      key === 'lead'
        ? (a: Row, b: Row) => diff(leadOf(a), leadOf(b)) || diff(priceOf(a), priceOf(b))
        : key === 'margin'
          ? (a: Row, b: Row) => diff(marginOf(b), marginOf(a)) || diff(priceOf(a), priceOf(b))
          : (a: Row, b: Row) => diff(priceOf(a), priceOf(b)) || diff(leadOf(a), leadOf(b));
    const pinned = (r: Row): number => (r.relevanceTier === 'TYPED_REF' ? 0 : 1);
    return [...visibleRows].sort(
      (a, b) =>
        pinned(a) - pinned(b) || cmp(a, b) || tierRank(a.relevanceTier) - tierRank(b.relevanceTier)
    );
  }, [visibleRows, sortKey, canSeeMargin]);

  /** One toggler for every facet family — they all behave identically. */
  const toggleIn =
    (setter: React.Dispatch<React.SetStateAction<Set<string>>>) =>
    (value: string): void =>
      setter((cur) => {
        const next = new Set(cur);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        return next;
      });
  const toggleBrand = toggleIn(setSelectedBrands);
  const toggleCategory = toggleIn(setSelectedCategories);
  const toggleCategoryOpen = toggleIn(setOpenCategories);
  const toggleType = toggleIn(setSelectedTypes);
  const toggleSpec = toggleIn(setSelectedSpecs);
  const toggleGroup = toggleIn(setFoldedGroups);
  const toggleFull = toggleIn(setShownInFull);

  const activeFilterCount =
    selectedBrands.size +
    selectedCategories.size +
    selectedTypes.size +
    selectedSpecs.size +
    (availTab === 'all' ? 0 : 1);
  /** Shared by the rail's own Clear control and the one the empty result state offers. */
  const clearAllFilters = () => {
    setSelectedBrands(new Set());
    setSelectedCategories(new Set());
    setSelectedTypes(new Set());
    setSelectedSpecs(new Set());
    setAvailTab('all');
  };
  /** The facet search hides whole groups whose title and options all miss the text. */
  const facetMatches = (...text: string[]): boolean =>
    !facetQuery.trim() ||
    text.some((t) => t.toLowerCase().includes(facetQuery.trim().toLowerCase()));

  // -------- cart --------
  /**
   * A basket entry is keyed by PRODUCT **and** ORIGIN, never by product alone.
   *
   * Two units off our shelf and one ordered in are the same reference at two prices, and they have
   * to reach the order as two lines. Merging them on productId would force a single unit price —
   * an average that reconciles to no purchase we ever made, reads as a mystery on the invoice, and
   * cannot be credited correctly when one of the three units comes back. The cart PANEL groups
   * them back under one product heading so the seller still sees one line with two sub-lines.
   */
  const cartKey = (productId: string, source: 'INTERNAL' | 'EXTERNAL'): string =>
    `${productId}:${source}`;

  /**
   * A cart line as the server takes it, on an order or a quote.
   *
   * A supplier line with no record yet sends its OFFER; `productId` on such a line is the search
   * row's id and is a basket key, never a product. The server mints (or reuses) the record at
   * that point — see services/catalog/offerLines.ts for why it is that moment and not earlier.
   */
  const lineForServer = (l: CartLine) => ({
    ...(l.offer ? { offer: l.offer } : { productId: l.productId }),
    quantity: l.qty,
    // The origin the seller composed. Without it the server re-derives the split from stock and
    // silently re-sources a line that was deliberately put on the supplier.
    source: l.source,
    ...(l.switchAgreed ? { switchAgreed: true } : {}),
    ...(l.customPriceHT != null ? { unitPriceHT: l.customPriceHT } : {}),
  });

  /** "Only 8 in stock" — the shelf line was held at the shelf; the rest is a supplier line, on purpose. */
  const notifyShelfCap = (sku: string, available: number) =>
    push({
      tone: 'warning',
      title: t('counter.shelfCap.title', { available }),
      message: t('counter.shelfCap.message', { sku, available }),
    });

  const pushLine = (
    r: Row,
    price: Price,
    source: 'INTERNAL' | 'EXTERNAL',
    qty: number,
    cost?: number,
    unitPriceHT?: number,
    /** A price negotiated on the RESULT ROW, kept as an override so the grid price it departs from
     *  survives alongside it. Distinct from `unitPriceHT`, which REPLACES the origin's price
     *  outright because a supplier-sourced unit genuinely has a different one. */
    customPriceHT?: number,
    /** A supplier's offer with no record behind it yet — carried to the server on the order. */
    offer?: CartLine['offer']
  ) =>
    setCart((cur) => {
      const idx = cur.findIndex((l) => cartKey(l.productId, l.source) === cartKey(r.id, source));
      // A shelf line stops at the shelf (see CartLine.stockQty); the seller is told below.
      const shelfCap = source === 'INTERNAL' ? (r.internalStock ?? undefined) : undefined;
      if (idx >= 0) {
        const next = [...cur];
        const cap = next[idx].stockQty ?? shelfCap;
        const wanted = next[idx].qty + qty;
        const capped = cap != null ? Math.min(wanted, Math.max(1, cap)) : wanted;
        if (capped < wanted) notifyShelfCap(r.sku, cap!);
        // A price set on the row is a deliberate act, so adding a second time at a new one
        // re-prices the whole line rather than being silently dropped for the existing figure.
        next[idx] = {
          ...next[idx],
          qty: capped,
          ...(customPriceHT != null && customPriceHT > 0
            ? { customPriceHT: round2(customPriceHT) }
            : {}),
        };
        return next;
      }
      const firstQty = shelfCap != null ? Math.min(qty, Math.max(1, shelfCap)) : qty;
      if (firstQty < qty) notifyShelfCap(r.sku, shelfCap!);
      return [
        ...cur,
        {
          productId: r.id,
          ...(offer ? { offer } : {}),
          sku: r.sku,
          name: r.name,
          brand: r.brand,
          qty: firstQty,
          ...(shelfCap != null ? { stockQty: shelfCap } : {}),
          // The ORIGIN's price, not the product's: an external unit was acquired at the supplier's
          // cost today, so it sells at its own figure.
          price:
            unitPriceHT != null && unitPriceHT !== price.finalUnitPriceHT
              ? { ...price, finalUnitPriceHT: unitPriceHT, unitPriceHT }
              : price,
          source,
          ...(customPriceHT != null && customPriceHT > 0
            ? { customPriceHT: round2(customPriceHT) }
            : {}),
          imageUrl: r.imageUrl,
          brandImageUrl: r.brandImageUrl,
          cost: cost ?? r.cost,
          ...(source === 'EXTERNAL'
            ? {
                supplierCode: r.supplierCode,
                leadDays: r.leadDays ?? undefined,
                deliverySlots: r.deliverySlots ?? [],
                promiseRule: r.promiseRule,
              }
            : {}),
          // What the seller was looking at when they put this in the basket. Kept so the promise
          // can be RE-checked at confirmation and any drift shown before the order goes out.
          ...(() => {
            const eta =
              source === 'INTERNAL'
                ? deliveryEta(0, calendar, Date.now())
                : externalEta(r, qty, Date.now(), calendar);
            return { promisedAt: eta.at, promisedLabel: eta.label };
          })(),
        },
      ];
    });

  /** One composed origin, straight from the multi-source composer on the result row. */
  const addOrigin = (
    r: Row,
    source: 'INTERNAL' | 'EXTERNAL',
    qty: number,
    unitPriceHT: number
  ): void => {
    if (!customer) {
      push({ tone: 'warning', message: t('counter.pickCustomerFirst') });
      return;
    }
    const base = (source === 'EXTERNAL' ? r.externalPrice : r.price) ?? r.price;
    if (!base) return;
    pushLine(r, base, source, qty, r.cost, unitPriceHT);
  };
  /**
   * `customPriceHT` is the price the seller worked out on the ROW's margin desk, before this line
   * existed. It is carried as an OVERRIDE rather than folded into the line's price, so the cart
   * still knows the catalogue figure it departs from: that is what the ✎ marker reads, what "reset
   * to catalogue" restores, and what the discount field in the cart computes its percentage off.
   */
  const addToCart = async (r: Row, qty = 1, customPriceHT?: number) => {
    if (!customer) {
      push({ tone: 'warning', message: t('counter.pickCustomerFirst') });
      return;
    }
    /* A SUPPLIER row sells like any other row — as an EXTERNAL line, sourced from that supplier
       with a lead time and a promised date. Whether the reference exists in our database decides
       nothing here; the SOURCE of the line does.

       The order chain still needs a record to hang the line off, so the server finds or creates
       one — UNLISTED, never in the search, nothing to complete, nobody notified — and the seller
       is not told about it, because nothing was catalogued. The server re-asks the supplier for
       the price (the client never sends a cost, which is what lets the counter hold the
       permission at all), and the line is then priced through the ordinary availability call. */
    if (r.source === 'SUPPLIER') {
      const off = r.supplier;
      // `r.sku` on a supplier row is the MANUFACTURER reference the search matched on, not our
      // SKU — see CatalogService.buildSupplierRows. It is the only number the supplier can be
      // asked again with, so it is what identifies the offer server-side.
      if (!off?.supplierCode || !r.sku || !canSourceFromSupplier) {
        push({
          tone: 'warning',
          title: t('counter.supplierOrderOnly'),
          message: t('counter.supplierOrderOnlyDetail', {
            brand: r.brand ?? '',
            sku: r.sku,
          }),
        });
        return;
      }
      setCreatingKey(r.id);
      try {
        /* PRICE IT, DO NOT MINT IT. The server re-quotes the supplier and prices the offer
           through the customer's own cascade without writing anything: a part that is only
           looked at, or basketed and abandoned, leaves no row in `products`. The record is made
           once, by POST /orders (or by saving the quote) — see services/catalog/offerLines.ts. */
        const { data: q } = await apiClient.post<{
          productId: string | null;
          price: Price | null;
          leadTimeDays: number | null;
          deliverySlots?: DeliverySlot[];
          promiseRule?: PromiseRule;
          cost?: number;
        }>('/catalog/offers/quote', {
          supplierCode: off.supplierCode,
          matchedNumber: r.sku,
          supplierArticleNo: off.supplierArticleNo ?? null,
          brand: r.brand,
          customerId: customerId ?? null,
        });

        /* ALREADY OURS. When a record exists the line names it directly and prices through the
           ordinary availability call, exactly as before — reuse is not creation, and it keeps a
           part we already stock behaving the same whichever row it was reached from. */
        if (q.productId) {
          const url = `/catalog/products/${q.productId}/availability${customerId ? `?customerId=${customerId}` : ''}`;
          const { data: av } = await apiClient.get<Availability>(url);
          const price = av.externalPrice ?? av.price;
          if (!price) {
            push({ tone: 'warning', message: t('counter.offerNoLongerAvailable', { sku: r.sku }) });
            return;
          }
          pushLine(
            {
              ...r,
              id: q.productId,
              source: 'CATALOG',
              leadDays: av.supplierLeadDays ?? r.leadDays,
              deliverySlots: av.supplierSlots ?? r.deliverySlots,
              promiseRule: av.supplierPromise ?? r.promiseRule,
            },
            price,
            'EXTERNAL',
            qty,
            av.cost ?? r.cost,
            undefined,
            customPriceHT
          );
          return;
        }

        if (!q.price) {
          push({ tone: 'warning', message: t('counter.offerNoLongerAvailable', { sku: r.sku }) });
          return;
        }
        // No record yet: the basket carries the OFFER. `r.id` (`sup:<code>:<number>`) is the
        // basket key only — it never reaches the server as a product.
        pushLine(
          {
            ...r,
            leadDays: q.leadTimeDays ?? r.leadDays,
            deliverySlots: q.deliverySlots ?? r.deliverySlots,
            promiseRule: q.promiseRule ?? r.promiseRule,
          },
          q.price,
          'EXTERNAL',
          qty,
          q.cost ?? r.cost,
          undefined,
          customPriceHT,
          {
            supplierCode: off.supplierCode,
            matchedNumber: r.sku,
            supplierArticleNo: off.supplierArticleNo ?? null,
            name: r.name,
            brand: r.brand ?? null,
          }
        );
      } catch (err) {
        showApiError(err);
      } finally {
        setCreatingKey(null);
      }
      return;
    }
    if (r.price) {
      pushLine(
        r,
        r.price,
        (r.internalStock ?? 0) >= 1 ? 'INTERNAL' : 'EXTERNAL',
        qty,
        undefined,
        undefined,
        customPriceHT
      );
      return;
    }
    try {
      const url = `/catalog/products/${r.id}/availability${customerId ? `?customerId=${customerId}` : ''}`;
      const { data: av } = await apiClient.get<Availability>(url);
      if (!av.price) {
        showApiError(new Error(t('counter.nonPositiveMargin', { name: r.name })));
        return;
      }
      pushLine(
        r,
        av.price,
        av.internalStock >= 1 ? 'INTERNAL' : 'EXTERNAL',
        qty,
        av.cost,
        undefined,
        customPriceHT
      );
    } catch (err) {
      showApiError(err);
    }
  };
  /* All three take the ORIGIN as well as the product: the basket can hold the same reference
     twice, once per origin, and a mutator keyed on productId alone would hit both — dragging the
     supplier sub-line's quantity or price along with our shelf one. */
  type Origin = 'INTERNAL' | 'EXTERNAL';
  const setQty = (productId: string, source: Origin, qty: number) =>
    setCart((cur) =>
      cur.map((x) => {
        if (x.productId !== productId || x.source !== source) return x;
        const wanted = Math.max(1, qty);
        // A shelf line stops at the shelf (CartLine.stockQty): the missing unit is a supplier
        // line the seller adds on purpose, never a silent switch.
        if (x.source === 'INTERNAL' && x.stockQty != null && wanted > x.stockQty) {
          notifyShelfCap(x.sku, x.stockQty);
          return { ...x, qty: Math.max(1, x.stockQty) };
        }
        return { ...x, qty: wanted };
      })
    );
  // Margin adjustment: set a line's final unit price (HT). null clears the override (back to grid).
  const setLinePrice = (productId: string, source: Origin, priceHT: number | null) =>
    setCart((cur) =>
      cur.map((x) =>
        x.productId === productId && x.source === source
          ? { ...x, customPriceHT: priceHT != null && priceHT > 0 ? round2(priceHT) : undefined }
          : x
      )
    );
  const removeLine = (productId: string, source: Origin) =>
    setCart((cur) => cur.filter((x) => !(x.productId === productId && x.source === source)));

  /**
   * How many DELIVERY NOTES this order will produce — one per origin present in the basket.
   *
   * Our shelf ships from the depot now; the supplier's units arrive on a later round and ship
   * then. Two origins is two shipments and therefore two BLs, and the seller has to be able to say
   * so before validating, not discover it when the customer asks why one part is missing.
   */
  const deliveryNoteCount = useMemo(() => new Set(cart.map((l) => l.source)).size, [cart]);

  /**
   * The basket as the seller reads it: ONE entry per product, its origins nested beneath.
   *
   * The flat list underneath stays one entry per (product, origin) because that is what has to
   * reach the order — two lines, two unit prices. This only regroups them for display, preserving
   * first-added order so the list doesn't reshuffle as quantities change.
   */
  const cartByProduct = useMemo(() => {
    const groups = new Map<
      string,
      {
        productId: string;
        sku: string;
        name: string;
        brand: string | null;
        imageUrl?: string | null;
        brandImageUrl?: string | null;
        units: number;
        lines: CartLine[];
      }
    >();
    for (const l of cart) {
      const g = groups.get(l.productId) ?? {
        productId: l.productId,
        sku: l.sku,
        name: l.name,
        brand: l.brand,
        imageUrl: l.imageUrl,
        brandImageUrl: l.brandImageUrl,
        units: 0,
        lines: [],
      };
      g.units += l.qty;
      g.lines.push(l);
      groups.set(l.productId, g);
    }
    // Our shelf above the supplier, always — the same rule the result row's origins follow.
    for (const g of groups.values()) {
      g.lines.sort((a, b) => (a.source === 'INTERNAL' ? 0 : 1) - (b.source === 'INTERNAL' ? 0 : 1));
    }
    return [...groups.values()];
  }, [cart]);

  // WP-1 Step 5 — shipping rule (flat fee, free-over threshold, its own VAT rate) comes from
  // admin_settings so the cart shows the same shipping line the server will persist. Defaults
  // keep the cart usable if the lookup fails; the server total is always authoritative.
  const [salesConfig, setSalesConfig] = useState({
    shippingFlatRateHT: 0,
    shippingFreeThresholdHT: Number.MAX_SAFE_INTEGER,
    shippingVatRate: 20,
    /* Also the search row's "Tomorrow 13:00" arithmetic — see deliveryEta(). These defaults are
       only a bridge until the fetch lands; the server's values are the ones that get frozen. */
    cutoff: '14:20',
    deliveryTime: '13:00',
    holidays: [] as string[],
    /* Feedback 2.1 — each supplier's own cut-off, "before" hour and lead, for the header chips. */
    supplierCutoffs: [] as Array<PromiseRule & { code?: string }>,
    /* Guide E3 — the smallest deposit the till may take on goods that are not handed over in
       this visit. The server enforces it again at /pay; this is only what the screen offers. */
    minDepositPct: 30,
  });
  useEffect(() => {
    apiClient
      .get<typeof salesConfig>('/meta/sales-config')
      .then(({ data }) => setSalesConfig(data))
      .catch(() => {
        /* non-blocking: cart still totals goods, server recomputes on submit */
      });
  }, []);
  // One clock for the whole list, so every row's delivery date turns over together at the cut-off.
  const nowMs = useMinuteTick();
  const calendar = useMemo<DeliveryCalendar>(
    () => ({
      cutoff: salesConfig.cutoff,
      deliveryTime: salesConfig.deliveryTime,
      holidays: salesConfig.holidays,
    }),
    [salesConfig.cutoff, salesConfig.deliveryTime, salesConfig.holidays]
  );
  const countdown = cutoffCountdown(calendar.cutoff, nowMs);

  /**
   * TTC of the lines served from ONE origin.
   *
   * A mixed basket is paid in two moments, because it is delivered in two: our shelf leaves with
   * the customer now and is invoiced now, the supplier's half is invoiced when it is handed over
   * (SC-02 — one order, two shipments, two invoices). Asking the till for the whole basket would
   * take money for goods nobody has yet, against an invoice that does not exist.
   */
  const originTTC = useMemo(() => {
    // Per-line HT + VAT as the server rounds them (lineFigures), so the half the till collects
    // is the figure paymentQuote will accept — never a screen-side estimate.
    const sum = (src: 'INTERNAL' | 'EXTERNAL') =>
      round2(
        cart
          .filter((l) => l.source === src)
          .reduce((s, l) => {
            const f = lineFigures(l);
            return s + f.ht + f.vat + l.price.depositHT * l.qty;
          }, 0)
      );
    return { internal: sum('INTERNAL'), external: sum('EXTERNAL') };
  }, [cart]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let deposit = 0;
    let vat = 0;
    for (const l of cart) {
      // Rounded per line, as the server writes the order (lineFigures) — the total shown is the
      // total the order will carry, and it is what `expectedTotalTTC` states on validation.
      const f = lineFigures(l);
      subtotal += f.ht;
      deposit += l.price.depositHT * l.qty;
      vat += f.vat;
    }
    subtotal = round2(subtotal);
    // WP-1 Step 5 — shipping is its own line with its own VAT rate, rounded per line, and is
    // free above the threshold. Mirrors what the server will persist as a SHIPPING order_line,
    // so the cart total matches the placed order instead of jumping after "Place order".
    //
    // NOT on a counter sale. `POST /pos/ticket` totals goods + VAT and nothing else, and it
    // REJECTS a payment that doesn't equal that total (400 AMOUNT_MISMATCH) — so a caisse cart
    // that added delivery showed 32.16 for a basket the server would only accept 14.16 for, and
    // the till could not be closed against it. Someone standing at the counter is not being
    // delivered to; the charge is wrong on its own terms, and disagreeing with the server about
    // the total is worse.
    const shipping =
      mode !== 'caisse' && cart.length > 0 && subtotal < salesConfig.shippingFreeThresholdHT
        ? round2(salesConfig.shippingFlatRateHT)
        : 0;
    // The server charges VAT on delivery at the CUSTOMER's rate (shippingVatRate reads the
    // exemption): a basket whose goods all carry 0 % is an exempt customer, so delivery is 0 % too.
    const shipVatRate =
      cart.length > 0 && cart.every((l) => lineFigures(l).vatRate === 0)
        ? 0
        : salesConfig.shippingVatRate;
    const shippingVat = round2(shipping * (shipVatRate / 100));
    return {
      subtotal,
      deposit: round2(deposit),
      shipping,
      shippingVat,
      freeShippingFrom: salesConfig.shippingFreeThresholdHT,
      ht: round2(subtotal + deposit + shipping),
      vat: round2(round2(vat) + shippingVat),
      ttc: round2(subtotal + deposit + shipping + round2(vat) + shippingVat),
    };
  }, [cart, salesConfig, mode]);
  const units = useMemo(() => cart.reduce((n, l) => n + l.qty, 0), [cart]);
  /** Any line served from a supplier's shelf — the basket is then an order, never a cash ticket. */
  const hasSupplierLine = useMemo(() => cart.some((l) => l.source === 'EXTERNAL'), [cart]);

  // -------- delivery --------
  /* Delivery mode is NOT a choice on this screen any more.
     Whether an order can ship in parts follows from where its lines come from: units off our own
     shelf leave today, units on a supplier round leave later, and no operator decision changes
     that. Letting a human pick produced promises the warehouse could not keep — COMPLETE on an
     order that physically arrives in two deliveries. The server derives it from the origins it
     actually reserved (OrderService), which is the only place that knows.

     The "Order no. / customer reference" field went with it: it was never sent anywhere. */

  // -------- checkout (caisse) --------
  type PayMethod = 'CASH' | 'CARD' | 'CHECK' | 'TRANSFER' | 'VOUCHER';
  interface PayLine {
    method: PayMethod;
    received: number;
    applied: number;
    /** VOUCHER (returns v2, migration 150): the purchase voucher spent. */
    voucherNumber?: string;
  }
  const [payments, setPayments] = useState<PayLine[]>([]);
  // A purchase voucher typed or scanned at the till, checked against the server before use.
  const [voucherNo, setVoucherNo] = useState('');
  const [voucher, setVoucher] = useState<{
    number: string;
    balance: number;
    error?: string;
  } | null>(null);
  const checkVoucher = async () => {
    const n = voucherNo.trim();
    if (!n) return;
    try {
      const { data } = await apiClient.get<{
        voucherNumber: string;
        balance: number;
        expired: boolean;
        validUntil: string;
      }>(`/vouchers/${encodeURIComponent(n)}`);
      if (data.expired || data.balance <= 0) {
        setVoucher({
          number: data.voucherNumber,
          balance: 0,
          error: data.expired
            ? t('counter.voucher.expired', { date: data.validUntil })
            : t('counter.voucher.empty'),
        });
        return;
      }
      // What is already tendered on this voucher in this basket is not spendable twice.
      const used = payments
        .filter((p) => p.voucherNumber === data.voucherNumber)
        .reduce((x, p) => x + p.applied, 0);
      const left = round2(data.balance - used);
      setVoucher({ number: data.voucherNumber, balance: left });
      setPayAmount(Math.min(left, Math.max(0, reste)).toFixed(2));
    } catch {
      setVoucher({ number: n, balance: 0, error: t('counter.voucher.notFound') });
    }
  };
  const [curMethod, setCurMethod] = useState<PayMethod | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [doneTicket, setDoneTicket] = useState<{
    invoiceNumber: string;
    ttc: number;
    received: number;
    change: number;
    orderId: string;
    orderNumber: string;
    /** B2B sale charged to the customer's account: nothing was collected at the till. */
    onAccount?: boolean;
    /** The hand-over minted an invoice (a comptant B2B account); else the monthly recap bills it. */
    invoiced?: boolean;
    /** Cash customer: the settlement receipt printed at the till (migration 111). */
    receipt?: { id: string; receiptNumber: string } | null;
    /** The sale's invoice — kept, handed over only on request. */
    invoice?: { id: string; invoiceNumber: string } | null;
    /** Mixed basket: what was paid for the supplier part, invoiced as a deposit (DEP). Kept in
     *  the background for a particulier (2026-10-08) and printed only on request. */
    depositInvoice?: { id: string; invoiceNumber: string } | null;
    /** The BL of this hand-over — THE document of a customer on account. */
    deliveryNote?: { id: string; deliveryNoteNumber: string } | null;
    /** Mixed sale: what stayed behind at the supplier, and when it lands. */
    awaitingSupplier?: number;
    awaitingEta?: string | null;
    awaitingTTC?: number;
    /** E7/E8 — the order's four digits; what the customer gives when they come to collect. */
    pickupCode?: string | null;
  } | null>(null);

  /** The sale's in-stock lines as the server sees them: EAN, location, picked so far. */
  const [prepLines, setPrepLines] = useState<
    Array<{
      id: string;
      productSku: string | null;
      productName: string | null;
      productEan: string | null;
      location: string | null;
      quantity: number;
      quantityShort: number;
      quantityPicked: number;
      status: string;
    }>
  >([]);
  // Followed while the panel is open: the warehouse's scans arrive on the order, not here.
  useEffect(() => {
    if (!prep) {
      setPrepLines([]);
      return;
    }
    let stop = false;
    const load = async () => {
      try {
        const { data } = await apiClient.get<{
          lines?: Array<{
            id: string;
            productId: string | null;
            source: string;
            lineType?: string;
            productSku: string | null;
            productName: string | null;
            productEan: string | null;
            location: string | null;
            quantity: number;
            quantityShort?: number;
            quantityPicked?: number;
            status: string;
          }>;
        }>(`/orders/${prep.orderId}`);
        if (stop) return;
        setPrepLines(
          (data.lines ?? [])
            .filter(
              (l) =>
                l.productId &&
                l.source === 'INTERNAL' &&
                l.lineType !== 'SHIPPING' &&
                l.status !== 'CANCELLED' &&
                l.status !== 'NOT_FOUND'
            )
            .map((l) => ({
              id: l.id,
              productSku: l.productSku,
              productName: l.productName,
              productEan: l.productEan,
              location: l.location,
              quantity: Number(l.quantity),
              quantityShort: Number(l.quantityShort ?? 0),
              quantityPicked: Number(l.quantityPicked ?? 0),
              status: l.status,
            }))
        );
      } catch {
        /* a missed refresh is retried on the next tick */
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), 3000);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, [prep?.orderId, prepTick]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Counter flow option 3 (owner, 2026-10-22): the seller may take the part off the shelf
   * themself. Each scan is written at once (POST /orders/:id/till-scan, the same locked pick as
   * the warehouse's), so a unit the preparer already scanned is refused here and vice versa —
   * whoever scans first wins, and nobody takes a second unit off the shelf for the same line.
   */
  tillScanRef.current = async (raw: string) => {
    const code = raw.trim();
    const current = prepRef.current;
    if (!code || !current) return;
    try {
      await apiClient.post(
        `/orders/${current.orderId}/till-scan`,
        { code, qty: 1 },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      beep.success();
      setPrepTick((n) => n + 1);
    } catch (err) {
      const errCode = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (errCode === 'SCAN_NOTHING_LEFT') {
        // Someone (the warehouse, or this till a moment ago) already took that unit.
        beep.error();
        push({ tone: 'warning', message: t('counter.prep.alreadyScanned', { code }) });
        setPrepTick((n) => n + 1);
        return;
      }
      beep.wrongProduct();
      if (errCode === 'SCAN_NOT_IN_ORDER') {
        push({ tone: 'warning', message: t('counter.prep.notInSale', { code }) });
        return;
      }
      showApiError(err);
    }
  };
  const prepReady =
    prepLines.length > 0 &&
    prepLines.every((l) => l.status === 'PICKED' || l.status === 'DELIVERED');

  /**
   * When the supplier half of this basket lands — the soonest of the lines still to come.
   * Quoted from the same calendar the result rows used, so the date the customer is told at the
   * till is the one they were shown while choosing.
   */
  const supplierEtaLabel = useMemo(() => {
    const etas = cart
      .filter((l) => l.source === 'EXTERNAL')
      .map((l) => externalEta(l, l.qty, nowMs, calendar))
      .sort((a, b) => a.at - b.at);
    return etas[0]?.label ?? null;
  }, [cart, nowMs]);

  /* B2B customers buy on credit: the sale goes on their account (the server checks the credit
     limit at order creation and lets the goods leave unpaid), and they top the account up by
     wire transfer later. Nothing is tendered at the till, so no payment methods are offered.
     B2C and walk-in customers pay on the spot. */
  /* 2026-10-08 — a pro COMPTANT is not on account: he pays each part when he collects it, at the
     till, and gets the invoice of that hand-over. No deposit and nothing up front on what the
     supplier still owes ("any deposit from a business needs its own invoice"). The B2B portal
     is never a till. */
  /* 3.2 (2026-10-23) — a pro with a ZERO credit line pays like a retail customer: everything at
     order time, the supplier part in full (no deposit), whatever his terms. The server holds the
     supplier order until then (prepayment.ts) and refuses goods leaving unpaid. NULL is
     "unlimited", never zero. */
  const zeroCreditPro =
    !isB2b &&
    customer?.customer_type === 'B2B' &&
    customer?.credit_limit != null &&
    Number(customer.credit_limit) === 0;
  const proComptant =
    !isB2b &&
    !zeroCreditPro &&
    customer?.customer_type === 'B2B' &&
    customer?.payment_terms === 'COMPTANT';
  const onAccount = customer?.customer_type === 'B2B' && !proComptant && !zeroCreditPro;
  const [supplierPay, setSupplierPay] = useState<'FULL' | 'DEPOSIT'>('FULL');
  const minDepositPct = salesConfig.minDepositPct;
  // The partial upfront payment on the supplier part: a FIXED cash amount the employee types,
  // not a percentage (owner's rule 2026-09-18). The rest is the balance due at delivery.
  const [depositInput, setDepositInput] = useState('');
  // A different amount due invalidates the tenders already typed against the old one — including
  // when the BASKET changes under an open checkout (a stock-changed switch rewrites lines, and the
  // till must not collect the amount computed for the old one).
  useEffect(() => {
    setPayments([]);
    setCurMethod(null);
    setPayAmount('');
  }, [supplierPay, depositInput, cart]);
  const applied = round2(payments.reduce((s, p) => s + p.applied, 0));
  /* What the customer settles AT THE TILL. The whole basket when everything is on our shelf; on
     a mixed one, only the half that leaves with them — the rest is invoiced at its own delivery. */
  /* Guide E3 / INV-54 — the supplier half is paid now: in full, or on a deposit of at least
     `minDepositPct` (30 %) of it. The floor comes from the server (/meta/sales-config) and the
     server enforces it again at /pay (422 AMOUNT_BELOW_MINIMUM), so the till cannot take a
     part-funded order the warehouse would then hold for nothing. */
  const depositFloor = round2((originTTC.external * minDepositPct) / 100);
  const depositAmount = (() => {
    const typed = round2(parseFloat((depositInput || '').replace(',', '.')));
    const chosen = Number.isFinite(typed) && typed > 0 ? typed : depositFloor;
    return Math.min(Math.max(chosen, depositFloor), originTTC.external);
  })();
  const externalDue = proComptant
    ? 0
    : zeroCreditPro || supplierPay === 'FULL'
      ? originTTC.external
      : depositAmount;
  const dueNow = onAccount
    ? 0
    : hasSupplierLine
      ? round2(originTTC.internal + externalDue)
      : totals.ttc;
  const reste = round2(dueNow - applied);
  const change = round2(payments.reduce((s, p) => s + (p.received - p.applied), 0));

  // reset checkout when switching quote / mode / clearing
  useEffect(() => {
    setPayments([]);
    setCurMethod(null);
    setPayAmount('');
    setDoneTicket(null);
    clearCompleted(); // leaving a finished sale's panel: that basket is done
  }, [activeIdx, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  // Tenders typed for a cash customer must not follow the basket onto a B2B account.
  useEffect(() => {
    if (onAccount) {
      setPayments([]);
      setCurMethod(null);
      setPayAmount('');
    }
  }, [onAccount]);

  const addPayment = () => {
    if (!curMethod) return;
    const received = round2(parseFloat((payAmount || '').replace(',', '.')));
    if (isNaN(received) || received <= 0) return;
    const due = reste;
    if (curMethod === 'VOUCHER') {
      // A voucher gives no change: only what is due, never beyond its balance.
      if (!voucher || voucher.error || voucher.balance <= 0) return;
      const ap = round2(Math.min(received, due, voucher.balance));
      if (ap <= 0) return;
      setPayments((ps) => [
        ...ps,
        { method: 'VOUCHER', received: ap, applied: ap, voucherNumber: voucher.number },
      ]);
      setVoucher(null);
      setVoucherNo('');
      setCurMethod(null);
      setPayAmount('');
      return;
    }
    const ap = round2(Math.min(received, due));
    setPayments((ps) => [...ps, { method: curMethod, received, applied: ap }]);
    setCurMethod(null);
    setPayAmount('');
  };
  const removePayment = (i: number) => setPayments((ps) => ps.filter((_, j) => j !== i));

  // -------- submit --------
  const [busy, setBusy] = useState(false);
  /**
   * On-screen manager override. When the server refuses a line for exceeding the counter's
   * discount ceiling it answers 422 DISCOUNT_EXCEEDS_LIMIT with details.overridable — a manager
   * then authorises on the spot and we replay the same sale carrying the signed token.
   *
   * MARGIN_BELOW_COST is deliberately NOT offered here: details.overridable is false, because no
   * override authorises a below-cost sale. That one has to be rung by a manager/admin/owner.
   */
  const [overridePrompt, setOverridePrompt] = useState<{
    pct: number;
    retry: (t: string) => void;
  } | null>(null);
  const [overrideToken, setOverrideToken] = useState<string | null>(null);

  /** Returns true when the failure was an overridable cap breach and the prompt was opened. */
  const offerOverride = (err: unknown, retry: (token: string) => void): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const d = (err as any)?.response?.data;
    if (d?.code !== 'DISCOUNT_EXCEEDS_LIMIT' || d?.details?.overridable !== true) return false;
    setOverridePrompt({ pct: Number(d.details.requestedPct ?? 0), retry });
    return true;
  };

  /**
   * The promise, re-asked at the moment of confirmation.
   *
   * Everything above quotes against the clock at RENDER time, and that answer expires silently:
   * the seller reads "Tomorrow 13:00" at 14:18, talks the customer through it, and presses Confirm
   * at 14:22. The cut-off has gone, the real delivery is a day later, and the customer has been
   * told something untrue by a screen that never changed.
   *
   * So it is recomputed here and any drift is put in front of the seller BEFORE the order goes
   * out. Not a toast afterwards — by then the promise has been made out loud.
   */
  /**
   * Print / email, offered at the moment the document comes into existence.
   *
   * Validating is exactly when the counter decides what the customer walks out with — a printed
   * copy, an email, or both — and hunting for the document afterwards to print it is the step
   * everyone forgets. `then` runs once the dialog is dismissed, so the screen does not navigate
   * away from under the operator while they are still choosing.
   */
  const [printPrompt, setPrintPrompt] = useState<{
    docType: 'QUOTE' | 'INVOICE' | 'ORDER' | 'DELIVERY_NOTE_CLIENT';
    documentId: string;
    documentNumber: string;
    /** Offers every document of that order in the dialog's Template list. */
    orderId?: string;
    /**
     * Opened by the end of a sale (feedback 2.5): it stays open across sends/prints and offers
     * "Open the order"; closing it leaves the seller on the till.
     */
    afterSale?: boolean;
    then: () => void;
  } | null>(null);
  /** Feedback 2.5 — every sale ends on its documents: all templates, all destinations. */
  const openSaleDocuments = (
    orderId: string,
    orderNumber: string,
    doc?: {
      docType: 'INVOICE' | 'DELIVERY_NOTE_CLIENT';
      documentId: string;
      documentNumber: string;
    }
  ) =>
    // Opened on the ORDER it starts on what this customer is handed first (ticket for a
    // particulier, BL for a pro); a named document starts on that one instead.
    setPrintPrompt({
      docType: doc?.docType ?? 'ORDER',
      documentId: doc?.documentId ?? orderId,
      documentNumber: doc?.documentNumber ?? orderNumber,
      orderId,
      afterSale: true,
      then: () => {},
    });
  // A done panel or a document prompt means the active basket became a sale (or a saved quote).
  useEffect(() => {
    if (doneTicket || printPrompt || prep) setCompletedId(active.id);
  }, [doneTicket, printPrompt, prep]); // eslint-disable-line react-hooks/exhaustive-deps

  const [promiseShift, setPromiseShift] = useState<
    { sku: string; was: string; now: string }[] | null
  >(null);

  /** The basket's command id (see postOrder): renewed when the basket or the customer changes. */
  const basketCommandId = useRef<string>(crypto.randomUUID());
  useEffect(() => {
    basketCommandId.current = crypto.randomUUID();
  }, [cart, customer?.id]);

  /**
   * INV-47 — the in-stock units a line was composed with are gone by the time the order is
   * validated (sold meanwhile). The server stopped the order and created nothing; switching the
   * missing units to a supplier is a different price and date, so it goes back to the seller —
   * and through them to the customer — never silently.
   */
  const [stockChanged, setStockChanged] = useState<{
    line: CartLine;
    availableQty: number;
    av: Availability | null;
  } | null>(null);

  const handleStockChanged = async (err: unknown): Promise<boolean> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const d = (err as any)?.response?.data;
    if (d?.code !== 'STOCK_CHANGED' || !d.details?.productId) return false;
    const line = cart.find((l) => l.productId === d.details.productId && l.source === 'INTERNAL');
    if (!line) return false;
    let av: Availability | null = null;
    try {
      const url = `/catalog/products/${line.productId}/availability${customer ? `?customerId=${customer.id}` : ''}`;
      av = (await apiClient.get<Availability>(url)).data;
    } catch {
      /* no live supplier offer — only "keep what is in stock" is offered */
    }
    setStockChanged({ line, availableQty: Math.max(0, Number(d.details.availableQty ?? 0)), av });
    return true;
  };

  const keepInStockOnly = (line: CartLine, availableQty: number) =>
    setCart((cur) =>
      availableQty > 0
        ? cur.map((x) =>
            x.productId === line.productId && x.source === 'INTERNAL'
              ? { ...x, qty: availableQty, stockQty: availableQty }
              : x
          )
        : cur.filter((x) => !(x.productId === line.productId && x.source === 'INTERNAL'))
    );

  /**
   * 409 PRICE_CHANGED — the server priced the order differently from the screen (a supplier
   * re-quote that moved, a timed-out supplier falling back to the shelf price, a cent of VAT
   * rounding) and created NOTHING. The basket takes the server's prices so the next validation
   * states the same total, the tenders are cleared (they were typed against the old one), and
   * the seller is shown what moved to re-check it with the customer before validating again.
   */
  const [priceShift, setPriceShift] = useState<{
    rows: { sku: string; name: string; wasHT: number; nowHT: number }[];
    wasTTC: number;
    nowTTC: number;
  } | null>(null);
  const handlePriceChanged = (err: unknown): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const d = (err as any)?.response?.data;
    if (d?.code !== 'PRICE_CHANGED' || !Array.isArray(d.details?.lines)) return false;
    const serverLines = d.details.lines as Array<{
      inputIndex: number;
      source: 'INTERNAL' | 'EXTERNAL';
      unitPriceHT: number;
      discountPct: number;
      vatRate: number;
    }>;
    const rows: { sku: string; name: string; wasHT: number; nowHT: number }[] = [];
    const next = cart.map((l, i) => {
      // The basket sends one line per origin, so one input line is one server line.
      const sl = serverLines.find((x) => x.inputIndex === i && x.source === l.source);
      // A seller's override was honoured as sent: nothing to take from the server for it.
      if (!sl || l.customPriceHT != null) return l;
      const finalHT = round2(sl.unitPriceHT * (1 - sl.discountPct / 100));
      if (Math.abs(finalHT - l.price.finalUnitPriceHT) > 0.005) {
        rows.push({ sku: l.sku, name: l.name, wasHT: l.price.finalUnitPriceHT, nowHT: finalHT });
      }
      return {
        ...l,
        price: {
          ...l.price,
          unitPriceHT: sl.unitPriceHT,
          discountPct: sl.discountPct,
          finalUnitPriceHT: finalHT,
          vatRate: sl.vatRate,
          unitPriceTTC: round2(finalHT * (1 + sl.vatRate / 100)),
        },
      };
    });
    setCart(() => next);
    setPriceShift({
      rows,
      wasTTC: Number(d.details.expectedTotalTTC ?? totals.ttc),
      nowTTC: Number(d.details.totalTTC ?? 0),
    });
    beep.error();
    return true;
  };

  const promiseDrift = (): { sku: string; was: string; now: string }[] =>
    cart.flatMap((l) => {
      if (l.promisedAt == null) return [];
      const fresh =
        l.source === 'INTERNAL'
          ? deliveryEta(0, calendar, Date.now())
          : externalEta(l, l.qty, Date.now(), calendar);
      if (fresh.at === l.promisedAt) return [];
      return [{ sku: l.sku, was: l.promisedLabel ?? '', now: fresh.label }];
    });

  /* Feedback 3.13 — the server refuses an order identical to one this customer (or, for a
     walk-in, this seller) placed in the last minutes: 409 DUPLICATE_ORDER. The seller is shown
     which order it duplicates and decides; only an explicit "yes, a second sale" re-posts with
     confirmDuplicate. Both till paths go through here. */
  const postOrder = async <T,>(body: Record<string, unknown>): Promise<{ data: T }> => {
    /* ONE CART, ONE ORDER (owner, 2026-10-30). The command id is the basket's, not the click's:
       the server replays a successful POST /orders for the same id, so a seller who clicks again
       after a lost answer gets the SAME order back instead of a second one. A refused attempt
       (409 price / stock / supplier, 422) is not cached, so the next click is a fresh try. The id
       is renewed whenever the basket or the customer changes (effect below). */
    const headers = () => ({ headers: { 'X-Command-Id': basketCommandId.current } });
    try {
      return await apiClient.post<T>('/orders', body, headers());
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const r = (err as any)?.response;
      if (r?.status === 409 && r?.data?.code === 'DUPLICATE_ORDER' && !body.confirmDuplicate) {
        const d = (r.data.details ?? {}) as {
          orderNumber?: string;
          status?: string;
          paid?: number;
          minutesAgo?: number;
        };
        const again = window.confirm(
          t('counter.duplicateOrderConfirm', {
            number: d.orderNumber ?? '?',
            minutes: d.minutesAgo ?? 0,
            status: enumLabel('orderStatus', d.status ?? ''),
            paid: (d.paid ?? 0) > 0 ? t('counter.duplicatePaid') : t('counter.duplicateUnpaid'),
          })
        );
        if (again)
          return apiClient.post<T>('/orders', { ...body, confirmDuplicate: true }, headers());
      }
      throw err;
    }
  };

  const placeOrder = async (tokenOverride?: string, promiseAcknowledged = false) => {
    if (!customer || cart.length === 0) return;
    if (!promiseAcknowledged) {
      const drift = promiseDrift();
      if (drift.length > 0) {
        setPromiseShift(drift);
        return;
      }
    }
    setBusy(true);
    const token = tokenOverride ?? overrideToken;
    try {
      const { data } = await postOrder<{ id: string; orderNumber: string }>({
        customerId: customer.id,
        lines: cart.map(lineForServer),
        ...(licensePlate ? { licensePlate } : {}),
        ...(token ? { discountOverrideToken: token } : {}),
        // The total on screen: a server total that differs is refused (409 PRICE_CHANGED) and
        // re-priced here — never an order priced differently from what the seller read out.
        // Less the consigne: the server bills no deposit line today (cores are not charged), so
        // stating it would refuse every basket holding a returnable core.
        expectedTotalTTC: round2(totals.ttc - totals.deposit),
      });
      /* 3.13 — say what happens next, here, where it is decided: "Enregistrée" alone sent one
         seller back to place the same order four more times. An order placed from this screen is
         CREATED, not validated: its supplier parts are reserved at the supplier when it is
         validated from its order page (2026-10-30), not here and not at the deposit. */
      const hasSupplier = cart.some((l) => l.source === 'EXTERNAL');
      push({
        tone: hasSupplier ? 'warning' : 'success',
        message: hasSupplier
          ? t('counter.orderPlacedToValidate', { number: data.orderNumber })
          : t('counter.orderPlaced', { number: data.orderNumber }),
      });
      // A confirmed order now HAS a document — offer it here, where the customer is still in
      // front of the seller asking for something in writing.
      openSaleDocuments(data.id, data.orderNumber);
    } catch (err) {
      if (await handleStockChanged(err)) return;
      if (handlePriceChanged(err)) return;
      if (!offerOverride(err, (t) => void placeOrder(t, true))) showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  /**
   * A MIXED BASKET, SETTLED AT THE TILL — SC-02 day D in one press.
   *
   * The customer is buying one thing from two places, so this is one ORDER, not a cash ticket:
   * `POST /pos/ticket` reserves and delivers every line off our own shelf and has no way to
   * express "this half is coming from a supplier on Thursday". So the order is placed, confirmed
   * (which fires the supplier order for the rest) and paid; the half we hold is prepared by the
   * warehouse and handed over from the preparation panel (`handOverPrepared`), which collects
   * that shipment's invoice. The supplier half is invoiced when IT is handed over.
   *
   * The cash still reaches the drawer: the register close totals `payment_ledger` by cashier and
   * session, and an order payment is a ledger row like any other.
   */
  const checkoutMixed = async (tokenOverride?: string) => {
    const ovr = (typeof tokenOverride === 'string' ? tokenOverride : null) ?? overrideToken;
    const { data: order } = await postOrder<{
      id: string;
      orderNumber: string;
      // E7 — "The 4-digit pickup code is created with the order and sent to the consumer in the
      // confirmation and in the 'ready' notification." What stays behind is collected with it.
      pickupCode?: string | null;
    }>({
      customerId: customer!.id,
      lines: cart.map(lineForServer),
      ...(licensePlate ? { licensePlate } : {}),
      ...(ovr ? { discountOverrideToken: ovr } : {}),
      // The customer is at the till: no shipping line (the screen shows none either).
      counterSale: true,
      /* The total the tenders were typed against. The server prices the order itself (a
         supplier line from a live re-quote), and on 2026-10-26 its figure differed from the
         screen's: the order was created and confirmed, then `/pay` refused the screen's amount —
         five times. Now a differing total is refused BEFORE anything is written (409
         PRICE_CHANGED → handlePriceChanged re-prices the basket), so the order that exists is
         always the one that was collected for. Less the consigne, which the server does not
         bill (see placeOrder). */
      expectedTotalTTC: round2(totals.ttc - totals.deposit),
    });

    /* CONFIRM, then MONEY, then the goods.
       Confirm is the stock check, and money is only taken on a confirmed order (the server refuses
       it before: ORDER_NOT_PAYABLE). An on-account customer's confirmation is what releases the
       supplier order — the credit check stands in for a payment. For a cash customer the supplier
       order goes out once the payment below covers the deposit (the server checks, prepayment.ts).
       Paying before the hand-over is Invariant #11: `deliver` refuses a shipment a cash customer
       has not covered, and paying first fails safe — a paid order still here can be finished by a
       human, goods handed over unpaid cannot. */
    // A sale with in-stock parts is always confirmed here: confirmation is what puts it in front
    // of the warehouse (Prise de poste), which prepares it while the customer waits (2.3).
    if (onAccount || proComptant || payments.length > 0 || originTTC.internal > 0) {
      try {
        await apiClient.post(
          `/orders/${order.id}/confirm`,
          {},
          { headers: { 'X-Command-Id': crypto.randomUUID() } }
        );
      } catch (err) {
        /* 2026-10-30 — validation IS the supplier's reservation. Refused (the supplier reserved
           short, refused, or did not answer): the order exists, CREATED and blocked — no payment,
           no hand-over, no override. The basket is done: the sale is on its order, the buyer is
           alerted, a manager cancels it, and the seller places the basket again if the sale
           stands. Any other refusal of the confirm is shown as before. */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const code = (err as any)?.response?.data?.code;
        if (code === 'SUPPLIER_BLOCKED') {
          beep.error();
          showApiError(err);
          push({
            tone: 'error',
            title: t('counter.supplierBlocked.title', { number: order.orderNumber }),
            message: t('counter.supplierBlocked.message'),
            action: {
              label: t('counter.prep.openOrder'),
              onClick: () => navigate(`/orders/${order.id}`),
            },
          });
          newSale();
          navigate(`/orders/${order.id}`);
          return;
        }
        throw err;
      }
    }
    let depositInvoice: { id: string; invoiceNumber: string } | null = null;
    let depositTicket: { id: string; receiptNumber: string } | null = null;
    if (!onAccount && payments.length > 0) {
      let paid: {
        depositInvoice?: { id: string; invoiceNumber: string } | null;
        depositTicket?: { id: string; receiptNumber: string } | null;
      };
      try {
        paid = (
          await apiClient.post<typeof paid>(
            `/orders/${order.id}/pay`,
            {
              payments: payments.map((p) => ({
                method: p.method,
                amount: p.applied,
                ...(p.voucherNumber ? { voucherNumber: p.voucherNumber } : {}),
              })),
              // Through the cashier's open register session (drawer counters, POS Tickets list).
              atRegister: true,
              // Goods leave right after: the hand-over invoices this money (and turns what is held
              // for the supplier part into a deposit invoice). An all-supplier basket hands nothing
              // over now, so the payment is invoiced as a deposit (DEP) straight away.
              settleOnHandover: originTTC.internal > 0,
            },
            { headers: { 'X-Command-Id': crypto.randomUUID() } }
          )
        ).data;
      } catch (err) {
        /* THE ORDER EXISTS AND IS CONFIRMED; only the money was refused. Leaving the basket on
           screen with an error toast is what sent a seller back to validate the same basket four
           more times on 2026-10-26: each attempt was a new confirmed, unpaid order. The basket is
           done — the sale is now on its order, which is where the money is collected (the order
           page takes a payment) and where a manager cancels it if it should not exist (a seller
           holds no ORDER_CANCEL). Nothing here is re-submittable. */
        beep.error();
        showApiError(err);
        push({
          tone: 'warning',
          title: t('counter.payFailed.title', { number: order.orderNumber }),
          message: t('counter.payFailed.message'),
          action: {
            label: t('counter.prep.openOrder'),
            onClick: () => navigate(`/orders/${order.id}`),
          },
        });
        newSale();
        navigate(`/orders/${order.id}`);
        return;
      }
      depositInvoice = paid?.depositInvoice ?? null;
      depositTicket = paid?.depositTicket ?? null;
    }

    // Nothing of this basket is on our shelf: there is no shipment to hand over and nothing to
    // collect. The order is placed and the customer is told when it lands — that IS the sale.
    if (originTTC.internal <= 0) {
      push({ tone: 'success', message: t('counter.orderPlaced', { number: order.orderNumber }) });
      // E7/E8 — nothing leaves today; the whole basket is collected on the order's four digits.
      if (order.pickupCode) {
        push({ tone: 'info', message: t('counter.pickupCode', { code: order.pickupCode }) });
      }
      // 2026-10-08 — one visit, one document. A particulier who paid a deposit takes his TICKET
      // (it carries the pickup code and every part to come); the DEP stays in the background,
      // printable on request from the order. Unpaid (or a pro, who pays at pickup) → the
      // acknowledgement.
      if (depositTicket) {
        // The done panel's "Print receipt" opens it — from a click, so no popup blocker.
        setDoneTicket({
          invoiceNumber: order.orderNumber,
          ttc: dueNow,
          received: round2(applied + change),
          change: round2(change),
          orderId: order.id,
          orderNumber: order.orderNumber,
          onAccount: false,
          receipt: depositTicket,
          depositInvoice,
          awaitingSupplier: cart.length,
          awaitingEta: supplierEtaLabel,
          awaitingTTC: originTTC.external,
          pickupCode: order.pickupCode ?? null,
        });
        beep.success();
        openSaleDocuments(order.id, order.orderNumber);
        return;
      }
      openSaleDocuments(
        order.id,
        order.orderNumber,
        depositInvoice
          ? {
              docType: 'INVOICE',
              documentId: depositInvoice.id,
              documentNumber: depositInvoice.invoiceNumber,
            }
          : undefined
      );
      return;
    }

    /* WHOEVER SCANS FIRST (counter flow option 3, owner 2026-10-22). The order is confirmed and
       paid; it now sits in Prise de poste as a counter sale with this seller's name. Each unit is
       scanned off the shelf by the seller in the panel below or by a preparer on the Picking
       screen — every scan is written at once, so the second scan of a unit is refused (INV-41:
       those scans are the ones the hand-over relies on). The panel shows each part with its EAN
       and location, and hands the sale over once everything is scanned. */
    setPrep({
      orderId: order.id,
      orderNumber: order.orderNumber,
      pickupCode: order.pickupCode ?? null,
      ttc: dueNow,
      received: round2(applied + change),
      change: round2(change),
      onAccount,
      awaitingEta: supplierEtaLabel,
      awaitingTTC: originTTC.external,
    });
  };

  /**
   * The parts are picked: hand them over. MONEY FIRST, then the goods — Invariant #11, and the
   * right way round anyway: `deliver` refuses to hand over a shipment a cash customer has not
   * covered, and the payment was taken when the sale was confirmed. No scans are sent: every
   * unit was already scanned, at the till or by the warehouse (an empty list means "hand over
   * what is picked").
   */
  const handOverPrepared = async () => {
    if (!prep) return;
    setBusy(true);
    try {
      const { data: handed } = await apiClient.post<{
        invoice: { id: string; invoiceNumber: string; totalTTC: number } | null;
        deliveryNote: { id: string; deliveryNoteNumber: string } | null;
        handedOver: number;
        awaitingSupplier: number;
        receipt: { id: string; receiptNumber: string; kind: string } | null;
        depositInvoice: { id: string; invoiceNumber: string } | null;
      }>(
        `/orders/${prep.orderId}/handover`,
        { scans: [] },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      const done = prep;
      setPrep(null);
      setDoneTicket({
        invoiceNumber: handed.invoice?.invoiceNumber ?? done.orderNumber,
        ttc: done.ttc,
        received: done.received,
        change: done.change,
        orderId: done.orderId,
        orderNumber: done.orderNumber,
        onAccount: done.onAccount,
        invoiced: !!handed.invoice,
        receipt: handed.receipt,
        invoice: handed.invoice,
        depositInvoice: handed.depositInvoice,
        deliveryNote: handed.deliveryNote,
        // What the customer is NOT walking out with, so the seller says it out loud.
        awaitingSupplier: handed.awaitingSupplier,
        awaitingEta: done.awaitingEta,
        awaitingTTC: done.awaitingTTC,
        // …and what they will be asked for when they come back for it (E8: identification first).
        pickupCode: done.pickupCode,
      });
      beep.success();
      // 2026-10-08 — one hand-over, one document, chosen by client type:
      //   particulier      → the ticket (the INV and any DEP only on request);
      //   pro comptant     → the invoice;
      //   pro on account   → the priced BL (the monthly recap is the fiscal document).
      // The documents pop-up opens on its own (feedback 2.5) on the one this customer is handed;
      // the particulier's ticket leads the list when the dialog is opened on the order.
      openSaleDocuments(
        done.orderId,
        done.orderNumber,
        handed.receipt
          ? undefined
          : handed.invoice
            ? {
                docType: 'INVOICE',
                documentId: handed.invoice.id,
                documentNumber: handed.invoice.invoiceNumber,
              }
            : handed.deliveryNote
              ? {
                  docType: 'DELIVERY_NOTE_CLIENT',
                  documentId: handed.deliveryNote.id,
                  documentNumber: handed.deliveryNote.deliveryNoteNumber,
                }
              : undefined
      );
    } catch (err) {
      beep.error();
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  /** Leave the till free for the next customer; the sale stays in preparation, on its order. */
  const leavePrep = () => {
    if (!prep) return;
    const { orderId, orderNumber } = prep;
    setPrep(null);
    newSale();
    push({
      tone: 'info',
      message: t('counter.prep.leftInPrep', { number: orderNumber }),
      action: { label: t('counter.prep.openOrder'), onClick: () => navigate(`/orders/${orderId}`) },
    });
  };

  /**
   * The till's one submit path — the SAME order code as any other order (owner's rule,
   * 2026-09-18): create → confirm → pay at the register → hand over. The separate
   * POST /pos/ticket path is no longer used by this screen.
   */
  const confirmCheckout = async (tokenOverride?: string) => {
    if (!customer || cart.length === 0 || reste > 0.001) return;
    setBusy(true);
    try {
      // Money goes through an open register. Checked BEFORE the order exists, so a closed till
      // does not leave a confirmed, unpaid order behind.
      if (!onAccount && payments.length > 0) {
        const { data: open } = await apiClient.get<Array<{ user_id: string }>>(
          '/pos/sessions?status=OPEN'
        );
        if (!open.some((s) => s.user_id === user?.sub)) {
          push({
            tone: 'warning',
            title: t('errors.codes.NO_OPEN_POS_SESSION.title'),
            message: t('errors.codes.NO_OPEN_POS_SESSION.message'),
            action: { label: t('counter.openRegister'), onClick: () => navigate('/pos/close') },
          });
          return;
        }
      }
      await checkoutMixed(tokenOverride);
    } catch (err) {
      // The order POST is checkoutMixed's first step, so a stock change or a price change there
      // leaves nothing behind: no order, no payment.
      if (await handleStockChanged(err)) return;
      if (handlePriceChanged(err)) return;
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const saveQuote = async () => {
    if (!customer || cart.length === 0) return;
    setBusy(true);
    try {
      const body = {
        customerId: customer.id,
        lines: cart.map(lineForServer),
        ...(licensePlate ? { licensePlate } : {}),
      };
      const headers = { headers: { 'X-Command-Id': crypto.randomUUID() } };
      // PUT replaces the quote's lines wholesale — which is what "add/delete then confirm"
      // means, and what keeps an edited quote priced by exactly the same rules as a new one.
      const { data } = editingQuoteId
        ? await apiClient.put<{ id: string; quoteNumber: string }>(
            `/quotes/${editingQuoteId}`,
            body,
            headers
          )
        : await apiClient.post<{ id: string; quoteNumber: string }>('/quotes', body, headers);
      push({
        tone: 'success',
        message: editingQuoteId
          ? t('counter.quoteUpdated', { number: data.quoteNumber ?? '' })
          : t('counter.quoteSavedDraft', { number: data.quoteNumber }),
      });
      const quoteId = data.id ?? editingQuoteId!;
      setPrintPrompt({
        docType: 'QUOTE',
        documentId: quoteId,
        documentNumber: data.quoteNumber ?? 'quote',
        then: () => navigate(`/quotes/${quoteId}`),
      });
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  // start a fresh sale after a completed ticket (clears the active quote's cart and customer)
  const newSale = () => {
    patchActive({ cart: [], customer: null });
    setCompletedId(null);
    setDoneTicket(null);
    setPayments([]);
    setCurMethod(null);
  };

  // -------- credit (B2B) --------
  const credit = useMemo(() => {
    if (!customer || customer.customer_type !== 'B2B') return null;
    // null = no ceiling to show (unlimited, or a stub that hasn't loaded) — nothing to gauge.
    if (customer.credit_limit == null) return null;
    const limit = Number(customer.credit_limit) || 0;
    const balance = Number(customer.current_balance) || 0;
    const reserved = Number(customer.reserved_credit) || 0;
    const used = balance + reserved;
    // A 0 limit is now the meaningful state "no credit line" (migration 079), and it is the
    // ONE the seller most needs to see: every on-account order will be refused. Hiding the
    // widget on `limit <= 0`, as this did, hid exactly the case worth showing.
    if (limit === 0) return { name: customer.name, blocked: true as const };
    return {
      name: customer.name,
      blocked: false as const,
      used,
      limit,
      avail: limit - used,
      ratio: used / limit,
    };
  }, [customer]);

  /* "Save as quote" — offered on both the deferred-order and the pay-now screens, so a counter
     seller who is asked for a price in writing does not have to rebuild the basket elsewhere. */
  const quoteActions = (
    <>
      {!isB2b && canQuote && (
        <button
          className="btn btn--ghost btn--block"
          style={{ marginTop: 'var(--s-2)' }}
          disabled={busy || !customer || cart.length === 0 || customer.customer_type === 'WALK_IN'}
          onClick={saveQuote}
          title={
            customer?.customer_type === 'WALK_IN' ? t('counter.namedCustomerRequired') : undefined
          }
        >
          {editingQuoteId ? t('counter.updateQuote') : t('counter.saveAsQuote')}
        </button>
      )}
      {/* A disabled control has to say why, ON the screen.
        The reason lived only in a title attribute, so it appeared on hover and never
        to anyone who simply looked — the button read as broken, and was reported as
        broken. It is not: a quote is a document addressed to someone, and Walk-in is
        the screen's default state, so this is the case every counter hits first.
        Stating it inline, with the fix one click away, is the whole answer. */}
      {!isB2b && canQuote && customer?.customer_type === 'WALK_IN' && cart.length > 0 && (
        <p className="whynot">
          {t('counter.whyNoQuote')}{' '}
          <button type="button" onClick={() => setPickerOpen(true)}>
            {t('counter.chooseNamedCustomer')}
          </button>
        </p>
      )}
    </>
  );

  // -------- render --------
  return (
    /* `min-h-full`, NOT `h-full`.

       These routes render full-bleed (AppShell), so this is the ONLY child of the scrolling
       <main> — and `height: 100%` fixed its box at exactly one viewport while the results list
       overflowed it. A sticky child can only travel inside its containing block, so `.searchrow`
       pinned correctly and then got carried off the moment that 100%-tall box scrolled past: about
       one screenful in, after which its top tracked the scroll one-for-one. Measured before/after
       in Chromium at 1600x900 over 40 rows — pinned at 0/400px, gone by 1200px.

       The filter rail and cart panel were NOT affected and are not what this fixes: they are grid
       items of `.wrap`, whose row is sized by the tall results column, so their containing block
       already spanned the full list.

       As a minimum this still fills the viewport on a short result set, which is what `h-full` was
       really there for — but it now grows with the list. */
    <div className="mds is-comptoir flex min-h-full flex-col">
      {infoPanel && (
        <ArticleInfoDialog
          reference={infoPanel.reference}
          tab={infoPanel.tab}
          onTab={(t) => setInfoPanel((cur) => (cur ? { ...cur, tab: t } : cur))}
          onClose={() => setInfoPanel(null)}
        />
      )}
      {printPrompt && (
        <PrintDocumentDialog
          docType={printPrompt.docType}
          documentId={printPrompt.documentId}
          documentNumber={printPrompt.documentNumber}
          customerId={customer?.id ?? null}
          orderId={printPrompt.orderId ?? null}
          afterSale={printPrompt.afterSale}
          onOpenOrder={
            printPrompt.afterSale && printPrompt.orderId
              ? () => {
                  const id = printPrompt.orderId!;
                  setPrintPrompt(null);
                  newSale();
                  navigate(`/orders/${id}`);
                }
              : undefined
          }
          onClose={() => {
            const { then } = printPrompt;
            setPrintPrompt(null);
            // A reprint from the done panel leaves that panel (and its basket) in place.
            if (!doneTicket) clearCompleted();
            then();
          }}
        />
      )}
      {promiseShift && (
        <div
          className="mds-modal-bd"
          role="dialog"
          aria-modal="true"
          aria-label={t('counter.promiseChangedAria')}
        >
          <div className="mds-modal sm">
            <div className="pshift-h">
              <AlertTriangle size={18} />
              <div>
                <h3>{t('counter.promiseMoved')}</h3>
                <p>{t('counter.promiseMovedDetail', { cutoff: calendar.cutoff })}</p>
              </div>
            </div>
            <div className="pshift-b">
              {promiseShift.map((s) => (
                <div className="pshift-row" key={s.sku}>
                  <span className="sku">{s.sku}</span>
                  <span className="was">{s.was}</span>
                  <ArrowRight size={13} />
                  <span className="now">{s.now}</span>
                </div>
              ))}
            </div>
            <div className="pshift-f">
              <button className="btn btn--ghost" onClick={() => setPromiseShift(null)}>
                {t('counter.goBack')}
              </button>
              <button
                className="btn btn--primary"
                onClick={() => {
                  setPromiseShift(null);
                  void placeOrder(undefined, true);
                }}
              >
                {t('counter.confirmNewDate')}
              </button>
            </div>
          </div>
        </div>
      )}
      {priceShift && (
        <div
          className="mds-modal-bd"
          role="dialog"
          aria-modal="true"
          aria-label={t('counter.priceChanged.aria')}
        >
          <div className="mds-modal sm">
            <div className="pshift-h">
              <AlertTriangle size={18} />
              <div>
                <h3>{t('counter.priceChanged.title')}</h3>
                <p>
                  {t('counter.priceChanged.detail', {
                    was: eur(priceShift.wasTTC),
                    now: eur(priceShift.nowTTC),
                  })}
                </p>
              </div>
            </div>
            <div className="pshift-b">
              {priceShift.rows.length === 0 ? (
                <p className="text-muted-foreground">{t('counter.priceChanged.roundingOnly')}</p>
              ) : (
                priceShift.rows.map((r) => (
                  <div className="pshift-row" key={`${r.sku}-${r.wasHT}`}>
                    <span className="sku" title={r.name}>
                      {r.sku}
                    </span>
                    <span className="was">{eur(r.wasHT)} HT</span>
                    <ArrowRight size={13} />
                    <span className="now">{eur(r.nowHT)} HT</span>
                  </div>
                ))
              )}
            </div>
            <div className="pshift-f">
              <button className="btn btn--primary" onClick={() => setPriceShift(null)}>
                {t('counter.priceChanged.recheck')}
              </button>
            </div>
          </div>
        </div>
      )}
      {prep && (
        <div
          className="mds-modal-bd"
          role="dialog"
          aria-modal="true"
          aria-label={t('counter.prep.title')}
        >
          <div className="mds-modal sm">
            <div className="pshift-h">
              <ScanLine size={18} />
              <div>
                <h3>{t('counter.prep.title')}</h3>
                <p>{t('counter.prep.detail', { number: prep.orderNumber })}</p>
              </div>
            </div>
            <div className="pshift-b">
              {prepLines.map((l) => {
                const due = l.quantity - l.quantityShort;
                const done = l.status === 'PICKED' || l.status === 'DELIVERED';
                return (
                  <div className="pshift-row" key={l.id} style={{ flexWrap: 'wrap' }}>
                    <span className="sku">{l.productSku}</span>
                    <span>{l.productName}</span>
                    <span className={done ? 'now' : ''}>
                      {done ? due : Math.min(l.quantityPicked, due)}/{due}
                    </span>
                    {/* What the seller (or the preparer they send) needs to find the part. */}
                    <span
                      style={{
                        flexBasis: '100%',
                        fontSize: 12,
                        color: 'var(--text-muted, inherit)',
                      }}
                    >
                      {t('counter.prep.ean')} :{' '}
                      <span className="mono">{l.productEan || t('counter.prep.notDefined')}</span>
                      {' · '}
                      {t('counter.prep.location')} :{' '}
                      <span className="mono">{l.location || t('counter.prep.notDefined')}</span>
                    </span>
                  </div>
                );
              })}
              {prepLines.length === 0 && <div className="pshift-row">{t('common.loading')}</div>}
            </div>
            <div className="pshift-f">
              <button className="btn btn--ghost" onClick={leavePrep} disabled={busy}>
                {t('counter.prep.leave')}
              </button>
              <button
                className="btn btn--ghost"
                disabled={busy || prepReady}
                onClick={() => {
                  // A barcode that will not read: type what is printed under it.
                  void (async () => {
                    const code = await promptForCode({
                      title: t('counter.prep.typeCodeTitle'),
                      description: t('counter.prep.typeCode'),
                      label: t('counter.prep.typeCodeLabel'),
                      placeholder: t('counter.prep.typeCodePlaceholder'),
                      hint: t('counter.prep.typeCodeHint'),
                      confirmLabel: t('counter.prep.typeCodeConfirm'),
                      // Short enough for a manufacturer reference, long enough to rule out a
                      // stray keystroke from the scanner wedge.
                      minLength: 3,
                      mono: true,
                      validate: (v) => (v.length < 3 ? t('counter.prep.typeCodeTooShort') : null),
                    });
                    if (code) void tillScanRef.current(code);
                  })();
                }}
              >
                {t('counter.prep.typeCodeButton')}
              </button>
              <button
                className="btn btn--primary"
                disabled={!prepReady || busy}
                onClick={() => void handOverPrepared()}
              >
                {prepReady ? t('counter.tillScan.handOver') : t('counter.prep.waiting')}
              </button>
            </div>
          </div>
          {codePromptDialog}
        </div>
      )}
      {stockChanged && (
        <div
          className="mds-modal-bd"
          role="dialog"
          aria-modal="true"
          aria-label={t('counter.stockChanged.title')}
        >
          <div className="mds-modal sm">
            <div className="pshift-h">
              <AlertTriangle size={18} />
              <div>
                <h3>{t('counter.stockChanged.title')}</h3>
                <p>
                  {t('counter.stockChanged.detail', {
                    sku: stockChanged.line.sku,
                    requested: stockChanged.line.qty,
                    available: stockChanged.availableQty,
                  })}
                </p>
              </div>
            </div>
            <div className="pshift-b">
              {/* 2026-10-30 (owner) — no "switch the rest to the supplier" here any more: a shelf
                  line never gets a supplier. The seller keeps what is on the shelf and, if the
                  customer wants the rest, adds it as a supplier line on purpose. */}
              <p>
                {stockChanged.av?.externalPrice && stockChanged.av.supplierAvailable
                  ? t('counter.stockChanged.supplierHint', {
                      missing: stockChanged.line.qty - stockChanged.availableQty,
                      price: eur(stockChanged.av.externalPrice.finalUnitPriceHT),
                      eta: promiseEta(stockChanged.av.supplierPromise, calendar, Date.now(), {
                        slots: stockChanged.av.supplierSlots,
                        qty: stockChanged.line.qty - stockChanged.availableQty,
                        leadDays: stockChanged.av.supplierLeadDays,
                      }).label,
                    })
                  : t('counter.stockChanged.noSupplier')}
              </p>
            </div>
            <div className="pshift-f">
              <button className="btn btn--ghost" onClick={() => setStockChanged(null)}>
                {t('counter.goBack')}
              </button>
              <button
                className="btn btn--ghost"
                onClick={() => {
                  keepInStockOnly(stockChanged.line, stockChanged.availableQty);
                  setStockChanged(null);
                  push({ tone: 'warning', message: t('counter.stockChanged.basketUpdated') });
                }}
              >
                {stockChanged.availableQty > 0
                  ? t('counter.stockChanged.keepInStock', { available: stockChanged.availableQty })
                  : t('counter.stockChanged.removeLine')}
              </button>
            </div>
          </div>
        </div>
      )}
      {overridePrompt && (
        <ManagerOverrideDialog
          requestedPct={overridePrompt.pct}
          onCancel={() => setOverridePrompt(null)}
          onApproved={(token) => {
            setOverrideToken(token);
            const { retry } = overridePrompt;
            setOverridePrompt(null);
            retry(token);
          }}
        />
      )}
      {/* Create a relation mid-sale. Same embedded form as the Relations list, so there is one
          definition of the customer sheet; the basket is untouched behind the overlay. */}
      {showCreateCustomer && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowCreateCustomer(false)}
            aria-hidden
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label={t('counter.newCustomerAria')}
            className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-y-auto rounded-lg border bg-background p-4 shadow-xl"
          >
            <CustomerCreate
              embedded
              onClose={() => setShowCreateCustomer(false)}
              onSuccess={onCustomerCreated}
            />
          </aside>
        </div>
      )}
      {/* multi-quote tab strip */}
      {!isB2b && (
        <div className="border-b" style={{ background: 'var(--mds-surface)', padding: '8px 12px' }}>
          <div className="txnswitch" ref={pickerRef}>
            <div className="carttabs" role="tablist" aria-label={t('counter.currentQuotes')}>
              {quotes.map((q, i) => {
                const on = i === activeIdx;
                const b2b = q.customer?.customer_type === 'B2B';
                const Icon = b2b ? Briefcase : User;
                // A walk-in reads "Nouveau client" over its type "Client de passage" — not the same
                // words twice (client, 2026-10-16).
                const nm =
                  q.customer?.customer_type === 'WALK_IN'
                    ? t('counter.walkInTitle')
                    : (q.customer?.name ?? t('counter.newQuote'));
                const closable = quotes.length > 1;
                if (on) {
                  return (
                    <button
                      key={q.id}
                      className={cn('txntab on', b2b && 'b2b')}
                      onClick={() => setPickerOpen((o) => !o)}
                      title={t('counter.changeCustomer')}
                    >
                      <span className="tt-ic">
                        <Icon size={16} />
                      </span>
                      <span className="tt-txt">
                        <span className="tt-nm">{nm}</span>
                        <span className="tt-meta">
                          {q.customer
                            ? enumLabel('customerType', q.customer.customer_type)
                            : t('counter.noCustomerSelected')}
                        </span>
                      </span>
                      <span className="tt-chev">▾</span>
                      {closable && (
                        <span
                          className="tt-x"
                          role="button"
                          aria-label={t('counter.closeThisQuote')}
                          onClick={(e) => {
                            e.stopPropagation();
                            closeQuoteTab(i);
                          }}
                        >
                          <X size={13} />
                        </span>
                      )}
                    </button>
                  );
                }
                return (
                  <button key={q.id} className="txntab" onClick={() => setActiveIdx(i)} title={nm}>
                    <span className="tt-ic">
                      <Icon size={14} />
                    </span>
                    <span className="tt-nm">{nm}</span>
                    {closable && (
                      <span
                        className="tt-x"
                        role="button"
                        aria-label={t('counter.closeThisQuote')}
                        onClick={(e) => {
                          e.stopPropagation();
                          closeQuoteTab(i);
                        }}
                      >
                        <X size={13} />
                      </span>
                    )}
                  </button>
                );
              })}
              <button
                className="txntab add"
                aria-label={t('counter.newQuote')}
                title={t('counter.newQuote')}
                onClick={addQuoteTab}
              >
                +
              </button>
            </div>

            {pickerOpen && (
              <div className="client-picker">
                <input
                  className="csearch"
                  autoFocus
                  placeholder={t('counter.customerSearch')}
                  value={customerQuery}
                  onChange={(e) => setCustomerQuery(e.target.value)}
                />
                <button className="copt" onClick={pickWalkIn}>
                  <span className="cn">
                    {t('counter.walkIn')} <span className="muted">{t('counter.walkInTag')}</span>
                  </span>
                  <span className="cm">{t('counter.walkInMeta')}</span>
                </button>
                {customerHits.map((c) => (
                  <button
                    key={c.id}
                    className="copt"
                    onClick={() => {
                      setCustomer(c);
                      setPickerOpen(false);
                      setCustomerQuery('');
                    }}
                  >
                    <span className="cn">
                      {c.name} <span className="muted">{c.siret ?? ''}</span>
                    </span>
                    <span className="cm">{enumLabel('customerType', c.customer_type)}</span>
                  </button>
                ))}
                {customerQuery.length >= 2 && customerHits.length === 0 && (
                  <div className="cm" style={{ padding: 8, color: 'var(--ink-500)' }}>
                    {t('counter.noMatchingCustomer')}
                  </div>
                )}
                {canCreateCustomer && (
                  <button className="copt new" onClick={openCreateCustomer}>
                    {t('counter.addCustomer')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* part search row */}
      <div className="searchrow">
        {/* The wrapper is what the history dropdown hangs off. It cannot hang off `.search`, which
            is `overflow:hidden` so the input's corners follow the rounded border — and would clip
            the panel with them. */}
        <div className="searchwrap">
          <div className="search" role="search">
            <span className="ic">
              <Search size={17} />
            </span>
            <input
              ref={searchRef}
              value={pq}
              onChange={(e) => {
                setPq(e.target.value);
                setHistoryOpen(true);
                setHistIdx(-1);
              }}
              onFocus={() => setHistoryOpen(true)}
              // Focus alone is not enough to reopen it: Escape closes the panel while leaving the
              // cursor exactly where it was, so the next click never crosses a focus boundary and
              // the box would stay mute until the seller clicked away and back.
              onClick={() => setHistoryOpen(true)}
              // Closing on blur rather than on an outside-click listener: every control INSIDE the
              // panel keeps focus in the input (onMouseDown preventDefault below), so a blur here
              // genuinely means the seller has gone elsewhere.
              onBlur={() => {
                setHistoryOpen(false);
                setHistIdx(-1);
              }}
              onKeyDown={(e) => {
                if (historyOpen && historyShown.length > 0) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setHistIdx((i) => (i + 1) % historyShown.length);
                    return;
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setHistIdx((i) => (i <= 0 ? historyShown.length - 1 : i - 1));
                    return;
                  }
                  if (e.key === 'Enter' && histIdx >= 0) {
                    e.preventDefault();
                    runSearchTerm(historyShown[histIdx]);
                    return;
                  }
                }
                if (e.key === 'Enter') {
                  setHistoryOpen(false);
                  void searchProduct(pq);
                }
              }}
              placeholder={t('counter.partSearch')}
              aria-label={t('counter.searchAria')}
              aria-expanded={historyOpen && historyShown.length > 0}
              aria-autocomplete="list"
              role="combobox"
            />
            <span className="scope">{t('counter.scopeAll')}</span>
            <button
              className="go"
              onClick={() => {
                setHistoryOpen(false);
                void searchProduct(pq);
              }}
            >
              <Search size={16} /> {t('common.search')}
            </button>
          </div>

          {/* RECENT SEARCHES. Opens on focus, narrows to what matches as the seller types, and
              disappears the moment there is nothing left to offer — an empty panel under the box
              is just a thing to dismiss. */}
          {historyOpen && historyShown.length > 0 && (
            <div className="searchhist" role="listbox" aria-label={t('counter.recentSearches')}>
              <div className="shhead">
                <span>{t('counter.recentSearches')}</span>
                <button
                  className="shclear"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => forgetSearch()}
                >
                  {t('counter.clearHistory')}
                </button>
              </div>
              {historyShown.map((h, i) => (
                <div
                  key={h}
                  className={cn('shrow', i === histIdx && 'is-active')}
                  role="option"
                  aria-selected={i === histIdx}
                >
                  {/* preventDefault on mousedown, not a click handler on the row: without it the
                      input blurs first, the panel unmounts, and the click lands on nothing. */}
                  <button
                    className="shpick"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => runSearchTerm(h)}
                  >
                    <History size={13} />
                    <span className="sht">{h}</span>
                  </button>
                  <button
                    className="shdel"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => forgetSearch(h)}
                    title={t('counter.forgetSearch')}
                    aria-label={t('counter.forgetSearch')}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Only way back to a dismissed strip, so it lives outside it. */}
        {!hotkeysShown && (
          <button
            className="hkshow"
            onClick={() => setHotkeysShown(true)}
            title={t('counter.showShortcuts')}
            aria-label={t('counter.showShortcuts')}
          >
            <Keyboard size={15} />
          </button>
        )}
      </div>

      {/* hotkey guide — mirrors the handler in the effect above */}
      {hotkeysShown && (
        <div className="hotkeybar" aria-label={t('counter.shortcuts')}>
          <span className="hk">
            <kbd>/</kbd> {t('counter.hkSearch')}
          </span>
          <span className="hk">
            <kbd>Esc</kbd> {t('counter.hkExit')}
          </span>
          {!isB2b && (
            <span className="hk">
              <kbd>Alt</kbd> <span className="plus">+</span> <kbd>1·2·3</kbd>{' '}
              {t('counter.hkSwitchQuote')}
            </span>
          )}
          <span className="hk">
            <kbd>Enter</kbd> {t('counter.hkRun')}
          </span>
          <button
            className="hkclose"
            onClick={() => setHotkeysShown(false)}
            title={t('counter.hideShortcuts')}
            aria-label={t('counter.hideShortcuts')}
          >
            <X size={13} />
          </button>
        </div>
      )}

      {/* 3-column body */}
      <div className={cn('wrap flex-1', filtersHidden && 'filters-off')}>
        {/* FILTERS */}
        <aside
          className={cn('col filters', filtersHidden && 'is-hidden')}
          aria-label={t('counter.filters')}
        >
          <div className="fhead">
            <h2>
              {t('counter.filters')}{' '}
              <span className="fcount">({activeFilterCount || rows.length})</span>
            </h2>
            <button className="fhide" onClick={() => setFiltersHidden((h) => !h)}>
              {filtersHidden ? t('counter.show') : t('counter.hide')}
              <ChevronDown size={12} className={cn(!filtersHidden && 'flip')} />
            </button>
          </div>

          {!filtersHidden && (
            <div className="fbody">
              {/* Every group below is built from the result set, so with no results the rail was
                  a find-a-filter box over a single Availability group of three zeroes. */}
              {rows.length === 0 && <p className="fnone">{t('counter.filtersAfterSearch')}</p>}
              {/* The box that finds a filter comes FIRST, unboxed. It is how you reach the rest of
                  the rail, not one of the things in it — sitting third, under two long lists, it
                  was below the fold exactly when the rail was long enough to need it. */}
              {rows.length > 0 && (
                <div className="ffind">
                  <Search size={13} />
                  <input
                    placeholder={t('counter.filterNamePlaceholder')}
                    aria-label={t('counter.findFilters')}
                    value={facetQuery}
                    onChange={(e) => setFacetQuery(e.target.value)}
                  />
                  {facetQuery && (
                    <button
                      className="fclearq"
                      onClick={() => setFacetQuery('')}
                      aria-label={t('counter.clearSearch')}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              )}

              {/* WHAT THE PART IS, before anything about this particular offer of it. Category then
                  sub-category then designation is the order a parts catalogue is organised in, and
                  it is the order a seller narrows: system, then assembly, then the part itself. */}
              {categoryFacets.some(
                (c) => facetMatches('category', c.name) || c.subs.some((s) => facetMatches(s.name))
              ) && (
                <FacetGroup
                  title={t('counter.category')}
                  folded={foldedGroups.has('category')}
                  onFold={() => toggleGroup('category')}
                  full={shownInFull.has('category')}
                  onFull={() => toggleFull('category')}
                  limit={8}
                >
                  {categoryFacets
                    .filter((c) => facetMatches(c.name) || c.subs.some((s) => facetMatches(s.name)))
                    .map((c) => (
                      <div className="fcat" key={c.id}>
                        {/* The disclosure sits BESIDE the label, not inside it. Two things are
                            being asked here — "filter by this category" and "what is under it" —
                            and they must stay separable: opening a branch to read its counts must
                            not filter by its parent, and ticking the parent must not force the
                            branch open. Nesting the button in the <label> would also make it a
                            control inside a control, which screen readers and the label's own
                            click-forwarding both handle badly. */}
                        <div className="fcat-row">
                          {c.subs.length > 0 ? (
                            <button
                              type="button"
                              className="fcatopen"
                              aria-expanded={openCategories.has(c.id)}
                              aria-label={t('counter.showSubcategories', { category: c.name })}
                              onClick={() => toggleCategoryOpen(c.id)}
                            >
                              <ChevronDown
                                size={12}
                                className={cn(openCategories.has(c.id) && 'flip')}
                              />
                            </button>
                          ) : (
                            // Keeps the chevron's width so leaf names line up with branch names.
                            <span className="fcatopen is-leaf" aria-hidden="true" />
                          )}
                          <label className="chk">
                            <input
                              type="checkbox"
                              checked={selectedCategories.has(c.id)}
                              onChange={() => toggleCategory(c.id)}
                            />
                            <span className="fname" title={c.name}>
                              {c.name}
                            </span>
                            <span className="cnt">({c.n})</span>
                          </label>
                        </div>
                        {openCategories.has(c.id) && (
                          <div className="fsubs">
                            {c.subs
                              .filter((s) => facetMatches(c.name, s.name))
                              .map((s) => (
                                <label className="chk is-sub" key={s.id}>
                                  <input
                                    type="checkbox"
                                    checked={selectedCategories.has(s.id)}
                                    onChange={() => toggleCategory(s.id)}
                                  />
                                  <span className="fname" title={s.name}>
                                    {s.name}
                                  </span>
                                  <span className="cnt">({s.n})</span>
                                </label>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                </FacetGroup>
              )}

              {/* The designation, one rung below the tree it belongs to. It used to be a loose chip
                  cloud floating above the panel; as a group it reads as what it is — the deepest
                  cut of the same "what is this part" question the two groups above ask. */}
              {typeFacets.length > 1 &&
                typeFacets.some(([name]) => facetMatches('part type', name)) && (
                  <FacetGroup
                    title={t('counter.partType')}
                    folded={foldedGroups.has('parttype')}
                    onFold={() => toggleGroup('parttype')}
                    full={shownInFull.has('parttype')}
                    onFull={() => toggleFull('parttype')}
                    limit={6}
                  >
                    {typeFacets
                      .filter(([name]) => facetMatches(name))
                      .map(([name, n]) => (
                        <label className="chk" key={name}>
                          <input
                            type="checkbox"
                            checked={selectedTypes.has(name)}
                            onChange={() => toggleType(name)}
                          />
                          <span className="fname" title={name}>
                            {name}
                          </span>
                          <span className="cnt">({n})</span>
                        </label>
                      ))}
                  </FacetGroup>
                )}

              {brandFacets.some((f) => facetMatches('manufacturer', f.brand)) && (
                <FacetGroup
                  title={t('counter.manufacturer')}
                  folded={foldedGroups.has('manufacturer')}
                  onFold={() => toggleGroup('manufacturer')}
                  /* Two orders, because the panel answers two questions: "is MY make here?"
                     (alphabetical) and "who actually covers this part?" (by count). */
                  sort={brandSort}
                  onSort={() => setBrandSort((s) => (s === 'alpha' ? 'count' : 'alpha'))}
                  full={shownInFull.has('manufacturer')}
                  onFull={() => toggleFull('manufacturer')}
                  limit={8}
                >
                  {brandFacets
                    .filter((f) => facetMatches(f.brand))
                    .map(({ brand, n, logo }) => (
                      <label className="chk" key={brand}>
                        <input
                          type="checkbox"
                          checked={selectedBrands.has(brand)}
                          onChange={() => toggleBrand(brand)}
                        />
                        {logo ? (
                          <ArticleImage
                            src={logo}
                            alt={brand}
                            size={18}
                            className="flogo"
                            fallback={null}
                          />
                        ) : (
                          <span className="badge brand flogo--none" style={brandStyle(brand)}>
                            {brand.slice(0, 2)}
                          </span>
                        )}
                        <span className="fname" title={brand}>
                          {brand}
                        </span>
                        <span className="cnt">({n})</span>
                      </label>
                    ))}
                </FacetGroup>
              )}

              {/* Everything above narrows WHICH PART. Everything below narrows THIS OFFER of it —
                  can I have it, and does it fit. Two different questions, so they are not one
                  undifferentiated stack of boxes any more. */}
              {rows.length > 0 &&
                facetMatches('availability', 'in stock', 'inbound', 'unavailable') && (
                  <FacetGroup
                    title={t('counter.availability')}
                    folded={foldedGroups.has('availability')}
                    onFold={() => toggleGroup('availability')}
                  >
                    {/* Same reason as the tabs above — nothing for it to select. */}
                    {(['ok', 'warn'] as const).map((k) => (
                      <label className="chk" key={k}>
                        <input
                          type="checkbox"
                          checked={availTab === k}
                          onChange={() => setAvailTab((t) => (t === k ? 'all' : k))}
                        />
                        <span className="fname">
                          {k === 'ok'
                            ? t('counter.inStockDepot')
                            : k === 'warn'
                              ? t('counter.inbound')
                              : t('counter.unavailable')}
                        </span>
                        <span className="cnt">({availCounts[k]})</span>
                      </label>
                    ))}
                  </FacetGroup>
                )}

              {specFacets
                .filter((g) => facetMatches(g.title, ...g.values.map(([v]) => v)))
                .map((g) => (
                  <FacetGroup
                    key={g.label}
                    title={g.title}
                    folded={foldedGroups.has(g.label)}
                    onFold={() => toggleGroup(g.label)}
                    full={shownInFull.has(g.label)}
                    onFull={() => toggleFull(g.label)}
                    limit={6}
                  >
                    {g.values.map(([value, n]) => (
                      <label className="chk" key={value}>
                        <input
                          type="checkbox"
                          checked={selectedSpecs.has(specKey(g.label, value))}
                          onChange={() => toggleSpec(specKey(g.label, value))}
                        />
                        <span className="fname" title={value}>
                          {value}
                        </span>
                        <span className="cnt">({n})</span>
                      </label>
                    ))}
                  </FacetGroup>
                ))}

              {activeFilterCount > 0 && (
                <button className="fclear" onClick={clearAllFilters}>
                  {t('counter.clearFilters', { count: activeFilterCount })}
                </button>
              )}
            </div>
          )}
        </aside>

        {/* RESULTS */}
        <main className="col results">
          <div className="rtitle">
            <h1>{t('counter.fullOffer')}</h1>
            {results !== 'empty' && (
              <span className="muted">
                {t('counter.referenceCount', { count: sellableRows.length })}
              </span>
            )}
            {/* The one thing on this screen that turns "delivered tomorrow" into a reason to
                decide NOW — which is why the customer is shown it too. It disappears once the
                cut-off has gone: a countdown to a deadline already missed sells nothing and
                misleads everyone. */}
            {/* Feedback 2.1 — the cut-off of EACH supplier's warehouse, hours and minutes, with
                what is left before it. One chip per supplier; the single global countdown only
                stands in until the list has loaded. */}
            {salesConfig.supplierCutoffs.length > 0 ? (
              <span className="cutoffs">
                {salesConfig.supplierCutoffs.map((s, i) => {
                  // TODAY's row of the weekly table (2026-10-29), read against the live clock: the
                  // config loads once and a till left open past midnight must not show yesterday's
                  // hour. Null = no cut-off today (Sunday: the order counts as Monday before).
                  const cut = todayCutoff(s, nowMs);
                  const left = cut ? cutoffCountdown(cut, nowMs) : null;
                  const name = s.code
                    ? t('counter.warehouseOf', { name: s.code })
                    : t('delivery.warehouseN', { n: i + 1 });
                  return (
                    <span
                      key={s.code ?? i}
                      className={cn(
                        'cutoff',
                        cut && !left && 'is-passed',
                        left?.urgent && 'is-urgent'
                      )}
                      title={
                        cut
                          ? t('counter.cutoffTitle', { time: left?.text ?? '0 min', cutoff: cut })
                          : t('delivery.noCutoffToday', { count: s.leadDays })
                      }
                    >
                      <Truck size={13} />{' '}
                      {cut
                        ? t('delivery.cutoffOf', { name, cutoff: cut })
                        : t('delivery.noCutoffOf', { name })}{' '}
                      {cut && (
                        <b>
                          {left
                            ? t('delivery.cutoffIn', { time: left.text })
                            : t('delivery.cutoffPassedShort')}
                        </b>
                      )}
                    </span>
                  );
                })}
              </span>
            ) : countdown ? (
              <span
                className={cn('cutoff', countdown.urgent && 'is-urgent')}
                title={t('counter.cutoffTitle', {
                  time: countdown.text,
                  cutoff: calendar.cutoff,
                })}
              >
                <Truck size={13} /> {t('counter.nextCollectionIn')} <b>{countdown.text}</b>
              </span>
            ) : null}
          </div>

          {/* Availability tabs, sort control and column header all describe a LIST. With none
              there they were four zeroes, a sort order over nothing and a header captioning empty
              space — the chrome of a table pretending it has rows. They come back with the rows;
              the loading skeletons keep them so nothing jumps when the results land. */}
          {results !== 'empty' && (
            <>
              <div className="tabs" role="tablist" aria-label={t('counter.filterResults')}>
                {/* No "Unavailable" tab: `sellableRows` has already removed every row
                    it would hold, so it could only ever read "(0)" and filter to nothing. */}
                {(['all', 'ok', 'warn'] as const).map((tab) => (
                  <button
                    key={tab}
                    className={cn('tab', availTab === tab && 'on')}
                    onClick={() => setAvailTab(tab)}
                    disabled={tab !== 'all' && availCounts[tab] === 0}
                  >
                    {tab === 'all'
                      ? t('counter.tabAll')
                      : tab === 'ok'
                        ? t('counter.tabInStock')
                        : tab === 'warn'
                          ? t('counter.inbound')
                          : t('counter.unavailable')}{' '}
                    <span className="muted">({availCounts[tab]})</span>
                  </button>
                ))}
              </div>

              <div className="toolbar">
                <span>{t('counter.sortBy')}</span>
                <div className="sortmenu">
                  <button
                    className="sorttrigger"
                    onClick={() => setSortOpen((o) => !o)}
                    aria-expanded={sortOpen}
                  >
                    {t(`counter.sort.${sortKey}`)}
                    <ChevronDown size={13} />
                  </button>
                  {sortOpen && (
                    <>
                      <div className="menu-overlay" onClick={() => setSortOpen(false)} />
                      <div
                        className="menu sortlist"
                        role="listbox"
                        aria-label={t('counter.sortResults')}
                      >
                        {sortOptions.map((o) => (
                          <button
                            key={o.key}
                            role="option"
                            aria-selected={o.key === sortKey}
                            className={cn('sortopt', o.key === sortKey && 'on')}
                            onClick={() => {
                              setSortKey(o.key);
                              setSortOpen(false);
                            }}
                          >
                            {t(`counter.sort.${o.key}`)}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                {/* Said out loud, because a silently shortened list is indistinguishable from a
                    thin catalogue — and the seller is the one who has to answer for it. */}
                {hiddenUnsellable > 0 && sellableRows.length > 0 && (
                  <span className="muted">
                    {t('counter.hiddenUnsellable', { count: hiddenUnsellable })}
                  </span>
                )}
                {!customer && (
                  <span className="spacer muted">{t('counter.pickCustomerToAdd')}</span>
                )}
              </div>

              {/* The fourth column is the MARGIN desk, and it exists only for roles that have one: the
              server omits cost and margin outright without MARGIN_VIEW, so heading an always-empty
              cell "Margin" for a counter operator would be captioning nothing. Those roles get
              four columns. The QTY column went into Availability: a bare "0" beside a supplier's
              ">10" was the ambiguity, and each warehouse line now carries its own figure. The
              header must stay in step with PartCard's cells or the labels slide off their columns. */}
              <div className={cn('res-head', !canSeeMargin && 'no-margin')} aria-hidden="true">
                <span>{t('counter.colProductSku')}</span>
                <span>{t('counter.manufacturer')}</span>
                <span>{t('counter.availability')}</span>
                {canSeeMargin && <span>{t('counter.margin')}</span>}
                <span className="ar">{t('counter.colPrice')}</span>
              </div>
            </>
          )}

          <ul className="parts" aria-live="polite">
            {/* 1.4 (feedback 20 Oct 2026): say the search is running, not just draw placeholders. */}
            {results === 'loading' && (
              <li className="srch-loading" role="status">
                {t('counter.searchingParts')}
              </li>
            )}
            {results === 'loading' &&
              Array.from({ length: 4 }).map((_, i) => (
                <li className="skel-row" key={i}>
                  <div className="skel sk-thumb" />
                  <div>
                    <div className="skel sk-line sk-60" />
                    <div className="skel sk-line sk-40" style={{ marginTop: 8 }} />
                    <div className="skel sk-line sk-80" style={{ marginTop: 8 }} />
                  </div>
                  <div className="skel sk-line" style={{ height: 28 }} />
                </li>
              ))}

            {/* Two different nothings, and they need different screens. Before a search this is
                the resting state of the till — so it offers the references this seller looks up
                all day. After one it is a dead end, and what it owes them is the term it actually
                searched and the way back out of whatever filter emptied the list. */}
            {/* Before a search this is the resting state of the till: the browse tabs (common parts,
                tyres, oils & liquids, packages) and the references this seller looks up all day. */}
            {results === 'empty' && !pq && (
              <li className="browse-slot">
                <CounterBrowse onCategory={browseCategory}>
                  {history.length > 0 && (
                    <div className="quicks">
                      <span className="qlab">
                        <Search size={12} /> {t('counter.recentSearches')}
                      </span>
                      <div className="qrow">
                        {history.slice(0, 6).map((h) => (
                          <button
                            key={h}
                            className="quick"
                            onClick={() => runSearchTerm(h)}
                            title={h}
                          >
                            <History size={12} />
                            <span>{h}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </CounterBrowse>
              </li>
            )}

            {/* After a search, an empty list is a dead end: what it owes the seller is the term it
                actually searched and the way back out of whatever filter emptied the list. */}
            {results === 'empty' && pq && (
              <li className="empty-slot">
                <div className="empty">
                  <div className="ic">
                    <SearchX size={26} />
                  </div>
                  <h2>{t('counter.noMatch')}</h2>
                  <span className="qterm">{pq}</span>
                  <p>{t('counter.noMatchHint')}</p>
                  {activeFilterCount > 0 && (
                    <button className="cta" onClick={clearAllFilters}>
                      <X size={14} /> {t('counter.clearFilters', { count: activeFilterCount })}
                    </button>
                  )}
                </div>
              </li>
            )}

            {/* Every row found, and not one of them sellable today. Without this the seller gets a
                bare list header over nothing — or worse, reads the "no match" state and concludes
                we don't list a part we plainly do. It names the reason and the way to see them. */}
            {results === 'ok' && sellableRows.length === 0 && supplierPending > 0 && (
              <li className="srch-loading" role="status">
                {t('counter.searchingParts')}
              </li>
            )}
            {results === 'ok' &&
              sellableRows.length === 0 &&
              hiddenUnsellable > 0 &&
              supplierPending === 0 && (
                <li className="empty-slot">
                  <div className="empty">
                    <div className="ic">
                      <PackageX size={26} />
                    </div>
                    <h2>{t('counter.nothingDeliverable')}</h2>
                    <p>{t('counter.nothingDeliverableHint', { count: hiddenUnsellable })}</p>
                  </div>
                </li>
              )}

            {results === 'ok' && supplierPending > 0 && (
              <li className="empty-slot" role="status">
                <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 size={14} className="animate-spin" />
                  {t('counter.supplierPending', { count: supplierPending })}
                </div>
              </li>
            )}
            {results === 'ok' && partialWarn && (
              <li>
                <div className="supplier-warn">
                  <AlertTriangle size={16} /> {t('counter.partialWarn')}
                  <button onClick={() => searchProduct(pq)}>{t('counter.retry')}</button>
                </div>
              </li>
            )}

            {results === 'ok' &&
              sortedRows.map((r) => (
                <PartCard
                  key={r.id}
                  r={r}
                  query={pq}
                  canAdd={!!customer}
                  showMargin={canSeeMargin}
                  canMovePrice={canMovePrice}
                  canRequestDiscount={canRequestDiscount}
                  canSellBelowCost={canSellBelowCost}
                  canSourceFromSupplier={canSourceFromSupplier}
                  sourcing={creatingKey === r.id}
                  cal={calendar}
                  nowMs={nowMs}
                  onAdd={(qty, unitPriceHT) => addToCart(r, qty, unitPriceHT)}
                  onAddOrigin={(src, n, unit) => addOrigin(r, src, n, unit)}
                  onInfo={(t) => setInfoPanel({ reference: r.sku, tab: t })}
                  onCopy={(text) => {
                    void navigator.clipboard
                      .writeText(text)
                      .then(() =>
                        push({ tone: 'success', message: t('counter.copied', { sku: text }) })
                      )
                      .catch(() => push({ tone: 'error', message: t('counter.couldNotCopy') }));
                  }}
                />
              ))}
          </ul>
        </main>

        {/* RIGHT — cart / quote / checkout */}
        <aside className="col side" aria-label={t('counter.cartAria')}>
          {credit &&
            (credit.blocked ? (
              <div className="credit">
                <div className="lab">
                  <span>{t('counter.credit', { name: credit.name })}</span>
                  <span className="tnum">{t('counter.noCreditLine')}</span>
                </div>
                <div className="lab">
                  <span className="muted">{t('counter.noCreditLineHint')}</span>
                </div>
              </div>
            ) : (
              <div className="credit">
                <div className="lab">
                  <span>{t('counter.credit', { name: credit.name })}</span>
                  <span className="tnum">
                    {eur(credit.used)} / {eur(credit.limit)}
                  </span>
                </div>
                <div className="bar">
                  <i
                    className={credit.ratio > 0.8 ? 'warn' : undefined}
                    style={{ width: `${Math.min(100, credit.ratio * 100).toFixed(0)}%` }}
                  />
                </div>
                <div className="lab">
                  <span className="muted">{t('counter.available')}</span>
                  <span className="avail-amt tnum">{eur(Math.max(0, credit.avail))}</span>
                </div>
              </div>
            ))}

          <div className="head">
            <span className="t">
              <ShoppingCart size={16} />{' '}
              {mode === 'caisse' ? t('counter.saleInProgress') : t('counter.currentQuote')}
            </span>
            <span className="muted">{t('counter.refs', { count: cart.length })}</span>
          </div>

          <ul className="clist">
            {cart.length === 0 ? (
              <li>
                <div className="empty" style={{ padding: '32px 16px' }}>
                  <div className="ic">
                    <ShoppingCart size={24} />
                  </div>
                  <h2>{mode === 'caisse' ? t('counter.emptySale') : t('counter.emptyQuote')}</h2>
                  <p>{t('counter.emptyHint')}</p>
                </div>
              </li>
            ) : (
              cartByProduct.map((group) => (
                <li className="citem" key={group.productId}>
                  <div className="hd">
                    <span className="badge brand" style={brandStyle(group.brand)}>
                      {group.brand ?? '\u2014'}
                    </span>
                    <span className="cqty tnum">{t('counter.units', { count: group.units })}</span>
                  </div>
                  <div className="body">
                    <div className="thumb" style={{ width: 56, height: 56 }}>
                      <ArticleImage
                        src={group.imageUrl}
                        brandSrc={group.brandImageUrl}
                        alt={group.brand ?? group.sku}
                        size={44}
                        fallback={<PartThumb size={44} />}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="nm">{group.name}</div>
                      <div className="ref">{t('counter.ref', { sku: group.sku })}</div>
                    </div>
                  </div>

                  {/* ONE product line, its origins as SUB-LINES — never the same reference twice
                      in the list. Each keeps its own price and its own quantity, because each is
                      a different acquisition that will travel on a different delivery note. */}
                  {group.lines.map((l) => (
                    <div className="csub" key={l.source}>
                      <div className="csub-h">
                        <span
                          className={cn(
                            'obadge',
                            l.source === 'INTERNAL' ? 'is-ours' : 'is-supplier'
                          )}
                        >
                          {l.source === 'INTERNAL' ? <MapPin size={11} /> : <Truck size={11} />}
                          {l.source === 'INTERNAL'
                            ? t('counter.warehouseOurs')
                            : t('counter.warehouseOf', {
                                name: l.supplierCode ?? t('counter.supplier'),
                              })}
                        </span>
                        <button
                          className="x"
                          aria-label={t('counter.removeOrigin', {
                            origin:
                              l.source === 'INTERNAL'
                                ? t('counter.originOurs')
                                : t('counter.originSupplier'),
                            sku: l.sku,
                          })}
                          onClick={() => removeLine(l.productId, l.source)}
                        >
                          <X size={14} />
                        </button>
                      </div>
                      {/* The promise, on the line it belongs to: what the customer was told at the
                          moment of sale, in their terms. */}
                      <div
                        className={cn(
                          'cwhen tnum',
                          l.source === 'INTERNAL' ? 'is-now' : 'is-later'
                        )}
                      >
                        {l.source === 'INTERNAL'
                          ? t('counter.availableNow')
                          : externalEta(l, l.qty, nowMs, calendar).label}
                      </div>
                      <div className="ft">
                        <div className="stepper">
                          <button
                            aria-label={t('counter.decrease')}
                            onClick={() => setQty(l.productId, l.source, l.qty - 1)}
                          >
                            <Minus size={14} />
                          </button>
                          <span className="q tnum">{l.qty}</span>
                          <button
                            aria-label={t('counter.increase')}
                            onClick={() => setQty(l.productId, l.source, l.qty + 1)}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                        <div>
                          <div className="lt tnum">{eur(round2(l.qty * lineUnit(l)))}</div>
                          <div className="u tnum">
                            {eur(lineUnit(l))} {t('counter.each')}
                            {l.customPriceHT != null && (
                              <span
                                style={{ color: 'var(--warn)' }}
                                title={t('counter.customPrice')}
                              >
                                {' '}
                                \u270e
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* The margin desk that used to sit here moved onto the RESULT ROW, where
                          the price is actually decided — see the margin column in PartCard. Three
                          input fields per sub-line were also most of this panel's height, and the
                          basket itself is what a seller reads back to the customer. What it can
                          still do from here is UNDO: a price carried in from the row is marked,
                          and one click puts the line back on the catalogue price. */}
                      {canSeeMargin && l.customPriceHT != null && (
                        <div className="cpricenote">
                          <span title={t('counter.customPrice')}>
                            {t('counter.cataloguePrice')}{' '}
                            <b className="tnum">{eur(l.price.finalUnitPriceHT)}</b>
                          </span>
                          <button
                            type="button"
                            className="lm-reset"
                            onClick={() => setLinePrice(l.productId, l.source, null)}
                            title={t('counter.resetToCatalogue')}
                          >
                            <RotateCcw size={12} /> {t('counter.reset')}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </li>
              ))
            )}
          </ul>

          <div className="totals">
            {/* Said plainly, BEFORE validating, because it is the part the customer feels: units
                coming off our shelf leave today, units ordered in leave on a later round, and
                those are two shipments with two delivery notes. A seller who only finds out at
                despatch has already promised one delivery. */}
            {deliveryNoteCount > 1 && (
              <div className="blnote">
                <FileText size={14} />
                <span>
                  <Trans
                    i18nKey="counter.deliveryNotesNote"
                    values={{ count: deliveryNoteCount }}
                    components={{ 1: <b /> }}
                  />
                </span>
              </div>
            )}
            <div className="ln">
              <span className="muted">{t('counter.itemCount', { count: units })}</span>
              <span className="tnum">{eur(totals.subtotal)}</span>
            </div>
            {totals.deposit > 0 && (
              <div className="ln">
                <span className="muted">{t('counter.coreDeposit')}</span>
                <span className="tnum">{eur(totals.deposit)}</span>
              </div>
            )}
            {/* WP-1 Step 5 — shipping as its own line (its own VAT rate), free above the threshold. */}
            {cart.length > 0 &&
              (totals.shipping > 0 ? (
                <div className="ln">
                  <span className="muted">
                    {t('counter.shipping')}
                    {Number.isFinite(totals.freeShippingFrom) && (
                      <em className="ml-1 text-xs opacity-70">
                        {t('counter.freeOver', { amount: eur(totals.freeShippingFrom) })}
                      </em>
                    )}
                  </span>
                  <span className="tnum">{eur(totals.shipping)}</span>
                </div>
              ) : (
                <div className="ln">
                  <span className="muted">{t('counter.shipping')}</span>
                  <span className="tnum">{t('counter.free')}</span>
                </div>
              ))}
            <div className="ln">
              <span className="muted">{t('common.totalHT')}</span>
              <span className="tnum">{eur(totals.ht)}</span>
            </div>
            <div className="ln">
              {/* Summed per line at each line's own rate (a basket can mix 20% and 0%),
                  so no single rate is hardcoded in the label. */}
              <span className="muted">{t('common.vat')}</span>
              <span className="tnum">{eur(totals.vat)}</span>
            </div>
            <div className="ln grand">
              <span>{t('common.totalTTC')}</span>
              <span className="tnum">{eur(totals.ttc)}</span>
            </div>

            {mode === 'devis' ? (
              <>
                {/* Vehicle. Optional — plenty of counter sales are not for one car — but when
                    present it is frozen on this document and printed on the invoice. */}
                <div className="pofield">
                  <span className="ic">
                    <Car size={16} />
                  </span>
                  <input
                    placeholder={t('counter.plate')}
                    value={licensePlate}
                    style={{ textTransform: 'uppercase' }}
                    onChange={(e) => {
                      plateTouched.current = true;
                      setLicensePlate(e.target.value.toUpperCase());
                    }}
                    aria-label={t('counter.plateAria')}
                  />
                </div>
                <button
                  className="btn btn--primary btn--block btn--lg"
                  style={{ marginTop: 'var(--s-3)' }}
                  disabled={busy || !customer || cart.length === 0}
                  onClick={() => void placeOrder()}
                >
                  {busy
                    ? t('counter.placing')
                    : isB2b
                      ? t('counter.order')
                      : t('counter.placeOrder')}
                </button>
                {quoteActions}
                {quoteLoadError && (
                  <p
                    style={{ marginTop: 'var(--s-2)', fontSize: 12, color: 'var(--danger, #b00)' }}
                  >
                    {quoteLoadError}
                  </p>
                )}
              </>
            ) : doneTicket ? (
              <div className="done">
                <div className="ico">
                  <Check size={30} />
                </div>
                {doneTicket.onAccount ? (
                  <>
                    <h3>{t('counter.chargedToAccount')}</h3>
                    <div className="sub">
                      {doneTicket.invoiced
                        ? t('counter.invoiceNo', { number: doneTicket.invoiceNumber })
                        : doneTicket.deliveryNote
                          ? t('counter.deliveryNoteNo', {
                              number: doneTicket.deliveryNote.deliveryNoteNumber,
                            })
                          : t('counter.chargedToAccountSub')}
                    </div>
                    <div className="rline">
                      <span className="muted">{t('common.totalTTC')}</span>
                      <b className="tnum">{eur(totals.ttc)}</b>
                    </div>
                  </>
                ) : (
                  <>
                    <h3>{t('counter.paymentConfirmed')}</h3>
                    <div className="sub">
                      {doneTicket.receipt
                        ? t('counter.receiptNo', { number: doneTicket.receipt.receiptNumber })
                        : t('counter.invoiceNo', { number: doneTicket.invoiceNumber })}
                    </div>
                    <div className="rline">
                      <span className="muted">{t('common.totalTTC')}</span>
                      <b className="tnum">{eur(doneTicket.ttc)}</b>
                    </div>
                    <div className="rline">
                      <span className="muted">{t('counter.received')}</span>
                      <b className="tnum">{eur(doneTicket.received)}</b>
                    </div>
                    <div className="rline">
                      <span className="muted">{t('counter.changeGiven')}</span>
                      <b className="tnum" style={{ color: 'var(--ok)' }}>
                        {eur(doneTicket.change)}
                      </b>
                    </div>
                  </>
                )}
                {/* The part of the sale that did NOT leave with the customer. The seller has to
                    say this before they walk away, so it is on the receipt panel rather than in
                    a toast that disappears. */}
                {(doneTicket.awaitingSupplier ?? 0) > 0 && (
                  <p className="stillcoming">
                    <Truck size={13} />{' '}
                    {t('counter.stillComing', {
                      count: doneTicket.awaitingSupplier,
                      date: doneTicket.awaitingEta ?? '',
                      amount: `≈ ${eur(doneTicket.awaitingTTC ?? 0)}`,
                    })}
                  </p>
                )}
                {/* E8 — the parts that follow are collected on this code, so the seller says it
                    while the customer is still standing there. */}
                {(doneTicket.awaitingSupplier ?? 0) > 0 && doneTicket.pickupCode && (
                  <p className="stillcoming">
                    {t('counter.pickupCode', { code: doneTicket.pickupCode })}
                  </p>
                )}
                {doneTicket.receipt && (
                  <button
                    className="btn btn--primary btn--block"
                    style={{ marginTop: 'var(--s-3)' }}
                    onClick={() =>
                      /* The print dialog opened on the ORDER: it lists every document of the
                         sale and starts on what this customer is handed first (Ticket → DEP →
                         Invoice for a particulier, the BL for a pro). */
                      setPrintPrompt({
                        docType: 'ORDER',
                        documentId: doneTicket.orderId,
                        documentNumber: doneTicket.orderNumber,
                        orderId: doneTicket.orderId,
                        then: () => {},
                      })
                    }
                  >
                    <Printer size={15} /> {t('counter.printReceipt')}
                  </button>
                )}
                {doneTicket.receipt && doneTicket.invoice && (
                  <button
                    className="btn btn--ghost btn--block"
                    style={{ marginTop: 'var(--s-2)' }}
                    onClick={() =>
                      setPrintPrompt({
                        docType: 'INVOICE',
                        documentId: doneTicket.invoice!.id,
                        documentNumber: doneTicket.invoice!.invoiceNumber,
                        orderId: doneTicket.orderId,
                        then: () => {},
                      })
                    }
                  >
                    {t('counter.issueInvoice')}
                  </button>
                )}
                {/* The deposit's own tax document — also on request only (2026-10-08). */}
                {doneTicket.receipt && doneTicket.depositInvoice && (
                  <button
                    className="btn btn--ghost btn--block"
                    style={{ marginTop: 'var(--s-2)' }}
                    onClick={() =>
                      setPrintPrompt({
                        docType: 'INVOICE',
                        documentId: doneTicket.depositInvoice!.id,
                        documentNumber: doneTicket.depositInvoice!.invoiceNumber,
                        orderId: doneTicket.orderId,
                        then: () => {},
                      })
                    }
                  >
                    {t('counter.printDepositInvoice', {
                      number: doneTicket.depositInvoice.invoiceNumber,
                    })}
                  </button>
                )}
                {doneTicket.onAccount && doneTicket.deliveryNote && !doneTicket.invoiced && (
                  <button
                    className="btn btn--primary btn--block"
                    style={{ marginTop: 'var(--s-3)' }}
                    onClick={() =>
                      setPrintPrompt({
                        docType: 'DELIVERY_NOTE_CLIENT',
                        documentId: doneTicket.deliveryNote!.id,
                        documentNumber: doneTicket.deliveryNote!.deliveryNoteNumber,
                        orderId: doneTicket.orderId,
                        then: () => {},
                      })
                    }
                  >
                    <Printer size={15} />{' '}
                    {t('counter.printDeliveryNote', {
                      number: doneTicket.deliveryNote.deliveryNoteNumber,
                    })}
                  </button>
                )}
                <button
                  className="btn btn--ghost btn--block"
                  style={{ marginTop: 'var(--s-2)' }}
                  onClick={() => navigate(`/orders/${doneTicket.orderId}`)}
                >
                  {t('counter.openOrder')}
                </button>
                <button
                  className="btn btn--primary btn--block"
                  style={{ marginTop: 'var(--s-2)' }}
                  onClick={newSale}
                >
                  {t('counter.newSale')}
                </button>
              </div>
            ) : (
              <>
                {/* A MIXED BASKET IS PAID IN TWO MOMENTS, because it is delivered in two.
                    The half on our shelf leaves with the customer now and is invoiced now; the
                    supplier's half is invoiced when it is handed over. Stating both figures is
                    what stops the till taking money for goods nobody has yet — and it is what
                    the seller reads out to the customer.

                    This used to be a dead end: the pay-now button was simply disabled, because
                    `POST /pos/ticket` delivers every line off our own shelf and cannot express
                    "this half comes from a supplier on Thursday". The sale now goes through as
                    an ORDER (see checkoutMixed) and the till collects the shipment it hands over. */}
                {hasSupplierLine && (
                  <div className="splitpay">
                    <div className="sprow">
                      <span className="lab">
                        <MapPin size={12} /> {t('counter.warehouseOurs')}
                      </span>
                      <span className="when">{t('counter.takenNow')}</span>
                      <b className="tnum">{eur(originTTC.internal)}</b>
                    </div>
                    <div className="sprow is-later">
                      <span className="lab">
                        <Truck size={12} /> {t('counter.supplier')}
                      </span>
                      <span className="when">
                        {supplierEtaLabel
                          ? t('counter.invoicedOnDelivery', { date: supplierEtaLabel })
                          : t('counter.invoicedLater')}
                      </span>
                      {/* Indicative, and marked as such: the supplier's price is re-quoted when
                          the order is placed, so the figure billed on delivery is that day's. */}
                      <b className="tnum" title={t('counter.supplierPriceRequoted')}>
                        ≈ {eur(originTTC.external)}
                      </b>
                    </div>
                    {!onAccount && !proComptant && !zeroCreditPro && (
                      <div className="flex flex-wrap items-center gap-2 text-xs mt-2">
                        <span className="muted">{t('counter.supplierPartPay')}</span>
                        {(['FULL', 'DEPOSIT'] as const).map((k) => (
                          <button
                            key={k}
                            type="button"
                            onClick={() => setSupplierPay(k)}
                            className={cn(
                              'rounded border px-2 py-0.5',
                              supplierPay === k &&
                                'bg-primary text-primary-foreground border-primary'
                            )}
                          >
                            {k === 'FULL'
                              ? t('counter.supplierPayFull', { amount: eur(originTTC.external) })
                              : t('counter.supplierPayDeposit', { pct: minDepositPct })}
                          </button>
                        ))}
                        {supplierPay === 'DEPOSIT' && (
                          <>
                            <input
                              inputMode="decimal"
                              value={depositInput}
                              onChange={(e) => setDepositInput(e.target.value)}
                              placeholder={eur(depositFloor)}
                              aria-label={t('counter.depositAmountAria')}
                              className="w-24 rounded border px-2 py-0.5 tnum"
                            />
                            <span className="muted">
                              {t('counter.depositFloor', {
                                pct: minDepositPct,
                                amount: eur(depositFloor),
                              })}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                    {!onAccount &&
                      !proComptant &&
                      !zeroCreditPro &&
                      supplierPay === 'DEPOSIT' &&
                      depositAmount > 0 && (
                        <p className="spnote">
                          {t('counter.balanceAtDelivery', {
                            amount: eur(round2(originTTC.external - depositAmount)),
                          })}
                        </p>
                      )}
                    <p className="spnote">
                      {onAccount
                        ? t('counter.mixedSaleNoteAccount')
                        : zeroCreditPro
                          ? t('counter.mixedSaleNoteZeroCredit')
                          : proComptant
                            ? t('counter.mixedSaleNotePro')
                            : t('counter.mixedSaleNote')}
                    </p>
                  </div>
                )}
                {onAccount ? (
                  <>
                    <p
                      className="muted"
                      style={{ marginTop: 'var(--s-3)', fontSize: 'var(--t-sm)' }}
                    >
                      {credit?.blocked ? t('counter.noCreditLineHint') : t('counter.onAccountHint')}
                    </p>
                    <button
                      className="btn btn--ok btn--block btn--lg"
                      style={{ marginTop: 'var(--s-2)' }}
                      disabled={busy || !customer || cart.length === 0 || !!credit?.blocked}
                      onClick={() => void confirmCheckout()}
                    >
                      {t('counter.chargeToAccount', { amount: eur(totals.ttc) })}
                    </button>
                  </>
                ) : (
                  <Checkout
                    ttc={dueNow}
                    confirmLabel={
                      hasSupplierLine && originTTC.internal <= 0
                        ? t('counter.placeOrder')
                        : undefined
                    }
                    reste={reste}
                    change={change}
                    payments={payments}
                    curMethod={curMethod}
                    payAmount={payAmount}
                    setCurMethod={setCurMethod}
                    setPayAmount={setPayAmount}
                    addPayment={addPayment}
                    removePayment={removePayment}
                    voucherNo={voucherNo}
                    setVoucherNo={(v) => {
                      setVoucherNo(v);
                      setVoucher(null);
                    }}
                    voucher={voucher}
                    checkVoucher={() => void checkVoucher()}
                    confirm={confirmCheckout}
                    disabled={
                      busy ||
                      !customer ||
                      cart.length === 0 ||
                      // "Partial payment" chosen but no amount typed yet.
                      (!proComptant &&
                        !zeroCreditPro &&
                        hasSupplierLine &&
                        supplierPay === 'DEPOSIT' &&
                        depositAmount <= 0)
                    }
                  />
                )}
                {quoteActions}
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

// ===================== LineMargin (counter cart, margin authority) =====================
// Per-line pricing at the till. Three linked ways to say the same thing, because counter staff
// think in all of them: discount % off the grid price, margin %, or the final €/unit the
// customer negotiated. Whichever is typed, the others follow.
//
// The floor is shown and blocked locally for operators who may not sell under cost — the server
// (MarginPolicy) is authoritative, but bouncing a sale at checkout is a terrible way to find out.
//
// READ-ONLY WITHOUT PRICE AUTHORITY (#14). The counter may see this desk and may not use it:
// the three fields are disabled and the panel says what to do instead (ask for a discount on the
// order). The alternative — leaving them typeable and letting the server answer 403 — is exactly
// the "endpoint that refuses with no explanation" this screen must not produce, and it would
// refuse at CHECKOUT, after the customer has been quoted a price out loud.
function LineMargin({
  cost,
  unit,
  gridUnit,
  canMovePrice,
  canRequestDiscount,
  canSellBelowCost,
  overridden,
  onSet,
}: {
  cost: number;
  unit: number;
  gridUnit: number;
  canMovePrice: boolean;
  canRequestDiscount: boolean;
  canSellBelowCost: boolean;
  overridden: boolean;
  onSet: (priceHT: number | null) => void;
}) {
  const { t } = useTranslation();
  const marginOf = (u: number) => (u > 0 ? ((u - cost) / u) * 100 : 0);
  const discountOf = (u: number) => (gridUnit > 0 ? ((gridUnit - u) / gridUnit) * 100 : 0);
  const [priceStr, setPriceStr] = useState(unit.toFixed(2));
  const [pctStr, setPctStr] = useState(marginOf(unit).toFixed(1));
  const [offStr, setOffStr] = useState(discountOf(unit).toFixed(1));

  // Re-sync when the effective unit price changes from outside (e.g. reset to grid price).
  useEffect(() => {
    setPriceStr(unit.toFixed(2));
    setPctStr(marginOf(unit).toFixed(1));
    setOffStr(discountOf(unit).toFixed(1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  // Belt and braces behind the `disabled` attribute below: a desk with no authority never emits
  // a price, whatever reaches the handler (autofill, a stray paste, a future refactor).
  const applyPrice = (v: string) => {
    if (!canMovePrice) return;
    setPriceStr(v);
    const p = parseFloat(v.replace(',', '.'));
    if (!isNaN(p) && p > 0) onSet(round2(p));
  };
  const applyPct = (v: string) => {
    if (!canMovePrice) return;
    setPctStr(v);
    const m = parseFloat(v.replace(',', '.'));
    if (!isNaN(m) && m < 100) onSet(round2(cost / (1 - m / 100)));
  };
  // Discount off the CATALOGUE price — what the customer is actually being given.
  const applyOff = (v: string) => {
    if (!canMovePrice) return;
    setOffStr(v);
    const d = parseFloat(v.replace(',', '.'));
    if (!isNaN(d) && d < 100 && gridUnit > 0) onSet(round2(gridUnit * (1 - d / 100)));
  };

  const margin = marginOf(unit);
  const marginEUR = round2(unit - cost);
  const belowCost = unit < cost;
  const marginColor = belowCost
    ? 'var(--danger)'
    : margin >= 25
      ? 'var(--ok)'
      : margin >= 10
        ? 'var(--warn)'
        : 'var(--danger)';

  return (
    /* THREE LINKED WAYS to say one thing, laid out as a small form rather than a run-on line.
       The old row put label, input, label, input, label, input, unit, unit and a reset glyph on a
       single wrapping flex line inside a 340px panel: everything was 46px wide, nothing was
       aligned, and which unit belonged to which box was a guess. Each control now owns a labelled
       cell in a grid, so the three read as three choices and the columns line up down the cart. */
    <div className="linemargin">
      <div className="lm-read">
        <span className="lm-cost">
          {t('counter.cost')} <b className="mono">{eur(cost)}</b>
        </span>
        {/* Margin in € as well as %: "18 %" and "4,20 €" land very differently on a cheap part. */}
        <span
          className="lm-gain mono"
          style={{ color: marginColor }}
          title={t('counter.marginPerUnit')}
        >
          {marginEUR >= 0 ? '+' : ''}
          {eur(marginEUR)}
        </span>
        {/* Clearing an override is always allowed: putting a line BACK on the catalogue price
            takes no authority, and an operator who inherited a negotiated price from a reopened
            quote needs a way to drop it. */}
        {overridden && (
          <button
            type="button"
            className="lm-reset"
            onClick={() => onSet(null)}
            title={t('counter.resetToCatalogue')}
          >
            <RotateCcw size={12} /> {t('counter.reset')}
          </button>
        )}
      </div>

      {belowCost && (
        <div className="lm-warn">
          <AlertTriangle size={12} />
          {canSellBelowCost ? t('counter.belowCostAllowed') : t('counter.belowCostBlocked')}
        </div>
      )}

      {/* #14 — say WHY the fields are dead, and name the lane that is open. "Ask a manager" is
          not advice, it is the actual procedure: a discount request on the order, decided by an
          approver, with the reason and both names kept (#18). */}
      {!canMovePrice && (
        <div className="lm-warn">
          <AlertTriangle size={12} />
          {canRequestDiscount ? t('counter.priceLockedAsk') : t('counter.priceLocked')}
        </div>
      )}

      <div className="lm-grid">
        <label className="lm-field">
          <span className="lm-lbl">{t('counter.discount')}</span>
          <span className="lm-inp">
            <input
              className="minp"
              inputMode="decimal"
              value={offStr}
              onChange={(e) => applyOff(e.target.value)}
              disabled={!canMovePrice}
              aria-label={t('counter.discountAria')}
            />
            <i className="lm-unit">%</i>
          </span>
        </label>
        {/* SAY WHICH MARGIN THIS IS. It is margin on the SELLING price — cost / (1 − m), see
            applyPct — not a coefficient on cost. A seller who types 45 meaning ×1.45 and gets
            ×1.8182 is 25 % out on every line with nothing wrong-looking on the screen, so the
            label carries "/sell" at the width the cell allows and the tooltip spells it out. */}
        <label className="lm-field" title={t('counter.marginOnSell')}>
          <span className="lm-lbl">{t('counter.marginShort')}</span>
          <span className="lm-inp">
            <input
              className="minp"
              inputMode="decimal"
              value={pctStr}
              onChange={(e) => applyPct(e.target.value)}
              disabled={!canMovePrice}
              aria-label={t('counter.marginOnSell')}
              style={{ color: marginColor }}
            />
            <i className="lm-unit">%</i>
          </span>
        </label>
        <label className="lm-field">
          <span className="lm-lbl">{t('counter.unitPrice')}</span>
          <span className="lm-inp">
            <input
              className="minp"
              inputMode="decimal"
              value={priceStr}
              onChange={(e) => applyPrice(e.target.value)}
              disabled={!canMovePrice}
              aria-label={t('counter.priceAria')}
            />
            <i className="lm-unit">€</i>
          </span>
        </label>
      </div>

      {/* The floor, stated rather than only enforced. Margin 0 IS the net purchase price, and
          that equivalence is the reason a seller can work in margin at all — it tells them how
          far down they can go without doing arithmetic. Enforcement is MarginPolicy's, server
          side; this is so nobody discovers the limit at checkout. */}
      {canMovePrice && <p className="lm-floor">{t('counter.marginFloorNote')}</p>}
    </div>
  );
}

// ===================== Filter panel group =====================
/**
 * One collapsible block of the filter rail: a title that folds it away, an optional sort toggle,
 * and a "show all" tail for lists longer than they are useful.
 *
 * The tail exists because facet lists are open-ended — a full-offer search on a common reference
 * returns twenty makes, and a rail that lists all of them pushes every other criterion off the
 * screen. Truncation only earns its keep if the count is honest about what is hidden, so the
 * button says how many rather than a bare "more".
 */
function FacetGroup({
  title,
  folded,
  onFold,
  sort,
  onSort,
  full,
  onFull,
  limit,
  children,
}: {
  title: string;
  folded: boolean;
  onFold: () => void;
  sort?: 'alpha' | 'count';
  onSort?: () => void;
  full?: boolean;
  onFull?: () => void;
  /** Rows shown while collapsed. Omit for groups short enough to always show whole. */
  limit?: number;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const items = Children.toArray(children);
  if (items.length === 0) return null;
  const capped = limit != null && !full && items.length > limit;
  const shown = capped ? items.slice(0, limit) : items;

  return (
    <div className={cn('fgroup', folded && 'is-folded')}>
      <h3>
        <button className="ftitle" onClick={onFold} aria-expanded={!folded}>
          <ChevronDown size={12} className={cn(!folded && 'flip')} />
          {title}
        </button>
        {onSort && (
          <button
            className="fsort"
            onClick={onSort}
            title={sort === 'count' ? t('counter.sortedByCount') : t('counter.sortedAlpha')}
            aria-label={t('counter.changeSortOrder')}
          >
            {sort === 'count' ? <ArrowDown01 size={13} /> : <ArrowDownAZ size={13} />}
          </button>
        )}
      </h3>
      {!folded && (
        <>
          {shown}
          {(capped || full) && limit != null && items.length > limit && (
            <button className="fmore" onClick={onFull}>
              {capped
                ? t('counter.showMore', { count: items.length - limit })
                : t('counter.showLess')}
              <ChevronDown size={11} className={cn(!capped && 'flip')} />
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ===================== PartCard (counter) =====================
function PartCard({
  r,
  query,
  canAdd,
  showMargin,
  canMovePrice,
  canRequestDiscount,
  canSellBelowCost,
  cal,
  nowMs,
  canSourceFromSupplier,
  sourcing,
  onAdd,
  onAddOrigin,
  onInfo,
  onCopy,
}: {
  r: Row;
  query: string;
  canAdd: boolean;
  showMargin: boolean;
  /** May this operator set a line's final price at all (#14)? False = read-only margin desk. */
  canMovePrice: boolean;
  /** …and if not, may they ask for a discount instead? Shapes what the locked desk offers. */
  canRequestDiscount: boolean;
  /** May this operator sell a part straight off a supplier's shelf? */
  canSourceFromSupplier: boolean;
  /** This row is being resolved against the supplier — a live round trip, slow enough to show. */
  sourcing: boolean;
  /** Whether this operator may take a line under cost — the margin desk warns either way, but only
   *  says "blocked" to someone it would actually be blocked for. */
  canSellBelowCost: boolean;
  /** Cut-off, delivery hour and the closure list — the whole rule, straight from the server. */
  cal: DeliveryCalendar;
  /** Shared minute clock, so the whole list turns over together at the cut-off. */
  nowMs: number;
  /** The negotiated unit price rides WITH the add: it was set on a row, not on a cart line, so
   *  there is nothing to apply it to until the line exists. `undefined` = sell at the grid price. */
  onAdd: (qty: number, unitPriceHT?: number) => void;
  /** Add ONE origin's composed quantity, at that origin's own price. */
  onAddOrigin: (source: 'INTERNAL' | 'EXTERNAL', qty: number, unitPriceHT: number) => void;
  /** Opens the identity panel on the tab the operator clicked — no navigation. */
  onInfo: (tab: ArticleInfoTab) => void;
  /** Copy any of the row's references to the clipboard — our SKU from the top of the row, the
   *  manufacturer's number from the information line. Both report through the same toast. */
  onCopy: (text: string) => void;
}) {
  const { t } = useTranslation();
  const [qty, setQty] = useState(1);
  const [zoomed, setZoomed] = useState(false);

  /* ---- the row's own price, before it is in anyone's basket -------------------------------------
     The margin desk on the row edits a price that has no cart line behind it yet, so the override
     lives HERE and rides along with the add. It is deliberately not pushed into the cart on every
     keystroke: a seller who works out a price and then walks away has not sold anything, and a row
     that added itself to the basket the moment a margin was typed would do exactly that.

     Reset when the row is re-priced from the server (a customer change re-runs /availability and
     every grid price with it) — an override computed against the previous customer's price is a
     figure nobody agreed to. */
  const basePrice = r.price?.finalUnitPriceHT ?? r.supplier?.indicativeSellPriceHT ?? null;
  const [override, setOverride] = useState<number | null>(null);
  useEffect(() => {
    setOverride(null);
  }, [basePrice]);
  const effectiveUnit = override ?? basePrice ?? 0;
  /* Present for every row of a margin role, empty on the rows it cannot price — see the cell. */
  const hasMarginCell = showMargin;

  const isMatch =
    !!query &&
    [r.sku, r.manufacturerRef, r.ean].some((v) => v?.toLowerCase() === query.toLowerCase());

  /* THE WAREHOUSES this reference can be served from, each NAMED, each with its own quantity and
     its own moment. "Entrepôt 1 · >10" under "Tomorrow 13:00" could not be told from our own
     shelf — a counter salesman reading it had no way to know whether the part was in the building
     to hand over now, or at a supplier and arriving on tomorrow's round. So every line says whose
     shelf it is: ours is MECADEPOT, available now; a supplier's is named after the supplier and
     carries the date its round lands (after the cut-off, the next one).

     Ours is shown on every row we carry, even at zero — "not on our shelf" is the fact that sends
     the seller to the supplier line, and leaving it out reads as "the question was never asked".
     A SUPPLIER row is a part we do not carry, so it has no MECADEPOT line by definition. */
  const isSupplierRow = r.source === 'SUPPLIER';
  const depotQty = r.internalStock ?? 0;
  const supplierQty = r.supplierQty ?? 0;
  const cappedQty = (n: number): string => (n > 10 ? '>10' : String(n));
  const supplierLabel = t('counter.warehouseOf', {
    name: r.supplierCode ?? r.supplierName ?? t('counter.supplier'),
  });
  const supplierRowEta =
    supplierQty > 0 || (r.leadDays != null && r.leadDays > 0)
      ? externalEta(r, 1, nowMs, cal)
      : null;

  // Only a real picture is worth a lightbox; the drawn placeholder has nothing to enlarge.
  const zoomable = !!(r.imageUrl || r.brandImageUrl);

  /* The information line. TecDoc lists far more criteria than a result row can carry, and the ones
     that decide a counter sale are the first few (size, then type) — the rest stay one click away
     behind "More info". Six rather than five: the line now ends with the reference, and five specs
     plus a code left an awkward half-empty second row at the counter's usual width.

     Nothing to show on a SUPPLIER row: `specifications` is only ever snapshotted onto a product we
     carry, so those rows fall back to the reference alone rather than an empty strip. */
  const specs = (r.specifications ?? []).slice(0, 6);

  /* THE NUMBER TO QUOTE — the manufacturer's, not ours. `r.sku` is an internal code that means
     nothing to the customer holding the old part or to the supplier taking the order, and it is
     already at the top of the row; this is the one that gets read down a phone. */
  const quotableRef = r.manufacturerRef ?? r.supplier?.supplierArticleNo ?? null;

  /* THE ORIGINS this reference can be served from, each with its own availability, delivery moment
     and price. Our shelf first, always — see the Branches cell for why the sort must never reach
     in here. An origin appears only if it can actually supply and can actually be priced: a row
     the seller cannot sell is worse than no row. */
  const origins = useMemo(() => {
    const out: {
      source: 'INTERNAL' | 'EXTERNAL';
      available: number;
      unitPriceHT: number;
      eta: Eta;
    }[] = [];
    if ((r.internalStock ?? 0) > 0 && r.price) {
      out.push({
        source: 'INTERNAL',
        available: r.internalStock ?? 0,
        unitPriceHT: r.price.finalUnitPriceHT,
        eta: deliveryEta(0, cal, nowMs),
      });
    }
    // The supplier's own price when we have one; failing that our shelf price, so a quote that
    // arrived without a cost still sells rather than vanishing.
    const extPrice = r.externalPrice?.finalUnitPriceHT ?? r.price?.finalUnitPriceHT;
    if ((r.supplierQty ?? 0) > 0 && extPrice != null) {
      out.push({
        source: 'EXTERNAL',
        available: r.supplierQty ?? 0,
        unitPriceHT: extPrice,
        eta: externalEta(r, 1, nowMs, cal),
      });
    }
    return out;
  }, [r, cal, nowMs]);

  const [originQty, setOriginQty] = useState<Partial<Record<'INTERNAL' | 'EXTERNAL', number>>>({});
  const composedUnits = origins.reduce((n, o) => n + (originQty[o.source] ?? 0), 0);
  const composedOrigins = origins.filter((o) => (originQty[o.source] ?? 0) > 0).length;

  return (
    <li className={cn('part', isMatch && 'is-match')}>
      {/* A full-width strip rather than an inline pill: it labels the ROW beneath it, and at pill
          width it read as another chip belonging to the product. */}
      {isMatch && (
        <div className="matchbar">
          <Search size={13} /> {t('counter.matchBar', { query })}
        </div>
      )}
      {/* Five tracks, not six with an empty one, when there is no margin column to fill: an unused
          track would leave a dead gap between availability and price down the whole list. */}
      <div className={cn('main', !hasMarginCell && 'no-margin')}>
        <div className="idblk">
          {/* Real part photo when TecDoc has one; failing that the brand logo, then the drawn
              placeholder so a row is never empty. A real picture opens a lightbox — at row size a
              filter housing and a filter cartridge are the same grey rectangle. */}
          {zoomable ? (
            <button
              type="button"
              className="thumb thumb--zoom"
              onClick={() => setZoomed(true)}
              aria-label={t('counter.enlargePhoto', { sku: r.sku })}
              title={t('counter.clickToEnlarge')}
            >
              <ArticleImage
                src={r.imageUrl}
                brandSrc={r.brandImageUrl}
                alt={r.brand ?? r.sku}
                size={68}
                fallback={<PartThumb size={58} />}
              />
              <span className="zoomhint" aria-hidden="true">
                <ZoomIn size={13} />
              </span>
            </button>
          ) : (
            <div className="thumb">
              <PartThumb size={58} />
            </div>
          )}
          <div className="idtext">
            <div className="top">
              <h3 className="sku" title={r.sku}>
                {r.sku}
              </h3>
              {/* Copy alone. The catalogue-view and favourite buttons that sat here were both
                  detours out of a sale in progress — and the favourite one was never wired to
                  anything. What the row genuinely needs is the reference in the clipboard. The
                  part sheet is still a click away under "More info". */}
              <span className="rowicons">
                <button
                  className="iconbtn"
                  aria-label={t('counter.copySku', { sku: r.sku })}
                  onClick={() => onCopy(r.sku)}
                >
                  <Copy size={15} />
                </button>
              </span>
            </div>
            <div className="pname" title={r.name}>
              {r.name}
            </div>
          </div>
        </div>

        <div className="cell brandcell">
          <span className="lab">{t('counter.manufacturer')}</span>
          {/* The logo alone is how a mechanic recognises a make across the counter, but logos are
              licensed TecDoc media that can be missing or 404 — so the name always stays. */}
          {r.brandImageUrl && (
            <ArticleImage
              src={r.brandImageUrl}
              alt={r.brand ?? ''}
              size={28}
              className="brandlogo"
              fallback={null}
            />
          )}
          <span className="badge brand" style={brandStyle(r.brand)}>
            {r.brand ?? '—'}
          </span>
        </div>

        {/* Availability, one NAMED line per warehouse. Each answers the customer's only question —
            when can I have it — for the shelf it names: ours right now, the supplier's on the day
            its round lands. The former single "Tomorrow 13:00" plus an unnamed "Entrepôt 1 >10"
            answered it for nobody in particular. */}
        <div className="cell availcell">
          <span className="lab">{t('counter.availability')}</span>
          {!isSupplierRow && (
            <span
              className={cn('whline', depotQty > 0 ? 'is-now' : 'is-none')}
              title={t('counter.depotStock', { count: depotQty })}
            >
              <span className="who">
                <span className="dot" />
                <MapPin size={11} />
                <span className="nm">{t('counter.warehouseOurs')}</span>
              </span>
              <span className="how">
                <b className="tnum">{cappedQty(depotQty)}</b>
                <span className="when">
                  {depotQty > 0 ? t('counter.availableNow') : t('counter.notOnShelf')}
                </span>
              </span>
            </span>
          )}
          {supplierRowEta && (
            <span
              className={cn('whline is-ext', supplierQty > 0 ? 'is-later' : 'is-none')}
              title={supplierRowEta.detail}
            >
              <span className="who">
                <span className="dot" />
                <Truck size={11} />
                <span className="nm">{supplierLabel}</span>
              </span>
              <span className="how">
                <b className="tnum">{cappedQty(supplierQty)}</b>
                {/* A date is a promise; a supplier holding none of it has none to make. */}
                <span className="when">{supplierQty > 0 ? supplierRowEta.label : '\u2014'}</span>
              </span>
            </span>
          )}
          {/* A supplier row that nobody can deliver (the explicitly-unavailable quote). A row we
              carry already says "not on our shelf" above, and says it once. */}
          {isSupplierRow && !supplierRowEta && (
            <span className="whline is-none">
              <span className="who">
                <span className="dot" />
                {r.supplier?.noOffer ? t('counter.noOffer') : t('counter.unavailable')}
              </span>
            </span>
          )}
        </div>

        {/* ---- MARGIN, ON THE ROW ------------------------------------------------------------
            The price a part leaves at is decided while looking at the offer, not afterwards in the
            basket. This column used to be a read-only "Cost 9,35 € · 24.8 %" tucked under the
            price, with the only editable control three columns away in the cart — so setting a
            price meant adding the line first, finding it in the panel, and adjusting it there.
            The same three linked fields now sit beside the part they price, and what they set
            travels into the cart with the add.

            Gated on margin authority, not merely hidden from it: the server sends no cost at all
            to a role without MARGIN_VIEW, so there is nothing here to render for one. Those roles
            keep the branches cell this column used to be. */}
        {hasMarginCell && (
          <div className="cell margincell">
            <span className="lab">{t('counter.margin')}</span>
            {/* The CELL is present for every row of a margin role, its CONTENTS only where there is
                something to price against. A row can carry authority and still have neither (a
                supplier quote with no cost, a product with no sellable price), and letting those
                rows drop the cell would shift the grid under them and stagger the column down the
                list. Say "nothing to price here" instead. */}
            {r.cost != null && basePrice != null ? (
              <LineMargin
                cost={r.cost}
                unit={effectiveUnit}
                gridUnit={basePrice}
                canMovePrice={canMovePrice}
                canRequestDiscount={canRequestDiscount}
                canSellBelowCost={canSellBelowCost}
                overridden={override != null}
                /* Wrapped, not passed bare: `setOverride` is a state setter, so a function value
                   reaching it would be taken as an updater rather than stored. */
                onSet={(priceHT) => setOverride(priceHT)}
              />
            ) : (
              <span className="nomargin" title={t('counter.noCostKnown')}>
                —
              </span>
            )}
          </div>
        )}

        {/* Price and the buy controls share ONE row band with every other column, rather than the
            controls hanging below the price — a seller scans the list across, not down. */}
        <div className={cn('cell pricecell', isSupplierRow && 'is-candidate')}>
          <span className="lab">{t('counter.colPrice')}</span>
          <div className="priceblk">
            {/* The figure follows the margin desk. A price the seller has just set has to be the
                one on the row, or the two columns disagree about what this part costs and the
                bigger, bolder of them is the stale one. */}
            <div className="pv tnum">
              {r.price ? (
                eur(effectiveUnit)
              ) : r.source === 'SUPPLIER' && r.supplier?.indicativeSellPriceHT != null ? (
                // No product record → no grid, customer discount or override. Say so: this is a
                // guide price (cost × default coefficient), not a quotable one.
                <span title={t('counter.indicativePrice')}>
                  ≈ {eur(r.supplier.indicativeSellPriceHT)}
                </span>
              ) : (
                '—'
              )}{' '}
              <span className="unit">{t('counter.exclVat')}</span>
            </div>
            {/* The catalogue price stays visible once it has been left behind — otherwise there is
                nothing on the row to say the figure above is negotiated rather than the grid's. */}
            {override != null && basePrice != null && (
              <div className="pricenote">
                <span className="wasprice tnum" title={t('counter.cataloguePrice')}>
                  {eur(basePrice)}
                </span>
              </div>
            )}
            {/* The customer's own grid discount — a fact about how this price was reached, so it
                sits under it. Margin left for a column of its own; this did not. */}
            {r.price && r.price.discountPct > 0 && (
              <div className="pricenote">
                <span className="remise">−{r.price.discountPct.toFixed(0)}%</span>
              </div>
            )}
          </div>
          <div className="actions">
            {/* ONE control set, whatever the row's source. A part on a supplier's shelf is added
                like any other and lands as a supplier line with a promised date; only an operator
                without the sourcing permission (the B2B portal) sees a statement instead. The
                "Create & sell" button that stood here tied cataloguing to the sale, and every
                press left a permanent dead reference in the search. */}
            {isSupplierRow && !canSourceFromSupplier ? (
              <span
                className="notincat"
                title={t('counter.supplierOrderOnlyDetail', { brand: r.brand ?? '', sku: r.sku })}
              >
                <AlertTriangle size={12} /> {t('counter.supplierOrderOnly')}
              </span>
            ) : (
              <>
                <input
                  className="qty tnum"
                  type="number"
                  min={1}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Number(e.target.value)))}
                  aria-label={t('counter.quantityFor', { sku: r.sku })}
                  disabled={r.avail === 'out' || sourcing}
                />
                <button
                  className="iconbtn iconbtn--accent"
                  aria-label={t('counter.addToCartAria', { sku: r.sku })}
                  /* THE OBVIOUS SPLIT, IN ONE CLICK — our shelf first, up to what is actually on it,
                 and only the shortfall from the supplier at the supplier's own price. This button
                 used to send the whole quantity as INTERNAL whenever there was a single unit in
                 the building: ten against four on the shelf lost the reservation race server-side
                 and came back as ONE external line of ten — units we already own, bought again,
                 and priced off our shelf. The composer below stays for a DELIBERATE split (all
                 from the supplier because the customer wants one delivery, not two). */
                  onClick={() => {
                    /* 2026-10-30 (owner) — the shelf line stops at the shelf. This button used to
                       put the overflow on a supplier line by itself, "whatever the supplier
                       currently holds": that is how a Ridex line ended "ordered from no supplier".
                       Now the shelf gets what it has, the seller is told "only 8 in stock", and
                       the missing unit is a SEPARATE supplier line they add on purpose — from the
                       supplier row, or the composer's supplier origin below. */
                    const shelf = origins.find((o) => o.source === 'INTERNAL');
                    if (shelf && qty > shelf.available) {
                      onAddOrigin('INTERNAL', shelf.available, shelf.unitPriceHT);
                      return;
                    }
                    onAdd(qty, override ?? undefined);
                  }}
                  disabled={
                    !canAdd || sourcing || r.avail === 'out' || (!isSupplierRow && !r.price)
                  }
                  title={
                    !canAdd
                      ? t('counter.pickCustomerFirst')
                      : isSupplierRow
                        ? t('counter.addFromSupplier', { supplier: supplierLabel })
                        : t('counter.addToCart')
                  }
                >
                  {sourcing ? <RefreshCw size={16} className="spin" /> : <ShoppingCart size={16} />}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* MULTI-SOURCE COMPOSER.
          One product, one reference, but two places it can come from — and they are not
          interchangeable: our shelf is stock we already own at a cost that is history, the
          supplier's is an acquisition made today with a lead time attached. So the seller composes
          a quantity PER ORIGIN and each origin keeps its own price. Shown only when there is a
          genuine choice; a single-origin row keeps the plain qty box above. */}
      {origins.length > 1 && (
        <div className="origins">
          <div className="ohead" aria-hidden="true">
            <span>{t('counter.source')}</span>
            <span className="ar">{t('counter.avail')}</span>
            <span>{t('counter.customerGetsIt')}</span>
            <span className="ar">{t('counter.unitPrice')}</span>
            <span className="ar">{t('counter.colQty')}</span>
          </div>
          {origins.map((o) => {
            // Our promise for the quantity asked; the supplier's own date for it is in the tooltip.
            const eta =
              o.source === 'EXTERNAL' ? externalEta(r, originQty.EXTERNAL || 1, nowMs, cal) : o.eta;
            return (
              <div className="orow" key={o.source}>
                <span className={cn('obadge', o.source === 'INTERNAL' ? 'is-ours' : 'is-supplier')}>
                  {o.source === 'INTERNAL' ? <MapPin size={12} /> : <Truck size={12} />}
                  {o.source === 'INTERNAL' ? t('counter.warehouseOurs') : supplierLabel}
                </span>
                <span className="oqty tnum">{o.available > 10 ? '>10' : o.available}</span>
                <span className={cn('owhen', eta.immediate && 'is-now')} title={eta.detail}>
                  {eta.immediate ? t('counter.rightNow') : eta.label}
                </span>
                <span className="oprice tnum">{eur(o.unitPriceHT)}</span>
                <span className="oadd">
                  <input
                    className="qty tnum"
                    type="number"
                    min={0}
                    max={o.available}
                    value={originQty[o.source] ?? 0}
                    onChange={(e) =>
                      setOriginQty((q) => ({
                        ...q,
                        // Never let the box promise more than that origin can actually serve.
                        [o.source]: Math.max(0, Math.min(o.available, Number(e.target.value))),
                      }))
                    }
                    aria-label={t('counter.quantityFromOrigin', {
                      origin:
                        o.source === 'INTERNAL'
                          ? t('counter.originOurs')
                          : t('counter.theSupplier'),
                      sku: r.sku,
                    })}
                  />
                </span>
              </div>
            );
          })}
          <div className="ofoot">
            <button
              className="btn btn--primary"
              disabled={!canAdd || composedUnits === 0}
              onClick={() => {
                for (const o of origins) {
                  const n = originQty[o.source] ?? 0;
                  if (n > 0) onAddOrigin(o.source, n, o.unitPriceHT);
                }
                setOriginQty({});
              }}
              title={canAdd ? t('counter.addComposed') : t('counter.pickCustomerFirst')}
            >
              <ShoppingCart size={15} />
              {t('counter.add')}{' '}
              {composedUnits > 0 ? t('counter.addUnits', { count: composedUnits }) : ''}
            </button>
            {/* Said here, not only at checkout: two origins means two shipments, and the seller is
                about to tell the customer when they get their parts. */}
            {composedOrigins > 1 && (
              <span className="onote">
                <FileText size={12} /> {t('counter.deliveryNotesCount', { count: composedOrigins })}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ---- the information line -------------------------------------------------------------
          The dimensions, then the reference to quote. A supplier's own catalogue puts both on one
          line under the row ("Longueur 272 mm | Largeur 123 mm | … | Code: D09320") because that
          is the line a counter reads out loud: the sizes settle whether it is the right part, and
          the code is what goes on the order.

          The reference sits at the END and is copyable in place. It was previously only reachable
          from the SKU at the top of the row, which is our own internal number — not the number a
          customer or a supplier will recognise. */}
      {(specs.length > 0 || quotableRef) && (
        <div className="specstrip">
          {/* TecDoc repeats a label across criteria (two "Length" rows on a bearing), so the
              position is part of the key — a bare label collides and drops a spec. */}
          {specs.map((s, i) => (
            <span className="specitem" key={`${s.label}-${i}`}>
              {specLabel(s)}: <b>{specValue(s)}</b>
            </span>
          ))}
          {quotableRef && (
            <span className="specitem specref">
              {t('counter.refCode')}: <b>{quotableRef}</b>
              <button
                type="button"
                className="refcopy"
                title={t('counter.copyRef', { ref: quotableRef })}
                aria-label={t('counter.copyRef', { ref: quotableRef })}
                onClick={() => onCopy(quotableRef)}
              >
                <Copy size={11} />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Four links, four DIFFERENT tabs of the same panel — and none of them leaves the sale.
          They were all wired to the same navigate() before, which made them indistinguishable. */}
      <div className="sublinks">
        <button onClick={() => onInfo('INFO')}>
          {t('counter.moreInfo')} <ChevronRight size={12} />
        </button>
        <button onClick={() => onInfo('CROSS')}>
          {t('counter.crossReferences')} <ChevronRight size={12} />
        </button>
        <button onClick={() => onInfo('OE')}>
          {t('counter.oeNumbers')} <ChevronRight size={12} />
        </button>
        <button onClick={() => onInfo('VEHICLES')}>
          {t('counter.applications')} <ChevronRight size={12} />
        </button>
      </div>

      {zoomed && (
        <ImageZoom
          src={r.imageUrl}
          brandSrc={r.brandImageUrl}
          alt={r.brand ?? r.sku}
          caption={`${r.sku}${r.name ? ` — ${r.name}` : ''}`}
          onClose={() => setZoomed(false)}
        />
      )}
    </li>
  );
}

/**
 * Lightbox for a part photo.
 *
 * It re-mounts <ArticleImage> at a large size rather than being handed a resolved URL, because the
 * resolution chain (part photo → brand logo → placeholder) and the session-gated blob fetch both
 * live in that component. The blob is cached per src, so reopening the same picture is instant and
 * costs no second request.
 */
function ImageZoom({
  src,
  brandSrc,
  alt,
  caption,
  onClose,
}: {
  src?: string | null;
  brandSrc?: string | null;
  alt: string;
  caption: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="mds-zoom-bd"
      role="dialog"
      aria-modal="true"
      aria-label={t('counter.photoAria', { caption })}
      onClick={onClose}
    >
      <div className="mds-zoom" onClick={(e) => e.stopPropagation()}>
        <button className="mds-zoom-x" onClick={onClose} aria-label={t('counter.close')}>
          <X size={18} />
        </button>
        <ArticleImage src={src} brandSrc={brandSrc} alt={alt} size={720} />
        <div className="mds-zoom-cap">{caption}</div>
      </div>
    </div>
  );
}

// ===================== Checkout (caisse) =====================
type PayMethod = 'CASH' | 'CARD' | 'CHECK' | 'TRANSFER' | 'VOUCHER';
const PAY_METHODS: { key: PayMethod; Icon: typeof Coins }[] = [
  { key: 'CASH', Icon: Coins },
  { key: 'CARD', Icon: CreditCard },
  { key: 'CHECK', Icon: FileText },
  { key: 'TRANSFER', Icon: ArrowLeftRight },
  // Returns v2 (migration 150): a purchase voucher given back on a return, spent here.
  { key: 'VOUCHER', Icon: Ticket },
];

function Checkout({
  ttc,
  confirmLabel,
  reste,
  change,
  payments,
  curMethod,
  payAmount,
  setCurMethod,
  setPayAmount,
  addPayment,
  removePayment,
  voucherNo,
  setVoucherNo,
  voucher,
  checkVoucher,
  confirm,
  disabled,
}: {
  ttc: number;
  /** Overrides the "Confirm payment X" wording — an all-supplier basket collects nothing here. */
  confirmLabel?: string;
  reste: number;
  change: number;
  payments: { method: PayMethod; received: number; applied: number; voucherNumber?: string }[];
  curMethod: PayMethod | null;
  payAmount: string;
  setCurMethod: (m: PayMethod | null) => void;
  setPayAmount: (v: string) => void;
  addPayment: () => void;
  removePayment: (i: number) => void;
  voucherNo: string;
  setVoucherNo: (v: string) => void;
  voucher: { number: string; balance: number; error?: string } | null;
  checkVoucher: () => void;
  confirm: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const due = Math.max(0, reste);
  const settled = reste <= 0.001;
  const active = curMethod ? PAY_METHODS.find((m) => m.key === curMethod)! : null;
  const quick =
    curMethod === 'CASH'
      ? [due, Math.ceil(due / 5) * 5, Math.ceil(due / 10) * 10, Math.ceil(due / 20) * 20].filter(
          (v, i, a) => v > 0 && a.indexOf(v) === i
        )
      : [];

  return (
    <>
      <div className="payhead">
        <span>{t('counter.amountDue')}</span>
        <span className={cn('reste tnum', settled && 'ok')}>{eur(due)}</span>
      </div>

      <div className="paygrid">
        {PAY_METHODS.map((m) => (
          <button
            key={m.key}
            className={cn('pm', curMethod === m.key && 'on')}
            aria-pressed={curMethod === m.key}
            onClick={() => {
              const next = curMethod === m.key ? null : m.key;
              setCurMethod(next);
              // Prefill the whole balance. The counter case is "the customer pays what they owe",
              // and leaving the field empty meant picking a method looked like it had done
              // something while Confirm stayed disabled with no explanation. Split payments just
              // type over it.
              setPayAmount(next && due > 0 ? due.toFixed(2) : '');
            }}
          >
            <m.Icon size={20} />
            <span>{enumLabel('paymentMethod', m.key)}</span>
          </button>
        ))}
      </div>

      {active?.key === 'VOUCHER' && (
        <div className="payform">
          <div className="frow">
            <input
              autoFocus
              className="font-mono uppercase"
              value={voucherNo}
              placeholder={t('counter.voucher.placeholder')}
              onChange={(e) => setVoucherNo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && checkVoucher()}
              aria-label={t('counter.voucher.number')}
            />
            <button className="btn" onClick={checkVoucher}>
              {t('counter.voucher.check')}
            </button>
          </div>
          {voucher?.error && (
            <div className="text-xs font-medium text-destructive">{voucher.error}</div>
          )}
          {voucher && !voucher.error && (
            <div className="text-xs text-muted-foreground">
              {t('counter.voucher.balance', {
                number: voucher.number,
                amount: eur(voucher.balance),
              })}
            </div>
          )}
        </div>
      )}

      {active && (active.key !== 'VOUCHER' || (voucher && !voucher.error)) && (
        <div className="payform">
          <div className="frow">
            <input
              autoFocus
              inputMode="decimal"
              value={payAmount}
              placeholder={due > 0 ? due.toFixed(2) : '0.00'}
              onChange={(e) => setPayAmount(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addPayment()}
              aria-label={t('counter.amountFor', {
                method: enumLabel('paymentMethod', active.key),
              })}
            />
            <button className="btn btn--primary" onClick={addPayment}>
              {t('counter.add')}
            </button>
          </div>
          {quick.length > 0 && (
            <div className="quick">
              {quick.map((v) => (
                <button key={v} onClick={() => setPayAmount(v.toFixed(2))}>
                  {eur(v)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        {payments.map((p, i) => {
          const m = PAY_METHODS.find((x) => x.key === p.method)!;
          return (
            <div className="payln" key={i}>
              <span className="meta">
                <span className="ico">
                  <m.Icon size={15} />
                </span>
                <span className="lbl">
                  <span className="nm">
                    {enumLabel('paymentMethod', m.key)}
                    {p.voucherNumber ? ` ${p.voucherNumber}` : ''}
                  </span>
                  {p.received > p.applied && (
                    <span className="sub">
                      {t('counter.receivedAmount', { amount: eur(p.received) })}
                    </span>
                  )}
                </span>
              </span>
              {/* Amount and remove on ONE row: the wrapper was a bare inline span, so the
                  flex button dropped under the figure instead of sitting beside it. */}
              <span className="end">
                <span className="amt tnum">{eur(p.applied)}</span>
                <button
                  className="rm"
                  aria-label={t('counter.removePayment')}
                  onClick={() => removePayment(i)}
                >
                  <X size={13} />
                </button>
              </span>
            </div>
          );
        })}
      </div>

      {settled && change > 0 && (
        <div className="rendu">
          <span>{t('counter.changeDue')}</span>
          <span className="v tnum">{eur(change)}</span>
        </div>
      )}

      {/* Say WHY it is disabled. A greyed "Confirm payment" with an untouched Amount due told the
          operator nothing — the balance still has to be allocated to a method first. */}
      {!settled && due > 0 && (
        <p className="muted" style={{ marginTop: 'var(--s-3)', fontSize: 'var(--t-sm)' }}>
          {t('counter.stillToAllocate', { amount: eur(due) })}
        </p>
      )}
      <button
        className="btn btn--ok btn--block btn--lg"
        style={{ marginTop: 'var(--s-2)' }}
        disabled={disabled || !settled}
        title={!settled ? t('counter.stillToAllocateShort', { amount: eur(due) }) : undefined}
        // `onClick={confirm}` handed React's click event in as the first argument — which
        // confirmCheckout takes as `tokenOverride`, so the DOM node ended up in the request body
        // and axios died on "Converting circular structure to JSON". No counter cash sale could
        // complete. Call it with no arguments.
        onClick={() => confirm()}
      >
        {confirmLabel ?? t('counter.confirmPayment', { amount: eur(ttc) })}
      </button>
    </>
  );
}

/**
 * Manager authorises an above-ceiling discount at the till. The manager types their OWN
 * credentials; the server verifies them, checks they actually hold the authority, and returns a
 * short-lived signed token that the sale is replayed with. Nothing is trusted client-side — the
 * token is the audit trail of who approved what.
 *
 * Not offered for a below-cost line: no override authorises that (see MarginPolicy).
 */
function ManagerOverrideDialog({
  requestedPct,
  onCancel,
  onApproved,
}: {
  requestedPct: number;
  onCancel: () => void;
  onApproved: (token: string) => void;
}) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ overrideToken: string; approvedBy: string }>(
        '/discounts/override',
        { email, password, requestedPct, reason }
      );
      onApproved(data.overrideToken);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label={t('counter.managerAuthAria')}
    >
      <div className="w-[26rem] max-w-[92vw] rounded-md border bg-card p-4 space-y-3 text-sm">
        <h2 className="font-semibold text-base">{t('counter.managerAuth')}</h2>
        <p className="text-muted-foreground">
          <Trans
            i18nKey="counter.managerAuthHint"
            values={{ pct: requestedPct }}
            components={{ 1: <b /> }}
          />
        </p>
        <input
          className="w-full rounded border bg-background px-2 py-2"
          type="email"
          autoComplete="off"
          placeholder={t('counter.managerEmail')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label={t('counter.managerEmail')}
        />
        <input
          className="w-full rounded border bg-background px-2 py-2"
          type="password"
          autoComplete="off"
          placeholder={t('counter.password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-label={t('counter.managerPassword')}
        />
        <input
          className="w-full rounded border bg-background px-2 py-2"
          placeholder={t('counter.auditReason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          aria-label={t('counter.reason')}
        />
        <div className="flex justify-end gap-2 pt-1">
          <button className="rounded border px-3 py-2" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button
            className="rounded bg-success px-3 py-2 text-primary-foreground disabled:opacity-40"
            onClick={() => void submit()}
            disabled={busy || !email || !password || reason.trim().length < 5}
          >
            {t('counter.authorise')}
          </button>
        </div>
      </div>
    </div>
  );
}
